// Registro del día (bitácora de turno) — dominio puro de MiPAE+.
//
// Flujo real del turno: el usuario entra y sale de un paciente varias veces y
// va anotando lo que hace (valoración, fármacos, procedimientos, indicaciones).
// Al cierre, el registro se sintetiza en la evolución por sistema.
//
// Captura HÍBRIDA (decisión del 2 de junio): los datos clínicos van
// estructurados (cerrados); cada entrada admite además una "nota breve" de
// texto libre que pasa por el filtro anti-PII antes de guardarse.
//
// Persistencia: device-local (ver registroTurnoStore). Es un apoyo personal,
// NO el registro oficial de la institución.

import { SISTEMAS, sanitizarCama, textoAislamientos } from './entregaTurno';
import { buildEvolucion, SISTEMAS as SISTEMAS_EVOLUCION, completitudEvolucion, tieneContenido } from './evolucionSistema';
import { buildExamenFisico, resumenExamenPrevio, minutosHastaExamen } from './examenFisico';
import { tipoCanonico, nuevoDispositivo as nuevoDispositivoEvolucion, DISPOSITIVO_LUMENES, ubicacionesSugeridas, calibresSugeridos, DISPOSITIVO_RECEPCION_A_EVOLUCION, DISPOSITIVOS_TIPO, esMultilumen } from './evolucionSistema';
import { detectPii } from './piiDetect';
import { notaCambioDictado, reconciliarCamposDictados, labelCampoDictado } from './evolucionDictado';
import { ZONAS_PRESION, COBERTURAS_LESION, LADO_LABEL, anclarCuracion } from './pielLpp';
import {
  conexionTrrCompleta, desconexionTrrCompleta, conexionTrrProsa, desconexionTrrProsa,
  estadoTrasDesconexion,
} from './terapiaTrr';
import {
  momentoAhora, momentoDeEntrada, ordenarPorHecho, esPosteriorA,
  fechaISOLocal, horaLocalHHMM, PIVOTE_DIA_SIGUIENTE, derivarFechaLegado,
} from './momento';
import { huellaDe } from './huella';
import { claveFichaRef } from './relojInteraccion';
import { formatearEvolucionParaLectura } from './formatoEvolucion';

export { SISTEMAS, sanitizarCama, textoAislamientos };

export const TIPOS = [
  { id: 'valoracion',    label: 'Valoración' },
  { id: 'farmaco',       label: 'Fármaco' },
  { id: 'procedimiento', label: 'Procedimiento' },
  { id: 'visita',        label: 'Visita / indicaciones' },
  { id: 'nota',          label: 'Nota' },
];

export const VIAS = ['VO', 'EV', 'IM', 'SC', 'SNG', 'Tópica', 'Inhalatoria'];

export const PROCEDIMIENTOS = [
  'Curación', 'Instalación CVP', 'Instalación CVC', 'Instalación sonda Foley',
  'Aspiración de secreciones', 'Toma de muestras', 'Retiro de dispositivo', 'Otro',
];

// "Visita médica" y "Visita técnica" eran el MISMO evento con dos nombres, y
// tenerlos ambos en el catálogo era justo lo que los hacía confundirse (dictado
// del fundador, 30-jul: "siempre fue visita técnica, elimina la visita médica,
// suelen confundirse pero son lo mismo"). Queda el nombre real.
export const VISITAS = ['Visita técnica', 'Interconsulta'];

// Tolerante al rename: una entrada guardada ANTES lleva escrito el nombre viejo.
// Se normaliza al LEER, nunca se migran datos (mismo criterio que
// ALIAS_LABEL_DISPOSITIVO). Sin esto la entrada dejaba de matchear el catálogo y
// compilaba VACÍO, desapareciendo de la evolución en silencio: es exactamente el
// bug que cazó la suite cuando se renombró el CVC.
const ALIAS_VISITA = { 'Visita médica': 'Visita técnica' };
export function visitaCanonica(visita) {
  const v = ALIAS_VISITA[visita] || visita;
  return VISITAS.includes(v) ? v : null;
}

// Turno del registro. El de 24 h se agregó el 2026-08-10 a pedido del fundador:
// hasta entonces quien hacía 24 horas tenía que declararse "Largo", y la
// retención le borraba el día a las 23:00, NUEVE HORAS antes de que terminara
// su turno. El string ES la etiqueta visible (así se guarda en `registro.turno`
// y así se muestra); los valores viejos siguen siendo válidos.
export const TURNO_24 = '24 horas';
export const TURNOS = ['Largo', 'Noche', TURNO_24];

// ── Recepción del paciente: gravedad, aislamiento y dispositivos ──────────────
// Escala de gravedad (de menor a mayor), según uso clínico chileno.
export const GRAVEDADES = [
  'Estable', 'De cuidado', 'De cuidado grave', 'Grave', 'Muy grave', 'De extrema gravedad',
];
// Urgencia de cada gravedad para el color de lo seleccionado (tres niveles,
// ver lib/tonoClinico.js): estable → nada que hacer; de cuidado → vigila;
// grave y más → actúa ahora. Un test exige que cubra el catálogo entero.
export const TONO_GRAVEDAD = {
  'Estable': 'estable',
  'De cuidado': 'atencion',
  'De cuidado grave': 'atencion',
  'Grave': 'critico',
  'Muy grave': 'critico',
  'De extrema gravedad': 'critico',
};

// Aislamiento: preventivo se atiende PRIMERO (proteger al paciente); contacto/
// gotitas/aéreo se atienden AL FINAL (no arrastrar contaminación).
export const AISLAMIENTOS = ['Ninguno', 'Preventivo', 'Contacto', 'Gotitas', 'Aéreo'];

// Un paciente puede estar en MÁS DE UN aislamiento a la vez (pedido del
// fundador, 2026-09-10): contacto Y gotitas es el caso de todos los días, y
// hasta hoy el chip era excluyente, así que la enfermera tenía que elegir cuál
// declarar y el EPP quedaba a medias. La lista vive en `registro.aislamientos`;
// `registro.aislamiento` se CONSERVA con el más severo, porque todo lo que
// ordena o cuenta con un solo valor (ordenarPorPrioridad, el conteo del
// resumen del turno, la columna remota, un cliente viejo que todavía no tiene
// la lista) sigue leyendo de ahí sin enterarse.
//
// `aislamientosDe` es el ÚNICO lector: un registro viejo, sin la clave nueva,
// se lee desde `aislamiento` (adapter, jamás se migran datos) y devuelve
// exactamente lo mismo que antes.
const AISLAMIENTOS_REALES = AISLAMIENTOS.filter((a) => a !== 'Ninguno');

export function aislamientosDe(registro) {
  const lista = Array.isArray(registro?.aislamientos)
    ? registro.aislamientos.filter((a) => AISLAMIENTOS_REALES.includes(a))
    : [];
  // UNIÓN con el valor único, no preferencia: la app de Play que todavía no
  // actualizó escribe SOLO `aislamiento` (PostgREST deja `aislamientos` como
  // estaba), así que si en el teléfono viejo cambian a aéreo y la lista trae
  // contacto, el aéreo tiene que sobrevivir (hallazgo del auditor clínico,
  // 2026-09-10: la dirección insegura era omitir el N95). El caso inverso deja
  // MÁS EPP del necesario, que es la dirección segura.
  const uno = registro?.aislamiento;
  if (AISLAMIENTOS_REALES.includes(uno) && !lista.includes(uno)) lista.push(uno);
  // Orden estable: el del catálogo, no el de los toques del usuario.
  return AISLAMIENTOS_REALES.filter((a) => lista.includes(a));
}

// El más severo de una lista, con el MISMO peso que ordena la atención
// (PESO_AISLAMIENTO). En empate (contacto y gotitas pesan igual) gana el
// primero del catálogo. Lista vacía ⇒ null.
export function aislamientoMasSevero(lista = []) {
  return aislamientosDe({ aislamientos: lista }).reduce((mas, a) => (
    mas === null || (PESO_AISLAMIENTO[a] ?? 1) > (PESO_AISLAMIENTO[mas] ?? 1) ? a : mas
  ), null);
}

/** Escribe la lista de aislamientos en un registro. PURA. */
export function conAislamientos(registro, lista = []) {
  const elegidos = aislamientosDe({ aislamientos: lista });
  return {
    ...(registro || {}),
    aislamientos: elegidos,
    aislamiento: aislamientoMasSevero(elegidos) || 'Ninguno',
  };
}

// Los 10 correctos de la administración segura de medicamentos (con su guía).
export const LOS_10_CORRECTOS = [
  { id: 'paciente',    label: 'Paciente correcto',                desc: 'Identifícalo por nombre completo y fecha de nacimiento, o revisa el brazalete. Nunca preguntes "¿Eres Juan?" esperando un sí.' },
  { id: 'medicamento', label: 'Medicamento correcto',             desc: 'Verifica el nombre del fármaco en el envase al menos tres veces.' },
  { id: 'dosis',       label: 'Dosis correcta',                   desc: 'Comprueba dos veces la cantidad indicada y realiza los cálculos antes de administrar.' },
  { id: 'via',         label: 'Vía de administración correcta',   desc: 'Asegúrate de que sea la vía indicada (oral, IV, IM, etc.).' },
  { id: 'hora',        label: 'Hora correcta',                    desc: 'Respeta los intervalos de tiempo y la pauta prescrita.' },
  { id: 'caducidad',   label: 'Fecha de caducidad correcta',      desc: 'Revisa que el medicamento y sus diluyentes no estén vencidos.' },
  { id: 'educacion',   label: 'Educar e informar al paciente',    desc: 'Explícale qué fármaco se administra, para qué sirve y posibles efectos.' },
  { id: 'alergias',    label: 'Indagar sobre alergias',           desc: 'Pregunta siempre si es alérgico a algún medicamento o componente.' },
  { id: 'preparacion', label: 'Preparación y administración correcta', desc: 'Prepara, administra y asume la responsabilidad de tu propio proceso.' },
  { id: 'registro',    label: 'Registro correcto',                desc: 'Anota de inmediato hora, dosis, vía y cualquier reacción en la ficha.' },
];

const IDS_10_CORRECTOS = new Set(LOS_10_CORRECTOS.map((c) => c.id));

// Catálogo de dispositivos invasivos al RECIBIR, DERIVADO del catálogo de la
// evolución (dictado del fundador en turno real, 2026-08-20: "pasar
// dispositivos invasivos completos arriba, porque están distintos en recepción
// y en evolución"). Eran dos listas escritas a mano y habían divergido en las
// dos formas posibles:
//   - en QUÉ existe: arriba había 9 tipos y abajo 16, así que un paciente que
//     llegaba con DVE, catéter de hemodiálisis, marcapasos, Swan-Ganz, PiCCO,
//     SOG o Nexiva no se podía ni nombrar al recibir;
//   - en CÓMO se llama: arriba decía "CVP", "Sonda Foley (CUP)" y "Tubo
//     endotraqueal" donde abajo dice "VVP", "Catéter urinario permanente" y
//     "TOT" — el rename del Foley (dictado 2026-07-16, "aplica en TODAS las
//     superficies") nunca había bajado hasta acá.
// Ahora el orden, la lista y las etiquetas salen de DISPOSITIVOS_TIPO (que va
// en orden céfalo-caudal) y el id de recepción sale del mapa del puente. Los
// ids NO cambian: son lo que está guardado en los registros.
//
// `permeabilizable` = se permeabiliza con una jeringa de SF por rama, así que
// entra al texto educativo y a la prosa de permeabilidad del documento de la
// visita. NO es lo mismo que `esVascular` de la evolución, que es más ancho
// (ahí incluye Swan-Ganz, PiCCO, la línea arterial y el catéter de hemodiálisis
// para efectos de permeabilidad y flebitis): el de hemodiálisis va SELLADO con
// heparina y no se permeabiliza con SF cada turno, y los otros van presurizados.
// Derivarlo de `esVascular` habría hecho que la app pidiera jeringas de SF para
// un catéter de diálisis, o sea un consejo clínico equivocado. Se declara a
// mano, y Nexiva entra porque es un periférico igual que la VVP.
//
// LA LÍNEA ARTERIAL SALIÓ el 2026-09-21 (dictado del fundador: "saca la
// permeabilización de la línea arterial"), y es la razón por la que este flag
// dejó de llamarse `vascular`: la línea arterial ES vascular, pero va en un
// sistema PRESURIZADO con lavado continuo, no se permeabiliza a mano. Seguir
// llamándolo `vascular` con la línea arterial afuera era una mentira esperando
// a que alguien la "arreglara" de vuelta. El cambio vale en las DOS superficies
// que leen el flag, y en las dos corresponde: ya no pide una jeringa para ella,
// y el documento de la visita ya no le escribe "infunde sin resistencia con
// técnica push-stop", que no es la técnica de una arterial. Lo suyo sigue
// entero en sus cuidados (onda arterial, sistema presurizado, sitio sin
// sangrado) y en su flebitis y reflujo de la evolución, que van por `esVascular`.
//
// `multiLumen` = pregunta cuántas ramas/lúmenes; ese sí sale de la fuente única
// de la evolución (`esMultilumen`: CVC, PICC y Swan-Ganz).
const PERMEABILIZABLES = new Set(['cvc', 'picc', 'cvp', 'nexiva']);
// `sitioDescribible` = tiene un sitio de PUNCIÓN del que este documento dice
// algo ("sitio de inserción sin signos de infección ni flebitis"). Es un eje
// DISTINTO de permeabilizar, y tenerlos separados es justo lo que faltaba: al
// sacar la línea arterial de la permeabilización con un solo flag, su línea
// entera se cayó del documento de la visita, porque sin permeabilidad, sin
// técnica y sin sitio no quedaba nada que decir y la función la omite. Perder
// la flebitis de una arterial por quitarle una jeringa habría sido un arreglo
// peor que el problema, y lo cazó el test del formato real de la TPM.
// La lista es la de antes del 2026-09-21: cambia la permeabilización, no lo
// que el documento sabe describir.
const SITIO_DESCRIBIBLE = new Set([...PERMEABILIZABLES, 'la']);
export const CATALOGO_DISPOSITIVOS = DISPOSITIVOS_TIPO.map((tipo) => {
  const id = Object.keys(DISPOSITIVO_RECEPCION_A_EVOLUCION)
    .find((k) => DISPOSITIVO_RECEPCION_A_EVOLUCION[k] === tipo);
  return {
    id, label: tipo,
    permeabilizable: PERMEABILIZABLES.has(id),
    sitioDescribible: SITIO_DESCRIBIBLE.has(id),
    multiLumen: esMultilumen(tipo),
  };
}).filter((c) => c.id);

// Grupos para RECORRER el catálogo al recibir (pedido del fundador 2026-09-28:
// "accesos vasculares, vía aérea, sondas y drenajes"). Es presentación: cada
// id sigue siendo el mismo del catálogo y del registro, y un test exige que
// los grupos cubran el catálogo entero sin repetir. El orden dentro de cada
// grupo es el céfalo-caudal de DISPOSITIVOS_TIPO.
export const GRUPOS_DISPOSITIVOS = [
  { id: 'vasculares', label: 'Accesos vasculares', ids: ['cvc', 'swan', 'picco', 'picc', 'la', 'cvp', 'nexiva', 'chd'] },
  { id: 'viaAerea', label: 'Vía aérea', ids: ['tet', 'tqt'] },
  { id: 'sondasDrenajes', label: 'Sondas y drenajes', ids: ['dve', 'sng', 'sog', 'sny', 'gtt', 'drenaje', 'foley', 'smf'] },
  { id: 'otros', label: 'Otros', ids: ['marcapasos'] },
];

export function gruposDispositivos(catalogo = CATALOGO_DISPOSITIVOS) {
  const enGrupo = new Set(GRUPOS_DISPOSITIVOS.flatMap((g) => g.ids));
  const grupos = GRUPOS_DISPOSITIVOS.map((g) => ({
    ...g,
    items: catalogo.filter((c) => g.ids.includes(c.id)),
  }));
  // Un tipo nuevo que nadie clasificó no desaparece: cae a "Otros".
  const huerfanos = catalogo.filter((c) => !enGrupo.has(c.id));
  if (huerfanos.length) grupos[grupos.length - 1] = { ...grupos.at(-1), items: [...grupos.at(-1).items, ...huerfanos] };
  return grupos.filter((g) => g.items.length);
}

// Búsqueda por etiqueta TOLERANTE al rename: una acción rápida guardada antes
// del 27-jul lleva escrito "CVC" y el catálogo ahora dice "Catéter venoso
// central". Sin esto esa acción quedaba inválida y compilaba vacío (lo cazó
// la suite al renombrar). Se normaliza al leer; nunca se migran datos.
// Los tres de 2026-08-20 son del alineamiento de vocabulario con la evolución:
// las acciones rápidas ya guardadas siguen resolviendo con el nombre viejo.
const ALIAS_LABEL_DISPOSITIVO = {
  CVC: 'Catéter venoso central',
  CVP: 'VVP',
  'Sonda Foley (CUP)': 'Catéter urinario permanente',
  'SNG / SNY': 'SNG',
  'Tubo endotraqueal': 'TOT',
};
export function dispositivoPorLabel(label) {
  const l = ALIAS_LABEL_DISPOSITIVO[label] || label;
  return CATALOGO_DISPOSITIVOS.find((c) => c.label === l) || null;
}


// Cuidados por tipo de dispositivo (chips cerrados). LEGADO LEÍDO desde la
// poda de recepción (dictado 2026-08-01: al recibir solo se sabe fecha y
// lúmenes; la verificación vive en la visita): la UI ya no los ofrece, pero
// un registro guardado con cuidados sigue imprimiendo su línea.
export const CUIDADOS_DISPOSITIVO = {
  cvc: ['Sitio de inserción visible', 'Sin signos de infección', 'Apósito limpio y seco', 'Restos hemáticos, requiere curación', 'Infunde y refluye bien', 'Lumen tapado al refluir'],
  picc: ['Sitio de inserción visible', 'Sin signos de infección', 'Apósito limpio y seco', 'Infunde y refluye bien', 'Lumen tapado al refluir', 'Medición de brazo registrada'],
  la: ['Onda arterial adecuada', 'Sin signos de isquemia distal', 'Sistema presurizado', 'Sitio sin sangrado'],
  cvp: ['Sin signos de flebitis', 'Permeable', 'Apósito limpio y seco', 'Fecha de instalación vigente'],
  foley: ['Fijada al muslo', 'Bajo el nivel de la vejiga', 'Rotar fijación cada 2 h', 'Permeable, diuresis presente', 'Circuito cerrado'],
  sng: ['In situ, corroborada por auscultación', 'Permeable', 'Fijación adecuada', 'Marca/cm registrada'],
  tet: ['Cm en comisura registrado', 'Presión de cuff adecuada', 'Fijación segura', 'Aspiración según necesidad'],
  tqt: ['Cánula permeable', 'Apósito limpio', 'Fijación adecuada', 'Cuff con presión adecuada'],
  drenaje: ['Permeable', 'Débito cuantificado', 'Sitio sin signos de infección', 'Fijación adecuada'],
  // 2026-09-21. Catálogo cerrado como los de arriba: lo que se constata en la
  // visita, nunca una conducta.
  sny: ['Posición post-pilórica confirmada', 'Permeable', 'Fijación adecuada', 'Marca/cm registrada'],
  gtt: ['Estoma sin signos de infección', 'Sin filtración periestomal', 'Permeable', 'Balón/fijación con tracción adecuada'],
  smf: ['Balón con volumen indicado', 'Sistema permeable', 'Débito cuantificado', 'Zona perianal indemne'],
};

export function cuidadosDe(tipo) {
  return CUIDADOS_DISPOSITIVO[tipo] || [];
}

// `fechaInstalacion` (ISO 'YYYY-MM-DD', null por defecto): día en que se
// instaló el dispositivo. Sin esto los días-catéter se re-tipeaban de memoria
// cada 12 h (fix #3 auditoría MiPAE en turno) — con la fecha, `diaDispositivo` los
// calcula solo. Opcional a propósito: un dispositivo sin fecha simplemente no
// muestra el badge de día (nunca se inventa).
//
// `ubicacion` (string, OPCIONAL — no se inicializa aquí): dónde está instalado,
// elegido con chips de catálogo cerrado en F1 (ver `ubicacionesRecepcion`). Un
// dispositivo sin la clave se comporta exactamente como antes.
//
// `origenEntradaId` (string, OPCIONAL — U4): id de la entrada de la cronología
// que instaló este dispositivo. Es el VÍNCULO con el evento, y existe porque
// identificar un dispositivo por coincidencia de tipo y fecha es adivinar: dos
// vías del mismo tipo instaladas el mismo día son indistinguibles, y corregir
// "la que coincide" puede reescribir la equivocada. Con el vínculo, corregir el
// momento de una instalación toca EXACTAMENTE el dispositivo que nació de ella;
// sin él (dispositivo de la recepción, o anterior a este cambio) no se toca
// nada y queda una discrepancia a la vista.
export function nuevoDispositivo(tipo) {
  const cat = CATALOGO_DISPOSITIVOS.find((c) => c.id === tipo);
  return { id: nuevoId(), tipo, lumenes: cat?.multiLumen ? 2 : null, permeabilizado: false, cuidados: [], fechaInstalacion: null };
}

// Fecha de hoy en ISO local ('YYYY-MM-DD'), sin desfase de zona horaria (a
// diferencia de `toISOString()`, que usa UTC). Mismo patrón que
// registroTurnoStore.fechaHoyKey, duplicado aquí para no acoplar los dos
// módulos por una función de una línea.
export const fechaISO = fechaISOLocal;

// Día del dispositivo (día 0 = el día de instalación — cambio de convención
// dictado por el fundador 2026-08-09: "la fecha real de instalación es el día
// 0; si se instaló hoy domingo 9, hoy es día 0, mañana 10 es día 1"; antes día
// 1 = instalación), a partir de `fechaInstalacion`. Cuenta por día calendario,
// no por horas — así "instalado ayer" siempre es "día 1" sin importar la hora
// exacta. null si no hay fecha, es inválida o es futura (no se inventa un día
// negativo). Día 0 ES un valor válido: comparar con `!= null`, nunca con
// `!dia` (0 es falsy y se pierde con un chequeo descuidado).
//
// Retrocompatibilidad deliberada, sin migrar datos: un `dia` guardado como
// string en una evolución o borrador VIEJO (antes del 2026-08-09) quedó
// escrito con la convención anterior, un día de más. No se corrige al leer
// porque el campo es editable a mano por el usuario y la vida de un registro
// de turno es un turno (se borra al terminar): el desfase muere solo, en
// horas, sin necesidad de una migración.
//
// `desdeUno` (2026-10-10, versión general de MiPAE en turno): fuera de Bupa el
// día de la instalación es el DÍA 1 ("contar desde 0" es rutina de Bupa, dijo
// el fundador). La fecha guardada es la misma; solo cambia el nombre del día.
// Sin la opción, todo como siempre. Cada registro sabe desde qué día cuenta
// (`cuentaDesdeUno`), así que nadie decide el número por su cuenta.
export function diaDispositivo(dispositivo, hoy = new Date(), { desdeUno = false } = {}) {
  const fecha = dispositivo?.fechaInstalacion;
  if (!fecha || !/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return null;
  const [y, m, d] = fecha.split('-').map(Number);
  const inst = new Date(y, m - 1, d);
  if (Number.isNaN(inst.getTime())) return null;
  const ahora = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  const dias = Math.round((ahora - inst) / (24 * 3600 * 1000));
  if (dias < 0) return null;
  return desdeUno ? dias + 1 : dias;
}

// Desde qué día cuenta este registro sus dispositivos. Se fija al CREAR el
// registro (`nuevoRegistro({ diaDesdeUno })`) y no cambia en todo el turno: si
// la persona cambia de institución a mitad de turno, sus números no saltan, y
// como viaja al remoto (columna `dia_desde_uno`) el PC dice lo mismo que el
// teléfono. Registro sin la clave (Bupa, o creado antes del 2026-10-10) ⇒ día
// 0 = instalación, o sea salida idéntica a la de siempre.
export function cuentaDesdeUno(registro) {
  return registro?.diaDesdeUno === true;
}

// "Así queda", en vivo, en la Recepción (brainstorm aprobado por el fundador
// 2026-09-28, idea 1): la frase que se arma con cada toque de F1. Solo
// catálogo (gravedad, aislamientos y tipos de dispositivo con su día), jamás
// texto libre: PII-safe por construcción. '' cuando no hay nada elegido (la
// UI no muestra la caja). Misma prosa de aislamientos que el SBAR y el hub
// (`textoAislamientos`), mismo día-catéter que la tarjeta (`diaDispositivo`).
export function lineaRecepcion(registro, hoy = new Date()) {
  if (!registro) return '';
  const partes = [];
  const gravedad = GRAVEDADES.includes(registro.gravedad) ? registro.gravedad : null;
  const aisl = aislamientosDe(registro);
  const preventivo = aisl.includes('Preventivo');
  const resto = textoAislamientos(aisl.filter((a) => a !== 'Preventivo'));
  const aislTxt = [preventivo ? 'preventivo' : null, resto ? `de ${resto}` : null].filter(Boolean).join(' y ');
  const disp = (registro.dispositivos || []).map((d) => {
    const cat = CATALOGO_DISPOSITIVOS.find((c) => c.id === d.tipo);
    if (!cat) return null;
    const lumenes = cat.multiLumen && d.lumenes ? ` de ${d.lumenes} lúmenes` : '';
    const dia = diaDispositivo(d, hoy, { desdeUno: cuentaDesdeUno(registro) });
    return `${cat.label}${lumenes}${dia != null ? ` (día ${dia})` : ''}`;
  }).filter(Boolean);
  const dispTxt = disp.length <= 1 ? disp.join('') : `${disp.slice(0, -1).join(', ')} y ${disp[disp.length - 1]}`;
  if (!gravedad && !aislTxt && !dispTxt) return '';
  if (aislTxt) partes.push(`en aislamiento ${aislTxt}`);
  if (dispTxt) partes.push(`con ${dispTxt}`);
  const cabeza = gravedad ? `Se recibe ${gravedad.toLowerCase()}` : 'Se recibe';
  // Con gravedad, las cláusulas van tras coma ("Se recibe grave, con…"); sin
  // ella, la primera sigue de corrido ("Se recibe con…").
  const cola = partes.length ? `${gravedad ? ', ' : ' '}${partes.join(', ')}` : '';
  return `${cabeza}${cola}.`;
}

// Inversa EXACTA de diaDispositivo (PR-E, 2026-08-09, entrada bidireccional de
// F1): a partir de un día (0 = hoy) devuelve la fecha de instalación en ISO
// 'YYYY-MM-DD'. La fuente de verdad SIGUE siendo `fechaInstalacion` — esto
// solo traduce lo que el enfermero escribe ("día 3") al dato que ya existía,
// cero cambio de shape. null si `dia` no es un entero ≥ 0 (vacío, decimal,
// negativo o texto no numérico: nunca se inventa una fecha).
// Con `desdeUno` el día 1 es hoy, y "día 0" no existe: null, no se inventa.
export function fechaDesdeDia(dia, hoy = new Date(), { desdeUno = false } = {}) {
  const texto = String(dia ?? '').trim();
  if (!/^\d+$/.test(texto)) return null;
  const n = Number(texto) - (desdeUno ? 1 : 0);
  if (n < 0) return null;
  const fecha = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
  fecha.setDate(fecha.getDate() - n);
  return fechaISO(fecha);
}

// Umbral de "evalúa recambio" por tipo de dispositivo — SOLO el CVP tiene uno
// estandarizado y de consenso amplio (recambio programado a las ~72 h de uso,
// guías de bioseguridad IAAS). Con el conteo día 0 = instalación, ese mismo
// momento clínico (72 h ⇒ el 4º día calendario) ahora se llama "día 3" en vez
// de "día 4" — el umbral baja de 4 a 3 a propósito, para que la alerta siga
// sonando el MISMO día calendario de siempre (fijado por test: lo que importa
// es el momento clínico, no el número de la convención vigente). CVC/PICC/
// línea arterial/Foley/SNG/TET/TQT/drenaje NO tienen un umbral de recambio
// fijo y universal (depende de indicación clínica, no de días) — para esos el
// badge muestra solo el número de día, sin matiz de alerta, a propósito:
// inventar un corte sería un consejo clínico que MiPAE no puede respaldar.
// Con `desdeUno` (fuera de Bupa) el mismo momento de 72 horas se llama "día 4",
// así que el umbral sube uno: suena el MISMO día calendario en las dos
// convenciones, fijado por test.
const UMBRAL_DIA_DISPOSITIVO = { cvp: 3 };
export function diaRequiereAtencion(tipoId, dia, { desdeUno = false } = {}) {
  const umbral = UMBRAL_DIA_DISPOSITIVO[tipoId];
  return umbral != null && typeof dia === 'number' && dia >= umbral + (desdeUno ? 1 : 0);
}

// ── Puente días-catéter → evolución (Invasivos) ───────────────────────────────
// El campo "Día" de cada dispositivo en la evolución por sistema
// (evolucionSistema.DispositivosEditor) hoy se teclea a mano. Este mapa
// traduce el id del catálogo de recepción (arriba, vocabulario propio) al
// tipo-string que usa la evolución (evolucionSistema.DISPOSITIVOS_TIPO) —
// mismo patrón que SISTEMA_VALORACION_A_EVOLUCION más abajo, pero para
// dispositivos: los vocabularios no calzan 1:1 (cvp → VVP: mismo catéter
// venoso periférico, dos nombres).
//
// El mapa MISMO vive ahora en evolucionSistema.js (2026-08-15): el dominio de
// la evolución también necesita traducir (las exclusiones clínicas del gesto
// "El resto, sin novedad" reciben dispositivos de F1) y es el módulo sin
// dependencias hacia acá — al revés habría ciclo, porque este archivo ya
// importa de allá. Se importa arriba con el nombre de siempre, así que los
// cuatro usos de abajo quedaron intactos.

// Ubicaciones ofrecidas al RECIBIR (chips de F1, dictado del fundador
// 2026-08-10: "igual colocar ubicación del dispositivo"). El catálogo NO se
// duplica: se traduce el tipo de recepción al de la evolución con el mismo mapa
// del sembrado y se leen las sugerencias de allá (UBICACIONES_SUGERIDAS). En F1
// son SOLO chips, catálogo cerrado y PII-safe por construcción: el matiz fino
// ("yugular derecha, difícil de canalizar") se escribe en la evolución, que sí
// tiene campo libre. Tipos sin sugerencias (Foley, SNG, TET…) devuelven [] y la
// UI no dibuja la fila: no se inventa una ubicación donde no significa nada.
export function ubicacionesRecepcion(tipo) {
  const destino = DISPOSITIVO_RECEPCION_A_EVOLUCION[tipo];
  return destino ? ubicacionesSugeridas(destino) : [];
}

// El CALIBRE al recibir (pedido del fundador 2026-09-10: "permitir colocar
// lugar y más detalle, excepto funcionalidad"), espejo EXACTO de la ubicación:
// mismo mapa de traducción, mismo catálogo cerrado (CALIBRES_SUGERIDOS vive en
// evolucionSistema.js, no se duplica), mismos chips sin texto libre. Un tipo
// sin calibre que elegir devuelve [] y la UI no dibuja la fila — el TOT, por
// ejemplo, cuya medida la escribe la acción de intubación y nadie más.
export function calibresRecepcion(tipo) {
  const destino = DISPOSITIVO_RECEPCION_A_EVOLUCION[tipo];
  return destino ? calibresSugeridos(destino) : [];
}

// Los lúmenes se cuentan distinto en cada lado: al recibir son un NÚMERO (el
// contador +/- de F1, 1 a 5) y en la evolución son un string del catálogo
// cerrado DISPOSITIVO_LUMENES ('3 lúmenes'). Sin traducir, la entrada sembrada
// llegaba con `lumenes: 3` y la evolución comparaba `3 === '3 lúmenes'`: el
// chip nunca se encendía, el bloque "Funcionamiento por lumen" no se dibujaba
// (DispositivosEditor lee LUMEN_NOMBRES[d.lumenes]) y los lúmenes no se
// imprimían en la línea del documento. O sea: lo declarado arriba se volvía a
// pedir abajo (bug reportado por el fundador, 2026-08-09).
//
// El string se arma con la regla del catálogo (singular en 1, plural en el
// resto) y se VERIFICA contra el propio DISPOSITIVO_LUMENES: fuera del
// catálogo, null — nunca se inventa un valor que la evolución no sabe leer.
export function lumenesTexto(lumenes) {
  if (typeof lumenes === 'string') {
    return DISPOSITIVO_LUMENES.includes(lumenes) ? lumenes : null;
  }
  const n = Number(lumenes);
  if (!Number.isInteger(n) || n < 1) return null;
  const etiqueta = n === 1 ? '1 lumen' : `${n} lúmenes`;
  return DISPOSITIVO_LUMENES.includes(etiqueta) ? etiqueta : null;
}

// Autocompleta el "Día" de los dispositivos invasivos de la evolución desde la
// fecha de instalación de recepción — SOLO cuando el campo está vacío (nunca
// pisa un día que el usuario ya escribió a mano). Un solo candidato por tipo
// (el primero con fecha registrada); con dos dispositivos del mismo tipo el
// usuario ajusta el resto a mano. Devuelve el MISMO objeto si no hay nada que
// completar, para no generar renders de más.
// `desdeUno`: la convención del registro (ver `cuentaDesdeUno`).
export function autocompletarDiaInvasivos(evolucion, dispositivosRecepcion = [], hoy = new Date(), { desdeUno = false } = {}) {
  const invasivos = evolucion?.sistemas?.invasivos;
  const lista = Array.isArray(invasivos?.dispositivos) ? invasivos.dispositivos : [];
  if (!lista.length || !dispositivosRecepcion.length) return evolucion;

  const sugeridos = {};
  for (const d of dispositivosRecepcion) {
    const destino = DISPOSITIVO_RECEPCION_A_EVOLUCION[d?.tipo];
    if (!destino || sugeridos[destino] != null) continue;
    const dia = diaDispositivo(d, hoy, { desdeUno });
    if (dia != null) sugeridos[destino] = dia;
  }
  if (!Object.keys(sugeridos).length) return evolucion;

  let cambio = false;
  const nuevaLista = lista.map((d) => {
    const vacio = d?.dia === undefined || d?.dia === null || String(d.dia).trim() === '';
    const sugerido = sugeridos[tipoCanonico(d?.tipo)];
    if (!vacio || sugerido == null) return d;
    cambio = true;
    return { ...d, dia: String(sugerido) };
  });
  if (!cambio) return evolucion;

  return { ...evolucion, sistemas: { ...evolucion.sistemas, invasivos: { ...invasivos, dispositivos: nuevaLista } } };
}

// ── Puente recepción → evolución (Invasivos): SEMBRAR las entradas ───────────
// El hueco que el fundador cazó en terreno (30-jul): "recibo turno, me piden
// anotar dispositivos invasivos, y esos después no están en el examen físico
// ni en la visita de enfermería". Los invasivos de F1 y los de la evolución
// eran dos almacenes sin puente de ida: autocompletarDiaInvasivos solo
// completaba el día de entradas que YA existían. Esta función CREA la entrada
// que falta: cada dispositivo anotado al recibir aparece en la evolución de
// invasivos (que es lo que la visita de enfermería edita) con su tipo, sus
// lúmenes y su día ya puestos.
//
// Idempotente POR MEMORIA, no por conteo (fix 2026-08-09): cada dispositivo de
// recepción que ya pasó por aquí queda anotado en `evolucion._sembrados`
// (clave opcional con prefijo _, mismo patrón que `_pielEnrutada`), y un id
// anotado NO se vuelve a sembrar nunca. La idempotencia anterior comparaba
// contra el CONTEO de tarjetas visibles y eso hacía dos cosas malas: borrar la
// tarjeta sembrada la hacía REAPARECER en el render siguiente (el conteo
// bajaba), y cambiarle el tipo a una sembrada la DUPLICABA (el conteo de su
// tipo original bajaba). Con la memoria, borrada queda borrada y cambiada
// queda cambiada: lo que el enfermero hace con su documento manda.
//
// El id de la tarjeta sembrada es DETERMINISTA (`inv-<id de recepción>`) en
// vez de aleatorio: con id nuevo en cada recálculo, React remontaba la tarjeta
// y el campo en foco perdía el cursor a mitad de escritura.
//
// Nunca edita ni borra entradas existentes: lo escrito en la Mesa manda, y el
// retiro de un dispositivo se documenta con su acción rápida, no borrando.
function completarMedidasIot(tarjeta, fuente) {
  if (fuente?.tipo !== 'tet' || !fuente._medidasIot) return tarjeta;
  const siguiente = { ...tarjeta };
  const libro = { ...(tarjeta._medidasIot || {}) };
  for (const campo of ['calibre', 'detalle']) {
    const actual = tarjeta[campo] || '';
    const anterior = tarjeta._medidasIot?.[campo];
    if (anterior === undefined ? !actual : actual === anterior) {
      siguiente[campo] = fuente._medidasIot[campo] || '';
      libro[campo] = siguiente[campo];
    }
  }
  siguiente._medidasIot = libro;
  return JSON.stringify(siguiente) === JSON.stringify(tarjeta) ? tarjeta : siguiente;
}

// Lo que sale de F1 hacia el documento SOLO puede ser catálogo: en la recepción
// la única entrada de sitio y calibre son chips, así que un valor fuera del
// catálogo de su tipo es jsonb adulterado y NO se siembra (patrón MEDIDA_RE de
// las medidas de lesión; hallazgo del auditor clínico 2026-09-10). El input
// libre de calibre de la Mesa no pasa por aquí y sigue siendo libre.
function valorRecepcionValido(fuente, campo) {
  const valor = typeof fuente?.[campo] === 'string' ? fuente[campo].trim() : '';
  if (!valor) return '';
  const catalogo = campo === 'calibre' ? calibresRecepcion(fuente?.tipo) : ubicacionesRecepcion(fuente?.tipo);
  return catalogo.includes(valor) ? valor : '';
}

export function sembrarInvasivosDesdeRecepcion(evolucion, dispositivosRecepcion = [], hoy = new Date(), { desdeUno = false } = {}) {
  if (!dispositivosRecepcion.length) return evolucion;

  const invasivos = evolucion?.sistemas?.invasivos || {};
  const lista = Array.isArray(invasivos.dispositivos) ? invasivos.dispositivos : [];
  const cuantosHay = (destino) => lista.filter((d) => tipoCanonico(d?.tipo) === destino).length;

  // Retrocompatibilidad: un borrador escrito ANTES de la memoria no trae
  // `_sembrados`, y sus tarjetas sembradas ya están en pantalla. La primera
  // pasada usa el conteo actual como semilla (lo visible se da por sembrado) y
  // recién ahí escribe la memoria, así la migración no duplica nada. Draft sin
  // la clave ⇒ conducta correcta sin migrar datos.
  const memoriaPrevia = Array.isArray(evolucion?._sembrados) ? evolucion._sembrados : null;
  const yaSembrados = new Set(memoriaPrevia || []);
  const cupoLegado = {};
  if (!memoriaPrevia) {
    for (const destino of Object.values(DISPOSITIVO_RECEPCION_A_EVOLUCION)) {
      cupoLegado[destino] = cuantosHay(destino);
    }
  }

  const nuevas = [];
  const memoria = [...(memoriaPrevia || [])];
  dispositivosRecepcion.forEach((fuente, i) => {
    const destino = DISPOSITIVO_RECEPCION_A_EVOLUCION[fuente?.tipo];
    if (!destino) return;
    // Los dispositivos de recepción siempre traen id (nuevoDispositivo); la
    // posición es el respaldo para datos armados a mano, y es estable mientras
    // no se reordene la lista.
    const idRecepcion = String(fuente?.id || `pos-${i}`);
    if (yaSembrados.has(idRecepcion)) return;
    yaSembrados.add(idRecepcion);
    memoria.push(idRecepcion);
    if (cupoLegado[destino] > 0) { cupoLegado[destino] -= 1; return; } // ya visible
    const dia = diaDispositivo(fuente, hoy, { desdeUno });
    nuevas.push(completarMedidasIot({
      ...nuevoDispositivoEvolucion(destino),
      id: `inv-${idRecepcion}`,
      origenRecepcionId: idRecepcion,
      dia: dia != null ? String(dia) : '',
      lumenes: lumenesTexto(fuente?.lumenes),
      // La ubicación declarada al recibir viaja con la tarjeta (2026-08-10):
      // igual que el día y los lúmenes, lo que se anotó arriba no se vuelve a
      // pedir abajo. Clave OPCIONAL en recepción: sin ella queda '' (el mismo
      // default de nuevoDispositivoEvolucion), o sea salida idéntica.
      ubicacion: valorRecepcionValido(fuente, 'ubicacion'),
      // Mismo trato para el CALIBRE (2026-09-10): clave OPCIONAL en recepción,
      // sin ella queda '' (el default de nuevoDispositivoEvolucion), o sea
      // salida idéntica a la de siempre. Ojo con el orden: `completarMedidasIot`
      // corre DESPUÉS y para el TOT manda `_medidasIot`, que es su fuente única.
      calibre: valorRecepcionValido(fuente, 'calibre'),
    }, fuente));
  });

  // La UBICACIÓN y el CALIBRE llegan tarde, y sin esto no llegaban nunca: la
  // tarjeta se siembra en el instante en que se agrega el dispositivo, y el
  // sitio y el calibre se eligen unos toques después. Por eso se rellena también sobre las tarjetas YA sembradas,
  // enlazadas por `origenRecepcionId` (la tarjeta EXACTA que salió de ese
  // dispositivo, nunca una escrita a mano en la Mesa) y SOLO cuando está vacía:
  // lo que alguien ya escribió abajo manda. Misma regla que el día
  // (autocompletarDiaInvasivos): se completa, jamás se pisa. Límite conocido y
  // aceptado: corregir el chip arriba después de que la tarjeta quedó con sitio
  // no la re-escribe; se corrige en la Mesa, que es donde vive el documento.
  const porOrigen = new Map(
    dispositivosRecepcion.map((f, i) => [String(f?.id || `pos-${i}`), f]),
  );
  let ubicacionesPuestas = false;
  const listaConUbicacion = lista.map((original) => {
    if (!original?.origenRecepcionId) return original;
    const fuente = porOrigen.get(String(original.origenRecepcionId));
    const d = completarMedidasIot(original, fuente);
    if (d !== original) ubicacionesPuestas = true;
    // Mismo criterio para los DOS campos que llegan tarde: se completa solo lo
    // que está vacío, uno a uno (un dispositivo puede traer sitio y no calibre).
    let salida = d;
    for (const campo of ['ubicacion', 'calibre']) {
      if (String(salida[campo] || '').trim()) continue;
      const valor = valorRecepcionValido(fuente, campo);
      if (!valor) continue;
      ubicacionesPuestas = true;
      salida = { ...salida, [campo]: valor };
    }
    return salida;
  });

  // Nada que sembrar, nada que completar Y nada nuevo que recordar: el MISMO
  // objeto (evita renders y escrituras de más, igual que autocompletarDiaInvasivos).
  if (!nuevas.length && !ubicacionesPuestas && memoria.length === (memoriaPrevia?.length || 0)) return evolucion;
  // Solo memoria (migración de un borrador viejo): la lista de dispositivos no
  // se toca ni por referencia.
  if (!nuevas.length && !ubicacionesPuestas) return { ...(evolucion || {}), _sembrados: memoria };

  return {
    ...(evolucion || {}),
    _sembrados: memoria,
    sistemas: {
      ...(evolucion?.sistemas || {}),
      invasivos: { ...invasivos, dispositivos: [...listaConUbicacion, ...nuevas] },
    },
  };
}

// Prosa de cada acceso PERMEABILIZABLE para el texto educativo. Vive acá y no
// en CATALOGO_DISPOSITIVOS porque es puro asunto de redacción (artículo,
// singular y plural), no una propiedad clínica del dispositivo. Las siglas se
// escriben tal cual: "los CVP", nunca "los cvps".
// Las claves siguen a PERMEABILIZABLES: la línea arterial salió de las dos el
// 2026-09-21 (ver el comentario de ese set). Dejar su prosa acá era código
// muerto que invitaba a devolverla.
const PROSA_PERMEABILIZABLE = {
  cvc: { art: 'el', artPl: 'los', uno: 'catéter venoso central', varios: 'catéteres venosos centrales' },
  picc: { art: 'el', artPl: 'los', uno: 'PICC', varios: 'PICC' },
  // La sigla sigue al catálogo: se alineó a "VVP" con la evolución el 20-ago.
  cvp: { art: 'el', artPl: 'los', uno: 'VVP', varios: 'VVP' },
  nexiva: { art: 'la', artPl: 'las', uno: 'Nexiva', varios: 'Nexiva' },
};

// Volumen estándar de la jeringa de permeabilización. El selector 10/5 cc de la
// recepción MURIÓ con la card interactiva (dictado del fundador 2026-08-10:
// "preparar jeringas y esas cosas, deja eso como texto"): la prosa enseña el
// estándar y remite al protocolo del servicio si ahí se usa otro tamaño.
export const JERINGA_ESTANDAR_CC = 10;

// Texto EDUCATIVO de la permeabilización (no una acción que marcar). Deriva del
// MISMO plan de siempre: una jeringa por rama vascular. Devuelve null si el
// paciente no tiene accesos vasculares (no se habla de lo que no hay).
//
// Anti-culpa: informa y enseña, no exige ni acusa. Puro y testeable.
export function textoPermeabilizacion(dispositivos = []) {
  // Agrupado POR TIPO: dos CVP se dicen "2 para los CVP", no dos veces lo mismo.
  const grupos = [];
  const porTipo = {};
  for (const d of dispositivos || []) {
    const cat = CATALOGO_DISPOSITIVOS.find((c) => c.id === d?.tipo);
    if (!cat || !cat.permeabilizable) continue;
    const ramas = cat.multiLumen ? Math.max(1, Math.min(5, Number(d.lumenes) || 1)) : 1;
    if (!porTipo[cat.id]) {
      porTipo[cat.id] = { id: cat.id, n: 0, ramas: 0, prosa: PROSA_PERMEABILIZABLE[cat.id] };
      grupos.push(porTipo[cat.id]);
    }
    porTipo[cat.id].n += 1;
    porTipo[cat.id].ramas += ramas;
  }
  const total = grupos.reduce((a, g) => a + g.ramas, 0);
  if (!total) return null;

  const cabecera = `Prepara ${total} ${total === 1 ? 'jeringa' : 'jeringas'} de ${JERINGA_ESTANDAR_CC} cc con SF 0,9%`;
  const frase = (g) => {
    const p = g.prosa;
    if (!p) return `${g.ramas} para ${g.n === 1 ? 'ese acceso' : 'esos accesos'}`;
    if (g.n === 1) {
      return g.ramas > 1
        ? `${g.ramas} para los lúmenes ${contraccionDe(p.art)} ${p.uno}`
        : `1 para ${p.art} ${p.uno}`;
    }
    return g.ramas > g.n
      ? `${g.ramas} para los lúmenes de ${p.artPl} ${p.varios}`
      : `${g.ramas} para ${p.artPl} ${p.varios}`;
  };

  // Un solo acceso: la frase del fundador, sin números que sobren.
  if (grupos.length === 1) {
    const g = grupos[0];
    const p = g.prosa;
    if (p && g.n === 1 && g.ramas > 1) return `${cabecera}: una por lumen ${contraccionDe(p.art)} ${p.uno}.`;
    if (p && g.n === 1) return `${cabecera} para ${p.art} ${p.uno}.`;
  }
  return `${cabecera}: ${listarProsa(grupos.map(frase))}.`;
}

// "de + el" es "del"; "de + la" se queda como está.
function contraccionDe(art) {
  return art === 'el' ? 'del' : 'de la';
}

// "a, b y c" (la lista corta de la prosa clínica).
function listarProsa(items) {
  const xs = items.filter(Boolean);
  if (xs.length <= 1) return xs[0] || '';
  return `${xs.slice(0, -1).join(', ')} y ${xs[xs.length - 1]}`;
}

// Plan de permeabilización: 1 jeringa de SF 0.9% por cada rama vascular
// (cada lumen de un CVC/PICC + la línea arterial + cada CVP). Ej: CVC 3L + LA
// = 4 jeringas. Devuelve { jeringas, detalle: ['3 CVC', '1 Línea arterial'] }.
export function planPermeabilizacion(dispositivos = []) {
  let jeringas = 0;
  const detalle = [];
  for (const d of dispositivos) {
    const cat = CATALOGO_DISPOSITIVOS.find((c) => c.id === d.tipo);
    if (!cat || !cat.permeabilizable) continue;
    const ramas = cat.multiLumen ? Math.max(1, Math.min(5, Number(d.lumenes) || 1)) : 1;
    jeringas += ramas;
    detalle.push(`${ramas} ${cat.label}`);
  }
  return { jeringas, detalle };
}

// Orden de atención: crítico → observación → estable; dentro de cada nivel,
// aislamiento preventivo primero, sin aislamiento al medio, de contacto/gotitas/
// aéreo al final. Empata por creado descendente.
const PESO_GRAVEDAD = {
  'De extrema gravedad': 0, 'Muy grave': 1, 'Grave': 2, 'De cuidado grave': 3, 'De cuidado': 4, 'Estable': 5,
};
const PESO_AISLAMIENTO = { 'Preventivo': 0, 'Ninguno': 1, 'Contacto': 2, 'Gotitas': 2, 'Aéreo': 3 };
export function ordenarPorPrioridad(registros = []) {
  return [...registros].sort((a, b) => {
    const g = (PESO_GRAVEDAD[a.gravedad] ?? 4.5) - (PESO_GRAVEDAD[b.gravedad] ?? 4.5);
    if (g !== 0) return g;
    const i = (PESO_AISLAMIENTO[a.aislamiento] ?? 1) - (PESO_AISLAMIENTO[b.aislamiento] ?? 1);
    if (i !== 0) return i;
    return (b.creado || 0) - (a.creado || 0);
  });
}

// ── M1 · Preparación global del turno ─────────────────────────────────────────
// Antes de la ronda conviene preparar UNA vez todo lo de la asignación: el total
// de jeringas de SF para permeabilizar (sumando todos los pacientes) y saber qué
// EPP llevar por los aislamientos. Función pura → agrega sobre todos los registros.
//
// Devuelve:
//   totalJeringas      — suma de jeringas de SF de toda la asignación
//   jeringasPorVolumen — [{ volumen, jeringas }] (cada paciente usa 10 o 5 cc)
//   porPaciente        — desglose por cama, ordenado por prioridad de atención
//   aislamientos       — pacientes con aislamiento (para llevar EPP), en orden
//   totalPacientes     — cuántos pacientes hay en la asignación
export function preparacionGlobal(registros = []) {
  const porVolumen = {};
  let totalJeringas = 0;
  const porPaciente = [];
  const aislamientos = [];

  for (const r of ordenarPorPrioridad(registros)) {
    const plan = planPermeabilizacion(r.dispositivos || []);
    const volumen = r.sfVolumen || 10;
    if (plan.jeringas > 0) {
      porVolumen[volumen] = (porVolumen[volumen] || 0) + plan.jeringas;
      totalJeringas += plan.jeringas;
      porPaciente.push({
        id: r.id,
        cama: sanitizarCama(r.cama),
        jeringas: plan.jeringas,
        volumen,
        detalle: plan.detalle,
      });
    }
    // Una fila por paciente con TODOS sus aislamientos: el EPP que hay que
    // llevar es la suma, no el más severo (`aislamiento` lleva la frase ya
    // armada y `aislamientos` la lista, para el icono del más severo).
    const suyos = aislamientosDe(r);
    if (suyos.length) {
      aislamientos.push({
        id: r.id,
        cama: sanitizarCama(r.cama),
        aislamiento: textoAislamientos(suyos),
        aislamientos: suyos,
        motivo: (r.aislamientoMotivo || '').trim(),
      });
    }
  }

  const jeringasPorVolumen = Object.entries(porVolumen)
    .map(([volumen, jeringas]) => ({ volumen: Number(volumen), jeringas }))
    .sort((a, b) => b.volumen - a.volumen);

  return {
    totalJeringas,
    jeringasPorVolumen,
    porPaciente,
    aislamientos,
    totalPacientes: registros.length,
  };
}

// ── M3 · Ronda de cada 2 h ────────────────────────────────────────────────────
// Checklist de la ronda: ítems universales que aplican a todo paciente, más un
// chequeo breve por cada dispositivo invasivo presente. Puro y des-identificado.
export const RONDA_UNIVERSAL = [
  'Cambio de posición / prevención de UPP',
  'Dolor y confort',
  'Sueros, BIC e infusiones en curso',
];

export const RONDA_DISPOSITIVO = {
  cvc:     'CVC: sitio, permeabilidad e infusiones',
  picc:    'PICC: sitio, permeabilidad e infusiones',
  la:      'Línea arterial: onda y perfusión distal',
  cvp:     'CVP: permeable y sin flebitis',
  foley:   'Foley: diuresis, circuito y fijación',
  sng:     'SNG/SNY: posición, permeabilidad y débito',
  tet:     'TET: presión de cuff y fijación',
  tqt:     'TQT: permeabilidad y aspiración',
  drenaje: 'Drenaje: débito y permeabilidad',
};

// Ítems de ronda de un paciente: universales + uno por cada tipo de dispositivo
// presente (sin duplicar si hay varios del mismo tipo).
export function itemsRonda(registro) {
  const items = [...RONDA_UNIVERSAL];
  for (const d of (registro?.dispositivos || [])) {
    const txt = RONDA_DISPOSITIVO[d.tipo];
    if (txt && !items.includes(txt)) items.push(txt);
  }
  return items;
}

// Id local estable sin depender de crypto (seguro en jsdom y navegador).
export function nuevoId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Hora HH:MM a partir de una fecha (default: ahora). Fuente única en momento.js.
export const horaHHMM = horaLocalHHMM;

// Un registro = un paciente/cama durante un turno.
// `diaDesdeUno` (2026-10-10): quien no es de Bupa cuenta el día de los
// dispositivos desde 1 (ver `cuentaDesdeUno`). La clave solo se escribe cuando
// es true: un registro de Bupa queda idéntico al de siempre.
export function nuevoRegistro({ cama = '', turno = null, diaDesdeUno = false } = {}) {
  const ahora = Date.now();
  return {
    ...(diaDesdeUno === true ? { diaDesdeUno: true } : {}),
    id: nuevoId(),
    cama: sanitizarCama(cama),
    turno: TURNOS.includes(turno) ? turno : null,
    creado: ahora,
    updated_at: ahora,
    cerrado: false,
    guardado: false,
    gravedad: null,
    // El más severo (compatibilidad) + la lista completa (2026-09-10).
    aislamiento: 'Ninguno',
    aislamientos: [],
    aislamientoMotivo: '',
    sfVolumen: 10,
    dispositivos: [],
    entradas: [],
  };
}

// ── Persistencia intermedia ───────────────────────────────────────────────────
// Efímero SIEMPRE (mandato del fundador, 2026-07-24, piloto Bupa): "al terminar
// el turno, se borra". El deadline principal es el FIN del turno según su tipo,
// más una gracia corta para cerrar y entregar con calma. Los fallbacks por
// inactividad (cerrado 12 h, abierto 24 h) quedan para registros sin turno
// declarado. El espejo remoto lo garantiza pg_cron en Supabase (migración
// registros_turno_borrado_fin_turno, misma regla en hora de Chile) aunque el
// usuario no vuelva a abrir la app.
//
// ARREGLO 2026-08-10 (reporte del fundador: "pasado las 00 se borra todo, para
// todos"). La gracia se contaba desde el fin TEÓRICO del turno, que es la misma
// hora de reloj para todo Chile: cualquier registro de turno Largo quedaba
// borrable a las 23:00 clavadas, tomaras la cama a las 08:00 o a las 19:00. Por
// eso desaparecía el trabajo de todos al mismo tiempo, y por eso al que iba
// atrasado se le borraba mientras seguía escribiendo. Ahora la gracia corre
// desde `max(fin del turno, último cambio)`: quien sale a las 20:00 pierde el
// registro a las 23:00 igual que antes, y quien está escribiendo a las 23:00 lo
// conserva hasta las 02:00. Esto además pone el código al día con lo que la
// Política de Privacidad YA prometía: "un registro no se borra mientras el
// usuario lo está tocando". El guard viejo de "tocado hace menos de 1 h" se
// eliminó porque quedó subsumido: la gracia de 3 h desde el último cambio es
// estrictamente más protectora.
export const HORAS_RETENCION = 12;
export const HORAS_RETENCION_ABIERTO = 24;
// Hora a la que TERMINA cada turno.
export const FIN_TURNO_HORA = { Largo: 20, Noche: 8, [TURNO_24]: 8 };
// Hora pivote para saber si ese fin cae hoy o mañana: si el registro se creó
// desde el pivote en adelante, el turno empezó HOY y termina mañana; si se creó
// antes, el turno venía de ayer y termina hoy. La Noche (20:00→08:00) pivotea a
// mediodía; el de 24 h (08:00→08:00) pivotea a las 08:00, que es su propio
// inicio. El Largo no cruza el día, así que no tiene pivote.
// El mapa vive en `momento.js`: fuente única (ver el comentario de allá).
export const GRACIA_FIN_TURNO_HORAS = 3;

// Timestamp (ms, hora local del dispositivo) en que TERMINA el turno del
// registro. null si el turno no está declarado.
export function finDeTurnoRegistro(registro) {
  if (!registro || !FIN_TURNO_HORA[registro.turno] || !registro.creado) return null;
  const creado = new Date(registro.creado);
  const fin = new Date(creado);
  fin.setHours(FIN_TURNO_HORA[registro.turno], 0, 0, 0);
  const pivote = PIVOTE_DIA_SIGUIENTE[registro.turno];
  if (pivote != null && creado.getHours() >= pivote) fin.setDate(fin.getDate() + 1);
  return fin.getTime();
}

export function turnoExpirado(registro, ahora = Date.now(), horas = null) {
  if (!registro) return false;
  const base = registro.updated_at || registro.creado || 0;
  if (horas != null) return ahora - base > horas * 3600 * 1000;
  // Regla principal: pasó el fin del turno Y pasaron 3 h desde tu último
  // cambio. El `max` es lo que impide borrarle el trabajo a quien sigue
  // documentando pasada la hora, y también cubre la documentación retroactiva
  // (un registro creado después del fin de su turno).
  const fin = finDeTurnoRegistro(registro);
  if (fin != null && ahora > Math.max(fin, base) + GRACIA_FIN_TURNO_HORAS * 3600 * 1000) {
    return true;
  }
  // Plazos por inactividad, para TODO registro, con o sin turno declarado
  // (cerrado 12 h, abierto 24 h desde el último cambio). Con turno rige lo
  // primero que ocurra; la tarea de la base usa la misma regla.
  const ventana = registro.cerrado ? HORAS_RETENCION : HORAS_RETENCION_ABIERTO;
  return ahora - base > ventana * 3600 * 1000;
}

// Separa los registros vivos de los expirados (vencidos, guardados incluidos).
export function purgarExpirados(registros = [], ahora = Date.now(), horas = null) {
  const vivos = [];
  const expirados = [];
  for (const r of registros) (turnoExpirado(r, ahora, horas) ? expirados : vivos).push(r);
  return { vivos, expirados };
}

// Marca local "ya emití evolucion_completa para este registro" (RegistroTurno
// la escribe; el guard persistido que dejó de sobrecontar el 2026-09-01). Vive
// acá y no en el componente porque la purga de expirados la borra junto con
// el registro: nada del turno sobrevive al turno, ni siquiera una marca de un
// byte con su id.
export const claveEvolucionCompleta = (id) => `mipae:evolucion-completa:${id}`;
export function borrarMarcasLocales(registro) {
  if (!registro?.id) return;
  try { localStorage.removeItem(claveEvolucionCompleta(registro.id)); } catch { /* sin storage */ }
  // El número al azar del reloj de interacción (relojInteraccion.js) muere
  // con el turno, igual que la marca de arriba.
  try { localStorage.removeItem(claveFichaRef(registro.id)); } catch { /* sin storage */ }
}

// ── "Retomar de tu turno anterior" (fix #3 auditoría MiPAE en turno) ────────────────
// Hoy el loop solo tiene salida: al cerrar, el registro desaparece del
// conjunto de trabajo y "Nuevo paciente" siempre parte en blanco, aunque sea
// la misma cama del turno anterior (recepción de contra-turno, "vuelvo a la
// 12 dos días seguidos"). Esta ventana busca en los registros CERRADOS Y
// GUARDADOS (nunca en los descartados) uno con la misma cama para ofrecer
// precargarlo — nunca automático, siempre una tarjeta que el usuario puede
// ignorar. Acotada a la retención de 12 h (2026-07-24): más allá el registro
// ya no existe, así que la ventana cubre el contra-turno inmediato
// (noche → día), no el "vuelvo en dos días".
export const HORAS_RETOMABLE = HORAS_RETENCION;

// El candidato más reciente con esa cama, guardado+cerrado, dentro de la
// ventana. null si no hay ninguno (cama nueva, o ya pasó la ventana).
export function registroRetomable(registros = [], cama, ahora = Date.now()) {
  const camaSan = sanitizarCama(cama);
  if (!camaSan) return null;
  const candidatos = (registros || []).filter((r) => (
    r && r.cerrado && r.guardado
    && sanitizarCama(r.cama) === camaSan
    && (ahora - (r.updated_at || r.creado || 0)) <= HORAS_RETOMABLE * 3600 * 1000
  ));
  if (!candidatos.length) return null;
  return candidatos.reduce((mas, r) => (
    (r.updated_at || r.creado || 0) > (mas.updated_at || mas.creado || 0) ? r : mas
  ));
}

// Resumen de una línea para la card ("CVC 3L + Foley · contacto"): dispositivos
// + aislamiento (si aplica). '' si el turno anterior no traía ninguno de los dos.
export function resumenRetomable(registro) {
  const partes = [];
  const disp = (registro?.dispositivos || [])
    .map((d) => {
      const cat = CATALOGO_DISPOSITIVOS.find((c) => c.id === d.tipo);
      if (!cat) return null;
      return cat.multiLumen && d.lumenes ? `${cat.label} ${d.lumenes}L` : cat.label;
    })
    .filter(Boolean);
  if (disp.length) partes.push(disp.join(' + '));
  const aisl = textoAislamientos(aislamientosDe(registro));
  if (aisl) partes.push(aisl);
  return partes.join(' · ');
}

// Precarga gravedad, aislamiento (+ motivo), dispositivos (con su fecha de
// instalación — ver "días-catéter" arriba) y antecedentes/anamnesis de un
// turno guardado anterior sobre un registro NUEVO recién creado. Las
// ENTRADAS y las tareas quedan tal cual las trae `base` (vacías): el turno
// nuevo es un documento nuevo, no una copia — solo el contexto del paciente
// se retoma. `permeabilizado`/`cuidados` de cada dispositivo se reinician:
// son la valoración de ESTE turno, no del anterior. Pura: no muta `anterior`.
export function precargarDesde(base, anterior) {
  if (!anterior) return base;
  const antecedentes = anterior.evolucion?.antecedentes;
  const tieneAntecedentes = antecedentes && (
    (antecedentes.dx || '').trim() || (antecedentes.morbidos || '').trim()
    || (antecedentes.quirurgicos || '').trim() || (antecedentes.anamnesis || '').trim()
  );
  // El examen del turno nuevo NO se copia (es una foto nueva, con su propia
  // hora sellada), pero los HALLAZGOS del ingreso anterior viajan como
  // referencia médico-legal (solo catálogo, nunca la nota): una LPP registrada
  // al ingreso previo sigue siendo "venía de antes". Device-local, PII-safe.
  const examenPrevio = resumenExamenPrevio(anterior.examenFisico);
  // El servicio elegido para el paciente (presentación de la evolución, ver
  // lib/contextoEvolucion.js) viaja con él: es la misma persona en la misma
  // cama. Catálogo cerrado re-validado al leer; sin la clave, nada cambia.
  const contexto = typeof anterior.evolucion?.contexto === 'string' ? anterior.evolucion.contexto : null;
  const evolucionPrecargada = {
    ...(tieneAntecedentes ? { antecedentes } : {}),
    ...(contexto ? { contexto } : {}),
  };
  return {
    ...base,
    gravedad: GRAVEDADES.includes(anterior.gravedad) ? anterior.gravedad : null,
    ...conAislamientos({}, aislamientosDe(anterior)),
    aislamientoMotivo: anterior.aislamientoMotivo || '',
    sfVolumen: anterior.sfVolumen || 10,
    dispositivos: (anterior.dispositivos || []).map((d) => ({
      ...d,
      id: nuevoId(),
      permeabilizado: false,
      cuidados: [],
    })),
    ...(Object.keys(evolucionPrecargada).length ? { evolucion: evolucionPrecargada } : {}),
    ...(examenPrevio ? { examenPrevio } : {}),
  };
}

// Alta en lote de camas al recibir turno: "3, 5, 7, 12" → 4 camas sanitizadas,
// sin duplicar vacíos. Puro: no crea registros, solo parsea la lista.
export function sanitizarListaCamas(texto) {
  return String(texto || '')
    .split(',')
    .map((c) => sanitizarCama(c))
    .filter(Boolean);
}

// datos estructurados del catálogo cerrado — NO incluye las notas libres, para
// minimizar lo que viaja a la IA. Devuelve '' si no hay nada estructurado.
export function contextoClinico(registro) {
  const r = registro || {};
  const sistemas = {};
  const acciones = [];

  for (const e of (r.entradas || [])) {
    if (e.tipo === 'valoracion') {
      for (const sis of SISTEMAS) {
        const sel = (e.datos?.sistemas?.[sis.id] || []).filter((o) => sis.opciones.includes(o));
        if (sel.length) sistemas[sis.label] = Array.from(new Set([...(sistemas[sis.label] || []), ...sel]));
      }
    } else if (e.tipo === 'farmaco') {
      const med = [e.datos?.nombre, e.datos?.dosis, VIAS.includes(e.datos?.via) ? e.datos.via : null]
        .filter((x) => x && String(x).trim()).join(' ');
      // nombre/dosis son texto libre: no los mandamos a la IA si traen PII.
      if (med && !detectPii(med).hit) acciones.push(`fármaco ${med}`);
    } else if (e.tipo === 'procedimiento' && PROCEDIMIENTOS.includes(e.datos?.procedimiento)) {
      acciones.push(e.datos.procedimiento.toLowerCase());
    }
  }

  const partesSistemas = Object.entries(sistemas).map(([k, v]) => `${k}: ${v.join(', ')}`);
  const dispositivos = (r.dispositivos || [])
    .map((d) => {
      const cat = CATALOGO_DISPOSITIVOS.find((c) => c.id === d.tipo);
      if (!cat) return null;
      return cat.multiLumen && d.lumenes ? `${cat.label} ${d.lumenes} lúmenes` : cat.label;
    })
    .filter(Boolean);

  // NO se incluye la cama: la palabra "cama N" dispara el guard anti-PII de
  // Revisa y, además, la ubicación no aporta nada al consejo clínico.
  const lineas = [];
  const aislTexto = textoAislamientos(aislamientosDe(r));
  const estado = [
    r.turno ? `turno ${r.turno.toLowerCase()}` : null,
    GRAVEDADES.includes(r.gravedad) ? r.gravedad.toLowerCase() : null,
    aislTexto ? `aislamiento ${aislTexto}` : null,
  ].filter(Boolean);
  if (estado.length) lineas.push(`Paciente: ${estado.join(', ')}.`);
  if (dispositivos.length) lineas.push(`Dispositivos: ${dispositivos.join(', ')}.`);
  if (partesSistemas.length) lineas.push(`Hallazgos por sistema: ${partesSistemas.join('; ')}.`);
  if (acciones.length) lineas.push(`Acciones del turno: ${Array.from(new Set(acciones)).join(', ')}.`);
  return lineas.join(' ');
}

// ── Compilado cronológico completo para "Armar desde mis notas del turno" ────
// A diferencia de contextoClinico (arriba: solo lo estructurado, sin notas, para
// dar contexto a Revisa) y de construirRelatoTurno (turnoEvolucionBridge.js: solo
// lo AMBIGUO que el enrutado determinista F1 no resuelve, para no duplicar), esta
// función vuelca TODA la cronología del turno —valoraciones, fármacos,
// procedimientos, visitas y notas— en un relato plano cronológico (hora + texto),
// ordenado por hora. Es la fuente de "Armar desde mis notas del turno": la IA
// (estructurarEvolucion) reparte este relato completo por sistema y mergeEvolucion
// lo funde sin pisar lo que el usuario ya marcó a mano.
//
// Mismo patrón anti-PII que contextoClinico: el texto libre (nota de la entrada,
// nombre/dosis del fármaco, insumos del procedimiento) se descarta si detecta PII
// — nunca nombres ni RUT viajan a la IA. Pura: no muta nada, no llama IA.
export function compilarNotasTurno(registro) {
  return lineasNotasTurno(registro).map((l) => l.linea).join('\n');
}

// El mismo relato, con cada nota numerada ("[1] 10:58: …"), y el mapa número →
// id de entrada. Es lo que viaja al reparto por IA desde la revisión del
// 2026-09-07: la respuesta vuelve por FRAGMENTOS atribuidos a esos números,
// así cada frase de la IA sabe de qué nota salió (identidad y versión) y el
// documento puede ordenarla por el hecho, sin partir frases por palabras.
export function relatoNotasTurno(registro) {
  const lineas = lineasNotasTurno(registro, { anotarRepresentado: true });
  return {
    texto: lineas.map((l, i) => `[${i + 1}] ${l.linea}`).join('\n'),
    ids: lineas.map((l) => l.id),
  };
}

// Ids de las entradas que compilarNotasTurno incluiría hoy — el llamador las
// anota en `evolucion._notasEnrutadas` cuando el reparto IA tuvo éxito, para
// que la próxima pasada lleve SOLO lo nuevo (evolución real del fundador,
// 2026-07-30: llegó de pabellón con 3 notas nuevas y re-armar habría
// duplicado las de la mañana... o peor, nadie re-armó y el neuro quedó mudo).
export function idsNotasEvolucion(registro) {
  return lineasNotasTurno(registro).map((l) => l.id).filter(Boolean);
}

// Lo que la película determinista YA escribió de una nota (campos dictados y
// suspensiones), en palabras de catálogo, para decirle al reparto que no lo
// vuelva a narrar (corrida real 2026-09-08: cada constatación salía dos veces,
// una por la película y otra por la IA). Catálogo cerrado: labels y valores
// de `datos.escritos`, jamás texto libre. La nota sigue viajando ENTERA con su
// identidad y versión: lo que agrega (síntomas, números, escalas) lo narra la IA.
export function representadoEnMesa(e) {
  const escritos = Array.isArray(e?.datos?.escritos) ? e.datos.escritos : [];
  const partes = [];
  for (const w of escritos) {
    if (Array.isArray(w.retirado) && w.retirado.length) { partes.push(`suspensión de ${w.retirado.map((d) => String(d).toLowerCase()).join(' y ')}`); continue; }
    const label = labelCampoDictado(w.sistema, w.k);
    const valor = Array.isArray(w.ahora) ? w.ahora.join(', ') : w.ahora;
    if (label && valor !== undefined && valor !== null && String(valor).trim() !== '') partes.push(`${String(label).toLowerCase()} ${String(valor).toLowerCase()}`);
  }
  return partes;
}

// ── Una nota anotada que el reparto dejó sin fragmento NO se pierde ──────────
// Segunda corrida real (2026-09-08, 93c32c8f, caso G): "Queda con naricera 1
// litro" escribió el campo soporte=oxigenoterapia, viajó anotada "(ya
// registrado en la Mesa: soporte oxigenoterapia)" y el modelo decidió que no
// agregaba nada: no emitió fragmento. Pero la película solo imprime "se
// constata con oxigenoterapia"; el dispositivo y el flujo no estaban en ningún
// lado y el documento los perdió. "Ya registrado: oxigenoterapia" no significa
// que el dispositivo, el flujo, la saturación ni la respuesta del paciente
// estén representados. Regla determinista, además del prompt: si una nota
// anotada no recibió ningún fragmento y su texto trae algo que las palabras
// de la anotación no cubren, la nota entra VERBATIM al sistema del campo que
// escribió, atribuida a su número (procedencia y posición temporal intactas).
// Nada de lo dicho se pierde; lo que sí quedó representado no se repite.
const _palabrasDe = (t) => normalizarPalabras(t).split(/[^a-z0-9]+/).filter(Boolean);
function normalizarPalabras(t) {
  return String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
// Palabras de la nota que la anotación NO cubre: cualquier palabra de 3 letras
// o más, o un número, ausente de los labels/valores representados.
export function notaAportaMasQueRepresentado(nota, representado) {
  const cubiertas = new Set(_palabrasDe((representado || []).join(' ')));
  return _palabrasDe(nota).some((w) => (w.length >= 3 || /^\d+$/.test(w)) && !cubiertas.has(w));
}
export function notasOmitidasPorReparto(registro, ids, estructura) {
  const porId = new Map((registro?.entradas || []).map((e) => [e?.id, e]));
  const referidas = new Set();
  for (const sis of Object.values(estructura?.sistemas || {})) {
    for (const f of sis?.fragmentos || []) {
      if (typeof f?.texto !== 'string' || !f.texto.trim()) continue;
      for (const n of f?.de || []) if (Number.isInteger(n)) referidas.add(n);
    }
  }
  const out = [];
  (ids || []).forEach((id, i) => {
    const n = i + 1;
    if (referidas.has(n)) return;
    const e = porId.get(id);
    const escritos = Array.isArray(e?.datos?.escritos) ? e.datos.escritos.filter((w) => w?.sistema) : [];
    const nota = String(e?.nota || '').trim();
    if (!nota || detectPii(nota).hit) return;
    if (!notaAportaMasQueRepresentado(nota, representadoEnMesa(e))) return;
    // Sin destino confirmado no inventar un sistema ni consumir la fuente.
    // El llamador captura el error antes de integrar y conserva las notas pendientes.
    if (!escritos.length) {
      const error = new Error('La respuesta dejó notas sin integrar. Tus notas siguen guardadas y pendientes; vuelve a armar la evolución antes de copiarla.');
      error.codigo = 'notas_sin_representar';
      throw error;
    }
    const sistema = SISTEMAS_EVOLUCION.some((s) => s.id === escritos[0].sistema) ? escritos[0].sistema : 'procedimientos';
    const texto = `${nota.replace(/\.+$/, '')}.`;
    out.push({ sistema, de: [n], texto: texto.charAt(0).toUpperCase() + texto.slice(1) });
  });
  return out;
}
// Devuelve la estructura con esas notas agregadas como fragmentos (misma forma
// que sanitizarEstructura: `fragmentos` + `nota` unida por salto de línea).
// Misma referencia si no hay nada que agregar.
export function completarNotasOmitidas(estructura, registro, ids) {
  const omitidas = notasOmitidasPorReparto(registro, ids, estructura);
  if (!omitidas.length) return estructura;
  const sistemas = { ...(estructura?.sistemas || {}) };
  for (const o of omitidas) {
    const prev = sistemas[o.sistema] || { hallazgos: [], nota: '', fragmentos: [] };
    const fragmentos = [...(prev.fragmentos || []), { de: o.de, texto: o.texto }];
    sistemas[o.sistema] = { ...prev, hallazgos: prev.hallazgos || [], fragmentos, nota: fragmentos.map((f) => f.texto).join('\n') };
  }
  return { ...estructura, sistemas, _notasCompletadas: omitidas.map((o) => o.de[0]) };
}

function lineasNotasTurno(registro, { anotarRepresentado = false } = {}) {
  const entradas = Array.isArray(registro?.entradas) ? registro.entradas : [];
  const ordenadas = ordenarPorHecho(entradas, registro);
  // Procedimientos que "Traer mis acciones" (F1, determinista) ya llevó a la
  // evolución: no viajan de nuevo a la IA — el usuario los vería duplicados
  // (una vez como dispositivo/nota del F1 y otra como prosa del reparto IA).
  const yaEnrutadas = new Set(registro?.evolucion?._accionesEnrutadas || []);
  // Notas que un "Armar desde mis notas" anterior ya repartió por sistema:
  // tampoco se reenvían (mergeEvolucion no distingue una frase re-repartida
  // de una nueva y el texto saldría duplicado).
  const notasConsumidas = new Set(registro?.evolucion?._notasEnrutadas || []);
  // Retiros que la película de la cronología narra determinista al imprimir
  // (peliculaDesdeAcciones): no viajan a la IA — el documento diría el mismo
  // hecho dos veces. Fuente única del criterio: retirosNarrables.
  const narradosPelicula = new Set(retirosNarrables(registro).map((e) => e.id));
  const lineas = [];

  for (const e of ordenadas) {
    if (e?.tipo === 'procedimiento' && e?.id && yaEnrutadas.has(e.id)) continue;
    if (e?.id && notasConsumidas.has(e.id)) continue;
    if (e?.id && narradosPelicula.has(e.id)) continue;
    const partes = [];

    if (e?.tipo === 'valoracion') {
      for (const sis of SISTEMAS) {
        const sel = (e.datos?.sistemas?.[sis.id] || []).filter((o) => sis.opciones.includes(o));
        if (sel.length) partes.push(`${sis.label}: ${sel.join(', ')}`);
      }
    } else if (e?.tipo === 'farmaco') {
      const med = [e.datos?.nombre, e.datos?.dosis, VIAS.includes(e.datos?.via) ? e.datos.via : null]
        .filter((x) => x && String(x).trim())
        .join(' ')
        .trim();
      // nombre/dosis son texto libre: no los mandamos a la IA si traen PII.
      if (med && !detectPii(med).hit) partes.push(`Fármaco ${med}`);
    } else if (e?.tipo === 'procedimiento' && e.datos?.accion && accionNarradaPorPelicula(e)) {
      // Intubación y extubación las narra la PELÍCULA determinista (ver
      // efectoEstadoDeAccion): mandarlas también al reparto imprimía el mismo
      // hecho dos veces (una por sistema, otra por la IA). No viajan. La
      // excepción es la nota de la autora que trae un NÚMERO que la prosa de
      // catálogo no tiene ("queda con naricera a 2 litros"): esa sí viaja,
      // porque nada de lo dicho se pierde.
      const fiel = e.fuente === 'texto' && (e.nota || '').trim() && !detectPii(e.nota).hit;
      if (fiel && notaAportaNumero(e.nota, compilarAccionRapida(e.datos))) partes.push(e.nota.replace(/\.+$/, ''));
      else continue;
    } else if (e?.tipo === 'procedimiento' && e.datos?.accion) {
      // Acción rápida: prosa compilada de catálogo cerrado, PII-safe por
      // construcción — viaja tal cual al reparto por sistema.
      // SALVO cuando la acción nació de un TEXTO de la autora (`fuente:
      // 'texto'`): ahí la nota ES la fuente fiel y viaja ella sola. La frase
      // compilada es una interpretación de catálogo que puede haber perdido
      // información (el motivo, una observación) o haberla leído mal; la IA
      // tiene que recibir lo que se escribió, no solo lo que se entendió.
      const fiel = e.fuente === 'texto' && (e.nota || '').trim() && !detectPii(e.nota).hit;
      const txt = fiel ? '' : compilarAccionRapida(e.datos);
      if (txt) partes.push(txt.replace(/\.$/, ''));
    } else if (e?.tipo === 'procedimiento' && PROCEDIMIENTOS.includes(e.datos?.procedimiento)) {
      const insumos = (e.datos?.insumos || '').trim();
      const sinPii = !insumos || !detectPii(insumos).hit;
      partes.push(`Procedimiento ${e.datos.procedimiento}${sinPii && insumos ? ` (${insumos})` : ''}`);
    } else if (e?.tipo === 'visita' && visitaCanonica(e.datos?.visita)) {
      partes.push(visitaCanonica(e.datos.visita));
    }

    // La nota libre de cualquier entrada es el relato rico — el oro para la IA
    // (igual que construirRelatoTurno), pero se descarta entera si trae PII.
    const nota = (e?.nota || '').trim();
    if (nota && !detectPii(nota).hit) partes.push(nota.replace(/\.+$/, ''));

    if (!partes.length) continue;
    const texto = partes.join('. ').trim();
    const conPunto = texto.endsWith('.') ? texto : `${texto}.`;
    const yaRepresentado = anotarRepresentado ? representadoEnMesa(e) : [];
    const anotacion = yaRepresentado.length ? ` (ya registrado en la Mesa: ${yaRepresentado.join('; ')})` : '';
    lineas.push({ id: e?.id || null, linea: e?.hora ? `${e.hora}: ${conPunto}${anotacion}` : `${conPunto}${anotacion}` });
  }

  return lineas;
}

// Datos estructurados por defecto según el tipo de entrada.
export function datosPorDefecto(tipo) {
  switch (tipo) {
    case 'valoracion':    return { sistemas: {} };          // { sistemaId: [hallazgo] }
    case 'farmaco':       return { nombre: '', slug: null, dosis: '', via: null, correctos: [], frecuencia: null };
    case 'procedimiento': return { procedimiento: null, insumos: '' };
    case 'visita':        return { visita: null };
    default:              return {};
  }
}

export function nuevaEntrada(tipo, { hora, momento, ahora = new Date() } = {}) {
  return {
    id: nuevoId(),
    tipo,
    // `hora` se conserva como la clave que leen todos los consumidores viejos;
    // `momento` es la forma completa del hecho (fecha + hora + precisión +
    // zona) que estrena la entrega 2. Las dos se escriben juntas y no pueden
    // divergir: `hora` es siempre `momento.hora` (o '' si es desconocida).
    ...momentoDeEntradaNueva({ hora, momento, ahora }),
    // `ts`: sello del SISTEMA al crear la entrada (patrón examen.ts). La hora
    // del HECHO la puede corregir el autor; este sello de REGISTRO no se edita
    // jamás, ni al corregir ni al repetir (ahí nace uno nuevo).
    ts: ahora instanceof Date ? ahora.getTime() : Date.now(),
    // Versión del contenido: 1 al crear, +1 en cada corrección. Crece siempre,
    // aunque el contenido vuelva a un valor anterior (A → B → A da v1, v2, v3).
    v: 1,
    datos: datosPorDefecto(tipo),
    nota: '',
  };
}

// Las dos claves del tiempo del hecho, escritas juntas.
function momentoDeEntradaNueva({ hora, momento, ahora = new Date() } = {}) {
  const m = momento || (hora ? { ...momentoAhora(ahora), hora } : momentoAhora(ahora));
  return { hora: m.hora || '', momento: m };
}

// ── Corrección de una entrada (entrega 2, lote 1) ───────────────────────────
// Editar CORRIGE el evento original: misma identidad, misma marca de registro,
// versión nueva. El historial guarda SOLO los campos que cambiaron y su valor
// anterior: una corrección de hora pesa unos pocos bytes y el texto de la nota
// se duplica únicamente cuando la nota fue lo que cambió. Vive dentro del mismo
// registro, así que nace y muere con él (misma retención, misma RLS, jamás
// telemetría).
//
// Cuando el historial acumulado supera el tope, las versiones más ANTIGUAS se
// colapsan en un marcador que conserva qué campos cambiaron y cuándo, y declara
// que los valores previos ya no están (`colapsado: true`). No se borra en
// silencio: el rastro de que hubo esas correcciones sobrevive.
export const HISTORIAL_MAX_BYTES = 8192;
const CAMPOS_VERSIONADOS = ['hora', 'momento', 'nota', 'datos', 'tipo'];

export function editarEntrada(prev, cambios, { ahora = new Date() } = {}) {
  if (!prev || typeof prev !== 'object') return prev;
  const propuesta = { ...prev, ...cambios };
  // `hora` y `momento` no pueden divergir: si el llamador toca una, manda ella.
  if (cambios && Object.prototype.hasOwnProperty.call(cambios, 'momento') && cambios.momento) {
    propuesta.hora = cambios.momento.hora || '';
  } else if (cambios && Object.prototype.hasOwnProperty.call(cambios, 'hora') && prev.momento) {
    propuesta.momento = { ...prev.momento, hora: cambios.hora || null, precision: cambios.hora ? (prev.momento.precision === 'desconocida' ? 'exacta' : prev.momento.precision) : 'desconocida' };
  }
  const previos = {};
  for (const k of CAMPOS_VERSIONADOS) {
    if (huellaDe(propuesta[k] ?? null) !== huellaDe(prev[k] ?? null)) previos[k] = prev[k] ?? null;
  }
  // Nada cambió de verdad: no se inventa una versión (un guardado que solo
  // reordena las claves de `datos` no es una corrección; huellaDe es canónica).
  // Se decide ANTES de podar el historial: una corrección que solo cambia la
  // nota sigue siendo una corrección aunque su texto anterior no se guarde.
  if (!Object.keys(previos).length) return prev;
  const cambiados = Object.keys(previos);
  // `hora` y `momento` son el MISMO dato con dos formas (editarEntrada los
  // mantiene sincronizados): guardar los dos duplicaba los bytes de la
  // corrección más común y hacía colapsar el historial antes de tiempo. La
  // hora anterior se recupera de `previos.momento.hora`.
  if ('hora' in previos && previos.momento) delete previos.hora;
  // La nota anterior pasa por la MISMA guarda de PII que la nota vigente. El
  // gesto de corregir una nota es justo el del arrepentimiento ("se me fue un
  // nombre"): si lo borrado sobreviviera en el historial, borrarlo no lo
  // borraría. Cuando la guarda pega, queda la señal de que la nota cambió y no
  // el texto. Límite honesto: lo que ningún patrón caza (un nombre de pila
  // suelto) sigue vivo en el historial hasta que la fila se purgue al fin del
  // turno, igual que la nota vigente.
  const omitidos = [];
  if (typeof previos.nota === 'string' && previos.nota && notaConPii(previos.nota)) {
    delete previos.nota;
    omitidos.push('nota');
  }
  const v = Number.isFinite(prev.v) ? prev.v : 1;
  const ts = ahora instanceof Date ? ahora.getTime() : Number(ahora) || Date.now();
  // `campos` solo se escribe cuando algo se omitió: dice QUÉ se corrigió aunque
  // su valor anterior no esté, que es la misma forma que usa el colapsado.
  const version = omitidos.length
    ? { v, ts, campos: cambiados, previos, omitidos }
    : { v, ts, previos };
  const historial = acotarHistorial([...(Array.isArray(prev.historial) ? prev.historial : []), version]);
  return { ...propuesta, id: prev.id, ts: prev.ts, v: v + 1, editadoTs: ts, historial };
}

function acotarHistorial(historial) {
  let out = historial;
  // `out.length >= 1`: una sola corrección de una nota larguísima también se
  // colapsa. Con la guarda en `> 1` se escapaba entera del tope, en silencio.
  while (JSON.stringify(out).length > HISTORIAL_MAX_BYTES && out.length >= 1 && !(out.length === 1 && out[0].colapsado)) {
    const [viejo, ...resto] = out;
    const campos = [...new Set([...(viejo.campos || []), ...Object.keys(viejo.previos || {})])];
    out = [{ v: viejo.v, ts: viejo.ts, campos, colapsado: true }, ...resto];
    // Si el más viejo ya estaba colapsado, se funde con el siguiente.
    if (viejo.colapsado && out.length > 1) {
      const [a, b, ...cola] = out;
      // `ts` es el de la versión MÁS VIEJA que el marcador representa: tomar el
      // del siguiente hacía que el marcador afirmara que la versión `a.v`
      // ocurrió a una hora en la que no ocurrió.
      out = [{ v: a.v, ts: a.ts, campos: [...new Set([...(a.campos || []), ...(b.campos || []), ...Object.keys(b.previos || {})])], colapsado: true }, ...cola];
    }
  }
  return out;
}

// "Repetir ahora": otro cuidado, no una corrección. Identidad nueva, momento de
// registro nuevo, momento del hecho = ahora, versión de vuelta en 1 y sin
// historial (el historial pertenece al evento que se corrigió, no a este).
export function repetirEntradaDesde(entrada, { ahora = new Date() } = {}) {
  const { historial, editadoTs, ...limpia } = entrada || {};
  const m = momentoAhora(ahora);
  return {
    ...limpia,
    id: nuevoId(),
    ts: ahora instanceof Date ? ahora.getTime() : Date.now(),
    v: 1,
    hora: m.hora,
    momento: m,
  };
}

// Corregir CUÁNDO se instaló un invasivo re-calcula el día del dispositivo.
//
// El día del catéter se fija al registrar la acción (`aplicarEfectoAccion`), y
// desde el lote 1 la fecha del hecho se puede corregir: sin esto, decir "fue
// ayer" arreglaba la cronología y dejaba el contador de días con el valor
// viejo, o sea la alerta del CVP sonando un día tarde.
//
// El dispositivo se identifica por su VÍNCULO con el evento (`origenEntradaId`),
// nunca por coincidencia de tipo y fecha (U4): dos vías del mismo tipo puestas
// el mismo día son indistinguibles, y "la que coincide" puede ser la otra.
// Sin vínculo (dispositivo de la recepción, o anterior a este cambio) no se
// toca nada, y `discrepanciasEstado` lo muestra en vez de adivinar.
// Y si la fecha del dispositivo ya no es la que este camino le puso, es que la
// autora la cambió a mano: se conserva su edición y también queda a la vista.
// Nunca retracta: solo corrige una fecha suya.
export function reajustarInstalacionPorMomento(registro, entrada, fechaAntes) {
  const { accion } = entrada?.datos || {};
  if (accion !== 'instalacion' && accion !== 'intubacion') return registro;
  const m = momentoDeEntrada(entrada, registro);
  const fechaAhora = m?.hora && m?.fecha ? m.fecha : null;
  if (!fechaAhora || !fechaAntes || fechaAhora === fechaAntes) return registro;
  const lista = registro?.dispositivos || [];
  const disp = lista.find((d) => d.origenEntradaId && d.origenEntradaId === entrada?.id);
  if (!disp || disp.fechaInstalacion !== fechaAntes) return registro;
  return { ...registro, dispositivos: lista.map((d) => (d === disp ? { ...d, fechaInstalacion: fechaAhora } : d)) };
}

// ── La vía aérea se reconstruye desde sus hechos (cierre de integridad 2026-09-06) ──
// El orden de ingreso no es el orden clínico: se registra atrasado y se
// corrige después. Aplicar cada intubación o extubación "sobre la lista tal
// como está" hacía que una extubación de las 10:00 ingresada a las 16:00
// sacara el tubo de la intubación de las 14:00, y que una intubación histórica
// ingresada al final volviera a poner un tubo ya retirado.
//
// En vez de eso, el tubo vigente se DERIVA: se toman todos los hechos de vía
// aérea del registro, se ordenan por el momento del HECHO con el predicado
// estricto (`esPosteriorA`), y se recorren desde lo recibido:
//  - lo recibido es el tubo SIN vínculo (declarado en F1: anterior a todo hecho
//    del turno por definición; también los tubos anteriores a este cambio);
//  - una intubación pone un tubo (el suyo, por `origenEntradaId`, si ya existe:
//    así se conservan las ediciones manuales) salvo que ya haya uno puesto,
//    que es la misma idempotencia de siempre (registrar dos veces no crea dos);
//  - una extubación saca el tubo puesto y GUARDA qué sacó en la entrada
//    (`efecto.dispositivoRetirado`): con eso una corrección de hora que la
//    mueve antes de la intubación devuelve el tubo, y un tubo recibido que se
//    fue no se pierde para el replay.
// Ante lo que no se puede establecer no se toca la lista y se declara la
// discrepancia (catálogo cerrado, `discrepanciasEstado` la muestra):
//  - dos hechos consecutivos que no se pueden ordenar (hora desconocida,
//    aproximada demasiado cerca, empate);
//  - dos tubos puestos a la vez (un vínculo que contradice al recibido);
//  - una extubación cuando no hay ningún tubo puesto (queda registrada, no
//    saca nada).
// Lo que la autora borró a mano de la ficha no reaparece: la intubación que
// ya creó su tubo lo anota en `efecto.dispositivoId`, y si ese tubo no está,
// es que alguien lo quitó. Límite honesto: un tubo creado antes de este
// cambio (sin esa anotación) y borrado a mano sí se recrearía.
// Puro: devuelve el MISMO registro cuando nada cambia. `entrada` (opcional) es
// el hecho que se está aplicando, por si aún no está en `entradas`.
export const VIA_AEREA_DISCREPANCIAS = {
  orden: 'via_aerea_orden',
  secuencia: 'via_aerea_secuencia',
  extubacionSinTubo: 'extubacion_sin_tubo',
  // El tubo que puso una intubación no está en la ficha y ningún hecho
  // posterior lo sacó: lo quitó alguien a mano, o lo sacó un hecho que ya se
  // borró sin dejar rastro. No se recrea; se dice.
  tuboNoEnFicha: 'tubo_no_en_ficha',
  // Un tubo vinculado a un registro que ya no existe.
  tuboSinFuente: 'tubo_sin_fuente',
};

// El retiro por el chip "Retiro" cuenta como salida del tubo cuando apunta a
// un tubo (por `dispositivoId` o por el label TOT): así el replay lo
// REPRESENTA y una corrección o un borrado posterior no resucita lo que la
// autora retiró a mano.
function esRetiroDeTubo(e, ids = null) {
  if (e?.datos?.accion !== 'retiro' || !accionRapidaCompleta(e.datos)) return false;
  const v = e.datos.valores || {};
  // Por id cuando el tubo se conoce; si el id ya no apunta a ningún tubo
  // (el tubo se fue con su fuente), vale el label: el hecho sigue siendo el
  // retiro de un tubo.
  if (v.dispositivoId && ids && ids.has(String(v.dispositivoId))) return true;
  return dispositivoPorLabel(v.dispositivo)?.id === 'tet';
}

// ¿Es un hecho de la vía aérea en ESTE registro? (intubación, extubación, o
// retiro de un tubo; ids = los tubos conocidos de la ficha). Lo usa la ficha
// para decidir si una edición o un borrado tienen que volver a derivar el tubo.
export function esHechoViaAerea(entrada, registro) {
  const lista = Array.isArray(registro?.dispositivos) ? registro.dispositivos : [];
  return esEventoViaAerea(entrada, new Set(lista.filter((d) => d?.tipo === 'tet').map((d) => String(d.id))));
}

function esEventoViaAerea(e, ids = null) {
  const accion = e?.datos?.accion;
  if (accion === 'intubacion' || accion === 'extubacion') return accionRapidaCompleta(e.datos);
  return esRetiroDeTubo(e, ids);
}

// Devuelve { registro, discrepancias, estado }. `estado` dice qué pasó con el
// replay y es lo que un llamador necesita para decidir sobre lo que quedó
// fuera de la lista: 'sin_eventos' (no hay hechos de vía aérea: nada que
// derivar), 'resuelto' (la lista devuelta ES el estado; lo que no está en
// ella se retiró o no tiene fuente) y 'no_resoluble' (no se pudo establecer
// la secuencia: la lista no se tocó y la discrepancia dice por qué).
export const REPLAY_ESTADO = { sinEventos: 'sin_eventos', resuelto: 'resuelto', noResoluble: 'no_resoluble' };

// La vía aérea de la Mesa según el replay. Devuelve la evolución (la misma
// referencia si nada cambia). Reglas: solo se toca un valor que sembró el
// puente de dispositivos, el chequeo de la visita o este mismo replay (libro
// `_viaAereaReplay`); lo que la autora respondió a mano no se pisa. Con tubo
// puesto, artificial; sin tubo tras un retiro o extubación, nativa; sin hechos
// que lo sostengan, vuelve a lo sembrado (o se retira lo que el replay puso).
function viaAereaSegunReplay(registro, { activo, huboRetiro }) {
  const evoBase = registro?.evolucion;
  if (!evoBase || typeof evoBase !== 'object') return evoBase;
  const sisV = evoBase.sistemas?.ventilacion || {};
  const camposV = sisV.campos || {};
  const actual = camposV.viaAerea;
  const sembrado = evoBase._puenteCampos?.['ventilacion:viaAerea'];
  const sembradoVisita = evoBase._puenteSoportes?.['ventilacion:viaAerea'];
  const replayPrev = evoBase._viaAereaReplay;
  const vacio = actual === undefined || actual === null || actual === '';
  const tocable = vacio || actual === sembrado || actual === sembradoVisita || actual === replayPrev;
  if (!tocable) return evoBase;
  let deseado = activo ? 'Vía aérea artificial' : (huboRetiro ? 'Vía aérea nativa' : null);
  let libroNuevo = deseado;
  if (!deseado) {
    // Nada que derivar: si el replay había escrito, se deshace hacia lo sembrado.
    if (!replayPrev || actual !== replayPrev) return evoBase;
    deseado = sembrado ?? sembradoVisita ?? undefined;
    libroNuevo = null;
  }
  if (actual === deseado) return evoBase;
  const camposNuevos = { ...camposV };
  if (deseado === undefined) delete camposNuevos.viaAerea; else camposNuevos.viaAerea = deseado;
  const { _viaAereaReplay: _omitido, ...resto } = evoBase;
  return {
    ...resto,
    sistemas: { ...(evoBase.sistemas || {}), ventilacion: { ...sisV, campos: camposNuevos } },
    ...(libroNuevo ? { _viaAereaReplay: libroNuevo } : {}),
  };
}

export function reconciliarViaAerea(registro, { entrada = null, hoy = new Date(), extra = [] } = {}) {
  const sinCambio = (discrepancias = [], estado = REPLAY_ESTADO.noResoluble) => ({ registro, discrepancias, estado });
  const entradasBase = Array.isArray(registro?.entradas) ? registro.entradas : [];
  const entradas = entrada && !entradasBase.some((e) => e?.id === entrada.id) ? [...entradasBase, entrada] : entradasBase;
  const lista = Array.isArray(registro?.dispositivos) ? registro.dispositivos : [];
  // El pool: los tubos de la ficha, los que alguna extubación o retiro sacó
  // (copia guardada en la entrada) y los que trae `extra` (la copia de una
  // entrada que se está BORRANDO: su rastro sirve una última vez). El objeto
  // ACTUAL de la ficha manda sobre cualquier copia.
  const pool = new Map();
  for (const t of (Array.isArray(extra) ? extra : [])) if (t && t.id && t.tipo === 'tet') pool.set(String(t.id), t);
  for (const e of entradas) {
    const t = e?.efecto?.dispositivoRetirado;
    if (t && t.id && t.tipo === 'tet') pool.set(String(t.id), t);
  }
  for (const d of lista) if (d?.tipo === 'tet' && d.id) pool.set(String(d.id), d);
  const idsTubo = new Set(pool.keys());
  const eventos = entradas.filter((e) => esEventoViaAerea(e, idsTubo));
  if (!eventos.length) {
    // Sin hechos de vía aérea no hay nada que derivar; pero si el replay había
    // movido la vía aérea de la Mesa (ver viaAereaSegunReplay) y ya no hay
    // hechos que lo sostengan (se borró la extubación), vuelve lo sembrado.
    const restaurada = viaAereaSegunReplay(registro, { activo: lista.some((d) => d?.tipo === 'tet'), huboRetiro: false });
    return restaurada === registro?.evolucion
      ? sinCambio([], REPLAY_ESTADO.sinEventos)
      : { registro: { ...registro, evolucion: restaurada }, discrepancias: [], estado: REPLAY_ESTADO.sinEventos };
  }
  const contexto = { ...registro, entradas };

  const recibidos = [...pool.values()].filter((t) => !t.origenEntradaId);
  if (recibidos.length > 1) return sinCambio([{ tipo: DISCREPANCIAS.variosTubos, dispositivoTipo: 'tet', entradaId: null }]);

  // Orden del HECHO, estricto entre vecinos: si no se puede afirmar la
  // secuencia, no se afirma nada.
  const ordenados = ordenarPorHecho(eventos, contexto).map((e) => ({ e, m: momentoDeEntrada(e, contexto) }));
  for (let i = 1; i < ordenados.length; i += 1) {
    if (!esPosteriorA(ordenados[i].m, ordenados[i - 1].m)) {
      return sinCambio([{ tipo: VIA_AEREA_DISCREPANCIAS.orden, dispositivoTipo: 'tet', entradaId: ordenados[i].e.id }]);
    }
  }

  const discrepancias = [];
  const retiros = new Map();   // id de extubación o retiro → tubo que sacó
  const creados = new Map();   // id de intubación → id del tubo que puso
  // `activo` es el tubo puesto; FANTASMA es "hubo un tubo puesto por esta
  // intubación pero ya no está en la ficha y nadie registró su salida": no se
  // recrea (lo que la autora hizo con su ficha manda), una extubación o un
  // retiro posterior lo consumen sin reclamar nada, y si llega al final del
  // replay se declara `tubo_no_en_ficha` en vez de callar.
  let activo = recibidos[0] || null;
  let fantasma = null; // id de la intubación cuyo tubo falta
  for (const { e, m } of ordenados) {
    if (e.datos.accion === 'intubacion') {
      const vinculado = [...pool.values()].find((t) => t.origenEntradaId && String(t.origenEntradaId) === String(e.id)) || null;
      if (activo || fantasma) {
        // Un vínculo propio con otro tubo ya puesto es dos tubos a la vez.
        if (vinculado && (!activo || vinculado.id !== activo.id)) {
          return sinCambio([{ tipo: VIA_AEREA_DISCREPANCIAS.secuencia, dispositivoTipo: 'tet', entradaId: e.id }]);
        }
        continue; // ya hay tubo: la misma intubación registrada de nuevo
      }
      if (vinculado) {
        const medidas = medidasTuboIntubacion(e.datos.valores);
        activo = JSON.stringify(vinculado._medidasIot) === JSON.stringify(medidas)
          ? vinculado : { ...vinculado, _medidasIot: medidas };
        continue;
      }
      if (e.efecto?.dispositivoId) { fantasma = e.id; continue; }
      const fechaCuidado = m?.hora && m?.fecha ? m.fecha : fechaISO(hoy);
      activo = { ...nuevoDispositivo('tet'), id: `tet-${e.id}`, fechaInstalacion: fechaCuidado, origenEntradaId: e.id, _medidasIot: medidasTuboIntubacion(e.datos.valores) };
      creados.set(e.id, activo.id);
      continue;
    }
    // extubación o retiro del tubo
    if (fantasma) { fantasma = null; continue; } // sacó un tubo que ya no estaba: nada que reclamar
    if (!activo) {
      discrepancias.push({ tipo: VIA_AEREA_DISCREPANCIAS.extubacionSinTubo, dispositivoTipo: 'tet', entradaId: e.id });
      continue;
    }
    // Un retiro que apunta por id a OTRO tubo que el puesto no cuadra.
    const apunta = e.datos.accion === 'retiro' ? e.datos.valores?.dispositivoId : null;
    if (apunta && String(apunta) !== String(activo.id)) {
      return sinCambio([{ tipo: VIA_AEREA_DISCREPANCIAS.secuencia, dispositivoTipo: 'tet', entradaId: e.id }]);
    }
    retiros.set(e.id, activo);
    activo = null;
  }
  if (fantasma) discrepancias.push({ tipo: VIA_AEREA_DISCREPANCIAS.tuboNoEnFicha, dispositivoTipo: 'tet', entradaId: fantasma });

  // La lista resultante: los no-tubo en su lugar, y el tubo vigente (si hay)
  // donde estaba o al final.
  const tubosAntes = lista.filter((d) => d?.tipo === 'tet');
  const vigenteMismo = tubosAntes.length === 1 && activo && tubosAntes[0] === activo;
  const listaNueva = vigenteMismo
    ? lista
    : activo && tubosAntes.some((t) => t.id === activo.id)
      ? lista.map((d) => (d?.tipo === 'tet' ? activo : d)).filter((d, i, arr) => d !== activo || arr.indexOf(d) === i)
      : [...lista.filter((d) => d?.tipo !== 'tet'), ...(activo ? [activo] : [])];
  const listaCambio = listaNueva !== lista && (
    listaNueva.length !== lista.length || listaNueva.some((d, i) => d !== lista[i])
  );

  // Las entradas: cada extubación recuerda qué sacó, cada intubación qué puso.
  let entradasCambio = false;
  const entradasNuevas = entradas.map((e) => {
    if (!esEventoViaAerea(e, idsTubo)) return e;
    const efectoPrev = e.efecto && typeof e.efecto === 'object' ? e.efecto : null;
    let efecto = null;
    if (e.datos.accion !== 'intubacion') {
      const t = retiros.get(e.id);
      efecto = t ? { ...(efectoPrev || {}), dispositivoRetirado: t } : (efectoPrev && efectoPrev.dispositivoRetirado ? (() => { const { dispositivoRetirado, ...resto } = efectoPrev; return Object.keys(resto).length ? resto : null; })() : efectoPrev);
    } else {
      const id = creados.get(e.id);
      efecto = id ? { ...(efectoPrev || {}), dispositivoId: id } : efectoPrev;
    }
    if (huellaDe(efecto ?? null) === huellaDe(efectoPrev ?? null)) return e;
    entradasCambio = true;
    const { efecto: _omitido, ...sinEfecto } = e;
    return efecto ? { ...sinEfecto, efecto } : sinEfecto;
  });

  // La VÍA AÉREA de la Mesa sigue al replay (caso G, 2026-09-07). El puente de
  // dispositivos siembra "Vía aérea artificial" con el tubo y, como los
  // puentes solo agregan, el campo quedaba así después de extubar: la rama
  // nativa seguía cerrada para los campos dictados ("queda con naricera" se
  // descartaba por rama) y el presente del documento afirmaba el tubo. El
  // estado de la vía aérea es un HECHO derivado de sus eventos, igual que la
  // lista de tubos, así que se escribe aquí y con libro propio
  // (`_viaAereaReplay`): SOLO se toca un valor que sembró el puente o este
  // mismo replay; lo que la autora respondió a mano no se pisa (fail-safe hacia
  // no tocar). Un tubo puesto vuelve a "artificial"; sin tubo tras un retiro o
  // extubación, "nativa".
  const evoBase = registro?.evolucion;
  const evolucionNueva = viaAereaSegunReplay(registro, { activo: Boolean(activo), huboRetiro: retiros.size > 0 });
  const evolucionCambio = evolucionNueva !== evoBase;

  if (!listaCambio && !entradasCambio && !evolucionCambio && entradas === entradasBase) return sinCambio(discrepancias, REPLAY_ESTADO.resuelto);
  return {
    registro: {
      ...registro,
      ...(listaCambio ? { dispositivos: listaNueva } : {}),
      ...(entradasCambio || entradas !== entradasBase ? { entradas: entradasNuevas } : {}),
      ...(evolucionCambio ? { evolucion: evolucionNueva } : {}),
    },
    discrepancias,
    estado: REPLAY_ESTADO.resuelto,
  };
}

// ── Borrar una entrada: el estado derivado no puede depender de una fuente que ya no existe ──
// Borrar es la tercera forma de cambiar un hecho (después de registrar y
// corregir) y pasa por el MISMO replay. Reglas:
//  - se recalcula solo lo atribuible a los hechos y vínculos que quedan;
//  - el tubo que la app creó DESDE la intubación borrada (`efecto.dispositivoId`
//    y vínculo coincidente) se retira: no hay fuente que lo sostenga. Un tubo
//    con vínculo pero sin esa anotación se conserva y se declara
//    `tubo_sin_fuente` (no se sabe si lo puso la app o la autora);
//  - la copia del tubo que la extubación o el retiro borrado había sacado
//    entra al replay una última vez (`extra`): si el replay la puede ubicar,
//    el tubo vuelve; si no puede (dos tubos a la vez, orden incierto), la copia
//    se CONSERVA en la ficha y la discrepancia se deriva al leer (`varios_tubos`,
//    orden), en vez de perderla en silencio o inventar cuál es el bueno;
//  - una extubación borrada sin copia (registrada antes de este cambio) no
//    puede reconstruir nada: la intubación queda con `tubo_no_en_ficha`.
// Puro: devuelve el mismo registro si la entrada no existe.
export function eliminarEntrada(registro, id) {
  const entradas = Array.isArray(registro?.entradas) ? registro.entradas : [];
  const borrada = entradas.find((e) => e?.id === id);
  if (!borrada) return registro;
  const restantes = entradas.filter((e) => e.id !== id);
  // Una nota que escribió campos dictados deja de sostenerlos al borrarse: se
  // vuelven a derivar desde las observaciones que quedan (misma regla que la
  // corrección; ver reconciliarCamposDictados).
  if (Array.isArray(borrada.datos?.escritos) && borrada.datos.escritos.length) {
    return reconciliarCamposDictados({ ...registro, entradas: restantes }).registro;
  }
  const lista = Array.isArray(registro?.dispositivos) ? registro.dispositivos : [];
  const idsTubo = new Set(lista.filter((d) => d?.tipo === 'tet').map((d) => String(d.id)));
  if (!esEventoViaAerea(borrada, idsTubo)) return { ...registro, entradas: restantes };

  let listaBase = lista;
  let evolucion = registro?.evolucion;
  const copia = borrada.efecto?.dispositivoRetirado || null;
  if (borrada.datos.accion === 'intubacion' && borrada.efecto?.dispositivoId) {
    const propio = lista.find((d) => d?.id === borrada.efecto.dispositivoId && String(d.origenEntradaId) === String(borrada.id));
    if (propio) {
      listaBase = lista.filter((d) => d !== propio);
      // La tarjeta de Invasivos que la app SEMBRÓ desde ese tubo se va con él,
      // pero solo si sigue tal como se sembró (el día se completa solo y no
      // cuenta): si la autora escribió en ella, es su documento y se queda.
      const tarjetas = evolucion?.sistemas?.invasivos?.dispositivos;
      if (Array.isArray(tarjetas)) {
        const fresca = sembrarInvasivosDesdeRecepcion({ sistemas: {} }, [propio]).sistemas?.invasivos?.dispositivos?.[0];
        const sinDia = ({ dia, ...resto }) => resto;
        const intacta = (t) => fresca && String(t?.origenRecepcionId) === String(propio.id) && huellaDe(sinDia(t)) === huellaDe(sinDia(fresca));
        if (tarjetas.some(intacta)) {
          evolucion = { ...evolucion, sistemas: { ...evolucion.sistemas, invasivos: { ...evolucion.sistemas.invasivos, dispositivos: tarjetas.filter((t) => !intacta(t)) } } };
        }
      }
    }
  }
  const base = {
    ...registro,
    entradas: restantes,
    ...(listaBase !== lista ? { dispositivos: listaBase } : {}),
    ...(evolucion !== registro?.evolucion ? { evolucion } : {}),
  };
  const { registro: derivado, estado } = reconciliarViaAerea(base, { extra: copia ? [copia] : [] });
  const enLista = (r) => (r.dispositivos || []).some((d) => d?.id === copia.id);
  if (copia && !enLista(derivado)) {
    // Qué hacer con la copia depende de lo que el replay pudo decir:
    //  - 'resuelto': la lista devuelta ES el estado. Si la copia no está, otro
    //    hecho restante la retiró (dos extubaciones, borrar la equivocada) o
    //    su fuente ya no existe. No se vuelve a poner: la copia queda en la
    //    entrada que la retiró, histórica, jamás vigente por su cuenta.
    //  - 'sin_eventos': no queda ningún hecho de vía aérea. El tubo RECIBIDO
    //    vuelve (lo sostiene la recepción, no un registro); el de una
    //    intubación que ya no existe no (se fue con su fuente).
    //  - 'no_resoluble': la secuencia no se pudo establecer y la lista no se
    //    tocó. La copia se CONSERVA en la ficha y la discrepancia se deriva
    //    al leer, en vez de perderla en silencio o inventar cuál es el bueno.
    if (estado === REPLAY_ESTADO.noResoluble || (estado === REPLAY_ESTADO.sinEventos && !copia.origenEntradaId)) {
      return { ...derivado, dispositivos: [...(derivado.dispositivos || []), copia] };
    }
  }
  return derivado;
}

// ── Discrepancias del estado del turno (U4) ─────────────────────────────────
// Derivadas al leer, jamás guardadas, catálogo cerrado y sin texto libre: la
// misma forma que `conflictosTrayectoria`. Existen porque la alternativa a
// avisar es adivinar, y adivinar sobre un dispositivo reescribe el equivocado.
// Avisan, no corrigen: la autora decide.
export const DISCREPANCIAS = {
  sinVinculo: 'sin_vinculo',
  fechaEditada: 'fecha_editada',
  variosTubos: 'varios_tubos',
};

// ¿Alguna versión de la entrada cambió CUÁNDO pasó el hecho? Se lee del
// historial que escribe `editarEntrada` (`previos.momento`, o `hora` cuando la
// entrada no tenía momento; con la nota omitida por PII el cambio queda en
// `campos`). Registro sin historial ⇒ false, o sea nada que reconciliar.
export function momentoCorregido(entrada) {
  const historial = Array.isArray(entrada?.historial) ? entrada.historial : [];
  return historial.some((v) => {
    const previos = v?.previos && typeof v.previos === 'object' ? v.previos : {};
    const campos = Array.isArray(v?.campos) ? v.campos : [];
    return 'momento' in previos || 'hora' in previos || campos.includes('momento') || campos.includes('hora');
  });
}

export function discrepanciasEstado(registro) {
  const entradas = Array.isArray(registro?.entradas) ? registro.entradas : [];
  const lista = registro?.dispositivos || [];
  const out = [];
  for (const e of entradas) {
    const accion = e?.datos?.accion;
    if (accion !== 'instalacion' && accion !== 'intubacion') continue;
    if (!accionRapidaCompleta(e.datos)) continue;
    const tipo = accion === 'intubacion' ? 'tet' : dispositivoPorLabel(e.datos?.valores?.dispositivo)?.id;
    if (!tipo) continue;
    const m = momentoDeEntrada(e, registro);
    const fecha = m?.hora && m?.fecha ? m.fecha : null;
    const vinculado = lista.find((d) => d.origenEntradaId && d.origenEntradaId === e.id);
    if (vinculado) {
      // La fecha del dispositivo dejó de ser la del hecho: alguien la cambió a
      // mano. Se conserva su edición (C9) y se dice que no coinciden.
      if (fecha && vinculado.fechaInstalacion && vinculado.fechaInstalacion !== fecha) {
        out.push({ tipo: DISCREPANCIAS.fechaEditada, dispositivoTipo: tipo, entradaId: e.id });
      }
      continue;
    }
    // Sin vínculo: solo importa si hay algún dispositivo de ese tipo que PODRÍA
    // ser el suyo. Si no queda ninguno (se retiró, se extubó) no hay nada que
    // reconciliar y callar es lo correcto.
    // Y solo si el MOMENTO del hecho se corrigió alguna vez: el aviso dice
    // "corregiste la hora y no sé a qué dispositivo aplicarla", y sin una
    // corrección no hay nada que aplicar. Antes acusaba a quien registraba una
    // intubación con el tubo ya declarado al recibir (la rama idempotente no
    // crea un segundo tubo ni vincula el que existe, así que la entrada queda
    // sin vínculo por diseño), y a todo registro anterior al vínculo: dos
    // avisos de una corrección que nadie hizo, en una app que no acusa.
    if (fecha && momentoCorregido(e) && lista.some((d) => d.tipo === tipo)) {
      out.push({ tipo: DISCREPANCIAS.sinVinculo, dispositivoTipo: tipo, entradaId: e.id });
    }
  }
  // Dos tubos a la vez es imposible en la realidad: la extubación no toca nada
  // y lo dice, en vez de elegir uno.
  if (lista.filter((d) => d.tipo === 'tet').length > 1) {
    out.push({ tipo: DISCREPANCIAS.variosTubos, dispositivoTipo: 'tet', entradaId: null });
  }
  // Lo que el replay de la vía aérea no pudo resolver (orden incierto, dos
  // tubos a la vez, extubación sin tubo puesto): derivado al leer, sin
  // escribir nada. `varios_tubos` ya está arriba; no se repite.
  // Con dos tubos en la ficha, `varios_tubos` ya lo dice: el "dos a la vez"
  // del replay es el mismo problema con otras palabras.
  const hayVarios = out.some((x) => x.tipo === DISCREPANCIAS.variosTubos);
  for (const d of reconciliarViaAerea(registro).discrepancias) {
    if (d.tipo === DISCREPANCIAS.variosTubos) continue;
    if (hayVarios && d.tipo === VIA_AEREA_DISCREPANCIAS.secuencia) continue;
    if (!out.some((x) => x.tipo === d.tipo && x.entradaId === d.entradaId)) out.push(d);
  }
  // Dos observaciones dictadas del mismo campo que no se pueden ordenar por su
  // hora: el campo queda como está y se dice qué revisar.
  for (const d of reconciliarCamposDictados(registro).discrepancias) {
    if (!out.some((x) => x.tipo === d.tipo && x.sistema === d.sistema && x.campo === d.campo)) out.push(d);
  }
  // Un tubo vinculado a un registro que ya no existe: no se toca, se dice.
  for (const d of lista) {
    if (d?.tipo === 'tet' && d.origenEntradaId && !entradas.some((e) => e?.id === d.origenEntradaId)) {
      out.push({ tipo: VIA_AEREA_DISCREPANCIAS.tuboSinFuente, dispositivoTipo: 'tet', entradaId: null });
    }
  }
  return out;
}

// ¿La nota libre de una entrada tiene PII? (best-effort, reusa detectPii).
export function notaConPii(nota) {
  return detectPii(nota).hit;
}

// ── Pendientes con hora ("tarjetero mental" del turno) ────────────────────────
// Fix #2 de la auditoría de MiPAE en turno: "vanco 20:00", "HGT 17:00" viven en la
// cabeza mientras se atiende — este es el tarjetero externo. Catálogo cerrado
// de tipo (sin texto libre ahí); `detalle` es texto corto y pasa por la MISMA
// guarda anti-PII que la nota de una entrada (detectPii/notaConPii, mismo
// trato, mismo punto de la UI donde se avisa).
//
// Persistencia: viaja dentro de `registro.tareas`, igual que cualquier otro
// campo del registro, en el JSON local del dispositivo (sin expiración, como
// el resto). NO tiene columna en `registros_turno` (Supabase) y esto no
// amerita una migración de esquema para un tarjetero personal — así que
// `registroTurnoStore.aRemoto/aLocal` simplemente no lo mapean: es
// device-local, igual que la ronda de 2 h. Anti-culpa: una tarea vencida no
// se oculta ni acusa — sigue en la lista, solo cambia el matiz (ver
// RegistroTurno.jsx).
// ── Indicaciones del día (F2 de la ficha: revisar indicaciones y exámenes) ───
// Catálogo CERRADO de indicaciones frecuentes: el plan de trabajo del turno se
// marca, no se transcribe (transcribir indicaciones médicas en texto libre es
// riesgo PII y de fidelidad — para el detalle está la ficha institucional).
// `registro.indicaciones: string[]` es device-local como `tareas` (plan de
// trabajo personal, no evidencia): sin columna remota, preservada en el merge,
// y NO se copia al retomar un turno (turno nuevo, indicaciones nuevas).
export const INDICACIONES_FRECUENTES = [
  'Régimen cero', 'Régimen liviano', 'Régimen común',
  'Reposo absoluto', 'Reposo relativo', 'Levantar a sillón',
  'O₂ según requerimiento', 'Fluidos EV en curso',
  'HGT c/6h', 'CSV c/4h', 'CSV c/6h',
  'Curación indicada', 'KNT indicada',
];

export const TIPOS_TAREA = ['Fármaco', 'Examen', 'Curación', 'Traslado', 'Control', 'Otro'];

export function nuevaTarea(tipo, hora, detalle = '') {
  return {
    id: nuevoId(),
    tipo: TIPOS_TAREA.includes(tipo) ? tipo : 'Otro',
    hora: hora || horaHHMM(),
    detalle: String(detalle || '').trim(),
    hecho: false,
  };
}

// Marcar un pendiente como hecho: el MISMO acto en la lista de la ficha y en el
// inicio de MiPAE en turno (2026-10-10). El check también REGISTRA: el pendiente
// aterriza en la cronología como lo que fue (tipo + detalle, texto que YA pasó
// la guarda de PII al crearse la tarea), sin la hora del pendiente en la prosa
// (norma 2026-08-02: la entrada lleva su propia hora de realización).
export function entradaPendienteHecho(tarea, { ahora = new Date() } = {}) {
  if (!tarea) return null;
  return { ...nuevaEntrada('nota', { ahora }), nota: `${tarea.tipo}${tarea.detalle ? `: ${tarea.detalle}` : ''} (pendiente realizado)` };
}

// `entrada` es opcional: quien necesita su id (la ficha, para que "aterrice"
// en la cronología) la arma antes con `entradaPendienteHecho`. Una tarea que no
// existe o que ya estaba hecha devuelve el MISMO registro: un doble toque no
// registra el acto dos veces.
export function marcarPendienteHecho(registro, id, { entrada = null, ahora = new Date() } = {}) {
  const tareas = Array.isArray(registro?.tareas) ? registro.tareas : [];
  const tarea = tareas.find((t) => t?.id === id);
  if (!tarea || tarea.hecho) return registro;
  const nota = entrada || entradaPendienteHecho(tarea, { ahora });
  return {
    ...registro,
    tareas: tareas.map((t) => (t.id === id ? { ...t, hecho: true } : t)),
    entradas: [...(registro.entradas || []), nota],
  };
}

// Pendientes (no hechas) de un registro, ordenadas por hora. Cada ítem trae
// `vencida` (ya pasó su hora respecto de `ahora`) para el tono anti-culpa de
// la UI: nunca se ocultan ni se acusan, solo cambia el matiz visual.
export function tareasPendientes(registro, ahora = new Date()) {
  const mAhora = momentoAhora(ahora);
  const tareas = Array.isArray(registro?.tareas) ? registro.tareas : [];
  return tareas
    .filter((t) => t && !t.hecho)
    .map((t) => {
      const m = momentoDeTarea(t, registro, ahora);
      // "Ya pasó su hora" se decide con el momento, no comparando "HH:MM" como
      // texto: en un turno de Noche eso marcaba vencida a las 22:05 una dosis
      // de las 04:00 que faltaba hora y media (la app acusando de atraso a
      // quien va adelantada), y dejaba sin marcar una de las 23:30 a la 01:00.
      return { ...t, momento: m, vencida: !!m && esPosteriorA(mAhora, m) };
    })
    .sort((a, b) => claveTarea(a).localeCompare(claveTarea(b)));
}

// El eje temporal de una TAREA es distinto del de un hecho: es una hora
// PROGRAMADA y la tarea no guarda fecha. La fecha se deriva del turno con el
// mismo pivote de la cronología (una hora anterior al pivote pertenece al día
// siguiente) y no se persiste: el tarjetero sigue guardando solo la hora.
export function momentoDeTarea(tarea, registro, ahora = new Date()) {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(tarea?.hora || '')) return null;
  // Sin turno declarado no hay pivote: la referencia es el reloj con el que se
  // está preguntando, no el del sistema, para que la respuesta no dependa de
  // cuándo se ejecuta.
  const ref = ahora instanceof Date ? ahora.getTime() : Date.now();
  const { fecha, inferida } = derivarFechaLegado(tarea.hora, registro, { ts: ref });
  return { fecha, hora: tarea.hora, precision: 'exacta', inferida: !!inferida };
}

// Exportada para ordenar pendientes de VARIAS camas con el mismo criterio
// (la lista del inicio de MiPAE en turno, lib/tableroTurno.js).
export const claveTarea = (t) => (t?.momento ? `${t.momento.fecha} ${t.momento.hora}` : `9999-99-99 ${t?.hora || ''}`);

// La tarea pendiente más próxima entre TODOS los pacientes (para la tira
// "Próximo:" del hub de MiPAE en turno), con la cama a la que pertenece. Prioriza
// las que aún no llegan a su hora; si todas ya vencieron, no hay "próximo"
// que ofrecer — el hub no debe levantar lo atrasado como si fuera urgente
// (ese tono anti-culpa vive en la ficha del paciente, no en el hub).
export function proximaTarea(registros = [], ahora = new Date()) {
  const candidatas = [];
  for (const r of registros || []) {
    for (const t of tareasPendientes(r, ahora)) {
      if (!t.vencida) candidatas.push({ ...t, cama: sanitizarCama(r?.cama), registroId: r?.id });
    }
  }
  if (!candidatas.length) return null;
  // Mismo criterio que la lista: entre camas, la más próxima es la del momento
  // más temprano, no la del texto de hora menor.
  candidatas.sort((a, b) => claveTarea(a).localeCompare(claveTarea(b)));
  return candidatas[0];
}

// Catálogo cerrado de frecuencias para la "próxima dosis" (entrada de fármaco).
export const FRECUENCIAS_FARMACO = ['c/6h', 'c/8h', 'c/12h', 'c/24h'];

// Hora de la próxima dosis: hora de la entrada + horas de la frecuencia,
// envolviendo la medianoche. `frecuencia` debe ser 'c/Nh' (catálogo cerrado);
// cualquier otra cosa (o una hora inválida) devuelve null — no inventa.
export function proximaDosis(hora, frecuencia) {
  const m = /^c\/(\d+)h$/.exec(frecuencia || '');
  if (!m || !/^([01]\d|2[0-3]):[0-5]\d$/.test(hora || '')) return null;
  const horasSuma = Number(m[1]);
  const [h, min] = hora.split(':').map(Number);
  const totalMin = (((h * 60 + min) + horasSuma * 60) % (24 * 60) + 24 * 60) % (24 * 60);
  const hh = String(Math.floor(totalMin / 60)).padStart(2, '0');
  const mm = String(totalMin % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Resumen de una línea para la timeline. Solo valores del catálogo cerrado +
// la nota (que ya fue filtrada en captura).
/**
 * Resumen de una entrada para pintarla o exportarla.
 *
 * @param {Object} entrada
 * @param {Object} [opts]
 * @param {boolean} [opts.conEtiqueta=true]  incluir el nombre del TIPO delante.
 *   Se apaga en superficies que ya pintan el tipo en su propio encabezado: la
 *   cronología de F3 escribe "13:12 · Nota" arriba y abajo salía "Nota. …", o
 *   sea la palabra dos veces pegada (turno real del fundador, 2026-08-29). El
 *   texto plano de `compilarRegistro` SÍ la necesita: ahí no hay chip que diga
 *   de qué tipo es cada línea, así que el default no cambia.
 *   La etiqueta vuelve igual si sin ella la línea quedaría muda.
 */
export function resumenEntrada(entrada, { conEtiqueta = true } = {}) {
  if (!entrada) return '';
  const { tipo, datos = {}, nota } = entrada;
  // El resumen se arma en tres piezas para poder soltar la etiqueta sin
  // reescribir el texto: etiqueta (el tipo) + detalle (lo que dice) + sufijo.
  let etiqueta = '';
  let detalle = '';
  let sufijo = '';

  if (tipo === 'valoracion') {
    const partes = SISTEMAS
      .map((sis) => {
        const sel = (datos.sistemas?.[sis.id] || []).filter((o) => sis.opciones.includes(o));
        return sel.length ? `${sis.label}: ${sel.join(', ')}` : null;
      })
      .filter(Boolean);
    etiqueta = 'Valoración';
    detalle = partes.join('; ');
  } else if (tipo === 'farmaco') {
    const med = [datos.nombre, datos.dosis, VIAS.includes(datos.via) ? datos.via : null]
      .filter((x) => x && String(x).trim())
      .join(' ');
    const nCorrectos = (datos.correctos || []).filter((c) => IDS_10_CORRECTOS.has(c)).length;
    etiqueta = 'Fármaco';
    detalle = med.trim();
    sufijo = nCorrectos >= LOS_10_CORRECTOS.length
      ? ' · ✓ 10 correctos'
      : nCorrectos > 0 ? ` · ${nCorrectos}/10 correctos` : '';
  } else if (tipo === 'procedimiento' && datos.accion) {
    // Acción rápida: la frase compilada ES el resumen (catálogo cerrado).
    detalle = compilarAccionRapida(datos)
      || ACCIONES_RAPIDAS.find((a) => a.id === datos.accion)?.label
      || 'Procedimiento';
  } else if (tipo === 'procedimiento') {
    etiqueta = 'Procedimiento';
    detalle = PROCEDIMIENTOS.includes(datos.procedimiento) ? datos.procedimiento : '';
    if (datos.insumos && datos.insumos.trim()) sufijo = ` (${datos.insumos.trim()})`;
  } else if (tipo === 'visita') {
    detalle = visitaCanonica(datos.visita) || 'Visita / indicaciones';
  } else if (datos.cambios?.length && notaCambioDictado(datos.cambios)) {
    // Nota dictada que además ACTUALIZÓ campos de la evolución ("dilo cuando
    // pase"): un dictado = una entrada, como el resto del módulo. El cambio es
    // catálogo cerrado y vive en `datos` a propósito, no en el texto: así no
    // viaja a la IA por `lineasNotasTurno` (que solo lee `.nota`) y la app no
    // termina glosándose a sí misma encima del campo que ya lo dice.
    detalle = notaCambioDictado(datos.cambios);
  } else {
    etiqueta = 'Nota';
  }

  const texto = (nota || '').trim();
  // La etiqueta se calla solo donde hay algo que la reemplace: sin detalle ni
  // nota volvería una línea en blanco, y una entrada muda no se lee.
  const conEti = conEtiqueta || (!detalle && !texto);
  const base = `${[conEti ? etiqueta : '', detalle].filter(Boolean).join(': ')}${sufijo}`.trim();
  if (!texto) return base;
  // La frase compilada ya cierra con punto: no se duplica al pegar la nota.
  return base ? `${base.replace(/\.$/, '')}. ${texto}` : texto;
}

// ── Acciones rápidas ("salí de la pieza") ─────────────────────────────────────
// Feedback del piloto (Maca, 2026-07-17): lo que más se hace es lo que menos
// se registra — curaciones, instalaciones y retiros, traslados, la ronda con
// el cuff y las fijaciones. Estas plantillas vienen LISTAS: se elige la
// acción, se marcan 2 o 3 chips de catálogo cerrado y la frase completa
// aterriza en la cronología con su hora. PII-safe por construcción: la prosa
// se compila SOLO de valores de catálogo + números; la plantilla no tiene
// campo de texto libre (la nota breve opcional de la entrada sigue pasando
// por detectPii como cualquier otra). La entrada resultante es tipo
// 'procedimiento' con datos { accion, valores } — misma cronología, mismo
// merge, mismo device-local.

export const ACCIONES_RAPIDAS = [
  { id: 'curacion',    label: 'Curación' },
  { id: 'instalacion', label: 'Instalación' },
  { id: 'retiro',      label: 'Retiro' },
  { id: 'traslado',    label: 'Traslado' },
  { id: 'intubacion',  label: 'Intubación' },
  { id: 'extubacion',  label: 'Extubación' },
  { id: 'ronda',       label: 'Ronda rápida' },
  // NO va acá la conexión de diálisis, y es a propósito. Se construyó como
  // acción rápida el 2026-09-21 y el fundador corrigió el mismo día: "esto no
  // es en turno, esto es aparte". El team de terapias extracorpóreas no tiene
  // turno abierto ni camas propias — los llaman, conectan a un paciente que es
  // de OTRA enfermera, reportan y se van — así que pedirles inventar un turno y
  // una cama para registrar treinta segundos de trabajo era justo la fricción
  // que esto venía a quitar. Hoy vive en su pantalla propia (`pages/Dialisis`).
  //
  // El DOMINIO sigue entero y cableado (`terapiaTrr.js`, `compilarAccionRapida`,
  // `efectoEstadoDeAccion`, `accionNarradaPorPelicula`): una entrada vieja con
  // `accion: 'conexion_trr'` sigue compilando su frase y narrando su evolución,
  // y volver a ofrecerlas acá es agregar las dos líneas de vuelta. Lo que se
  // retiró es el punto de ENTRADA, no la capacidad.
];

export const CURACION_OBJETIVOS = [
  'Herida operatoria', 'Lesión por presión', 'Sitio de inserción de invasivo',
  'Traqueostomía', 'Drenaje', 'Dermatitis (DAI)',
];
export const CURACION_ASPECTOS = [
  'Limpia y seca', 'Con exudado escaso', 'Con exudado moderado', 'Con exudado abundante',
  'Con tejido granulatorio', 'Con signos de infección local',
  'Piel perilesional indemne', 'Piel perilesional enrojecida',
];

// Zonas para la curación de LPP: derivadas del MISMO catálogo del mapa
// corporal (pielLpp.ZONAS_PRESION) — no duplicar zonas. Género correcto en el
// lado (escápula derecha, codo derecho).
const ZONA_FEMENINA = new Set(['oreja', 'escapula', 'rodilla']);
export const ZONAS_CURACION = ZONAS_PRESION.flatMap((z) => (
  z.lados
    ? z.lados.map((l) => `${z.label} ${ZONA_FEMENINA.has(z.id) ? (l === 'der' ? 'derecha' : 'izquierda') : LADO_LABEL[l]}`)
    : [z.label]
));

export const SITIOS_INSTALACION = [
  'Yugular derecho', 'Yugular izquierdo', 'Subclavio derecho', 'Subclavio izquierdo',
  'Femoral derecho', 'Femoral izquierdo', 'EESS derecha', 'EESS izquierda',
  'EEII derecha', 'EEII izquierda', 'Nasal', 'Oral',
];

export const RETIRO_MOTIVOS = [
  'Fin de la indicación', 'Recambio programado', 'Mal funcionamiento',
  'Signos de infección local', 'Salida accidental',
];
export const RETIRO_SITIO_ESTADOS = ['Sitio sin alteraciones', 'Sitio enrojecido', 'Sitio con secreción'];

export const TRASLADO_DESTINOS = [
  'TAC', 'Resonancia', 'Rayos', 'Ecografía', 'Endoscopía', 'Colonoscopía',
  'Pabellón', 'Hemodinamia', 'Diálisis',
];
export const TRASLADO_REGRESOS = ['Regresa en condiciones estables', 'Regresa con nuevos requerimientos', 'Aún no regresa'];

// ── Intubación: el evento que faltaba ────────────────────────────────────────
// Encargo del fundador (2026-08-23), de su turno real: "presentó caída de
// glasgow y requirió manejo avanzado de via aerea y se lo llevaron a pabellón".
// Es el evento más crítico que puede tener un turno de UCI y NO tenía forma de
// registrarse: quedaba como nota dictada, y la evolución solo lo contaba bien
// si la IA acertaba a repartirlo. Con catálogo cerrado lo escribe la app.
// Dictado del fundador (2026-08-23): dice "SRI", no "secuencia de intubación
// rápida". El label lleva el nombre completo para que se entienda al elegirlo;
// la PROSA usa la sigla, que es lo que él escribe.
export const INTUBACION_TECNICAS = [
  'Secuencia rápida de intubación (SRI)', 'Intubación convencional',
  'Intubación con videolaringoscopio', 'Intubación con fibrobroncoscopio',
];
// Sigla para la prosa; sin entrada, se usa el label en minúscula.
const INTUBACION_TECNICA_PROSA = { 'Secuencia rápida de intubación (SRI)': 'SRI' };
// El nombre anterior ('Secuencia de intubación rápida', vivo entre el 22 y el
// 23 de agosto) se LEE, no se migra: un registro guardado con él sigue
// compilando su frase. Misma regla que ALIAS_LABEL_DISPOSITIVO.
const ALIAS_TECNICA_INTUBACION = {
  'Secuencia de intubación rápida': 'Secuencia rápida de intubación (SRI)',
};
export function tecnicaIntubacionCanonica(t) {
  return ALIAS_TECNICA_INTUBACION[t] || t;
}
export function tecnicaIntubacionProsa(tecnica) {
  const t = tecnicaIntubacionCanonica(tecnica);
  return INTUBACION_TECNICA_PROSA[t] || minusculaProsa(String(t || ''));
}
// Drogas de inducción y bloqueo EN EL ORDEN EN QUE SE ADMINISTRAN: el array es
// el orden de la prosa (misma regla que GRUPOS_SEGMENTO del examen). El nombre
// jamás es texto libre; la dosis es número + unidad de lista corta, re-validada
// al leer (regla #642: jsonb adulterado jamás imprime).
export const INTUBACION_DROGAS = [
  'Fentanilo', 'Midazolam', 'Propofol', 'Etomidato', 'Ketamina',
  'Lidocaína', 'Rocuronio', 'Succinilcolina',
];
export const INTUBACION_UNIDADES = ['mcg', 'mg', 'g'];
export const INTUBACION_RESULTADOS = [
  'Al primer intento', 'Requirió más de un intento', 'Vía aérea difícil',
];
// POR QUÉ se intubó. Es el corazón clínico del episodio y hasta el 23-ago no
// había dónde registrarlo: el chip saltaba de "nada" a "intubado". Del dictado
// del fundador: "progresó con disnea y posterior falla respiratoria". Multi
// porque el deterioro se encadena, y EN ORDEN de progresión: el array es el
// orden de la prosa.
export const INTUBACION_MOTIVOS = [
  'Disnea', 'Agotamiento respiratorio', 'Falla respiratoria',
  'Compromiso de conciencia', 'Inestabilidad hemodinámica',
  'Paro cardiorrespiratorio', 'Protección de vía aérea',
  'Procedimiento programado',
];
// El VERBO depende del motivo, y esto no es cosmética: "progresó con paro
// cardiorrespiratorio" o "progresó con procedimiento programado" son frases
// clínicamente absurdas, y una evolución que las imprime delata a la máquina
// en la primera línea. Tres familias:
//  · 'progresivo': el paciente se fue deteriorando   → "Progresó con…"
//  · 'subito': ocurrió de golpe                      → "Presentó…"
//  · 'indicacion': NO es un deterioro, es una decisión del equipo. No lleva
//    frase de deterioro: se teje como la razón del acto ("Requirió intubación
//    orotraqueal por protección de vía aérea…").
const MOTIVO_TIPO = {
  'Disnea': 'progresivo',
  'Agotamiento respiratorio': 'progresivo',
  'Falla respiratoria': 'progresivo',
  'Compromiso de conciencia': 'subito',
  'Inestabilidad hemodinámica': 'subito',
  'Paro cardiorrespiratorio': 'subito',
  'Protección de vía aérea': 'indicacion',
  'Procedimiento programado': 'indicacion',
};
// Las indicaciones, para tejerlas en el acto en vez de en el deterioro.
export function indicacionIntubacionProsa(valores = {}) {
  const elegidos = Array.isArray(valores.motivos) ? valores.motivos : [];
  const ind = INTUBACION_MOTIVOS
    .filter((m) => elegidos.includes(m) && MOTIVO_TIPO[m] === 'indicacion')
    .map(minusculaProsa);
  return ind.length ? listaProsaAcciones(ind) : null;
}
// QUÉ SE INTENTÓ ANTES. "requirió apoyo con ambú y bolsa mascarrilla y oxígeno
// al 100%" — que se haya escalado antes de intubar es dato clínico y
// médico-legal: dice que no se saltó ningún paso.
export const INTUBACION_RESCATE = [
  'Oxígeno al 100%', 'Ventilación con bolsa-mascarilla', 'Cánula orofaríngea',
  'Aspiración de secreciones', 'CNAF', 'VNI',
];
// La prosa de cada apoyo, cuando el label no cabe en la oración. El chip dice
// "Ventilación con bolsa-mascarilla" (claro al elegirlo) pero la frase queda
// "requirió apoyo CON ventilación CON bolsa-mascarilla": dos preposiciones
// pegadas. En la prosa va la forma corta, que además es como lo dice el
// fundador ("apoyo con ambú y bolsa mascarilla"). Mismo patrón que SRI.
const RESCATE_PROSA = { 'Ventilación con bolsa-mascarilla': 'bolsa-mascarilla' };
// La saturación más baja del episodio. Número entero 0-100 re-validado al leer
// (regla #642): un valor fuera de rango no se imprime, jamás se "corrige".
const INTUB_SAT_RE = /^\d{1,3}$/;
export function saturacionIntubacionProsa(valores = {}) {
  const v = String(valores.satMinima ?? '').trim();
  if (!INTUB_SAT_RE.test(v)) return null;
  const n = Number(v);
  return n >= 0 && n <= 100 ? `${n}` : null;
}

// La frase del DETERIORO, calcada del dictado: "progresó con disnea y
// posterior falla respiratoria con desaturación hasta 56% d sao2". Devuelve
// null si no se declaró nada: sin motivo no se inventa un deterioro.
export function deterioroIntubacionProsa(valores = {}) {
  const elegidos = Array.isArray(valores.motivos) ? valores.motivos : [];
  // Las INDICACIONES no son deterioro: se cuentan en el acto, no aquí.
  const motivos = INTUBACION_MOTIVOS
    .filter((m) => elegidos.includes(m) && MOTIVO_TIPO[m] !== 'indicacion');
  const sat = saturacionIntubacionProsa(valores);
  if (!motivos.length && !sat) return null;
  if (!motivos.length) return `Presentó desaturación hasta ${sat}% de SaO2.`;
  const txt = motivos.map(minusculaProsa);
  // "Progresó" solo si de verdad hubo una progresión; si todo fue súbito
  // (paro, compromiso de conciencia), el verbo es "presentó".
  const hayProgresivo = motivos.some((m) => MOTIVO_TIPO[m] === 'progresivo');
  const verbo = hayProgresivo ? 'Progresó con' : 'Presentó';
  // El "y posterior X" solo tiene sentido encadenando una progresión.
  const lista = txt.length > 1 && hayProgresivo
    ? `${txt.slice(0, -1).join(', ')} y posterior ${txt[txt.length - 1]}`
    : listaProsaAcciones(txt);
  return `${verbo} ${lista}${sat ? `, con desaturación hasta ${sat}% de SaO2` : ''}.`;
}

// Lo que se intentó ANTES de intubar, en el orden del catálogo.
export function rescateIntubacionProsa(valores = {}) {
  const elegidos = Array.isArray(valores.rescate) ? valores.rescate : [];
  const usados = INTUBACION_RESCATE
    .filter((r) => elegidos.includes(r))
    .map((r) => RESCATE_PROSA[r] || minusculaProsa(r));
  return usados.length ? listaProsaAcciones(usados) : null;
}
const INTUB_DOSIS_RE = /^\d{1,4}([.,]\d{1,2})?$/;
const INTUB_TUBO_RE = /^[6-9]([.,][05])?$/;
const INTUB_CM_RE = /^\d{1,2}$/;

// Las dosis imprimibles, en el orden del catálogo. Una droga marcada sin dosis
// válida se nombra igual (el hecho de que se usó ES el dato); lo que nunca
// viaja es un número con formato desconocido.
export function dosisIntubacionProsa(valores = {}) {
  const porDroga = valores.dosis && typeof valores.dosis === 'object' ? valores.dosis : {};
  const elegidas = Array.isArray(valores.drogas) ? valores.drogas : [];
  return INTUBACION_DROGAS
    .filter((d) => elegidas.includes(d))
    .map((d) => {
      const v = String(porDroga[d]?.valor ?? '').trim();
      const u = porDroga[d]?.unidad;
      if (!INTUB_DOSIS_RE.test(v) || !INTUBACION_UNIDADES.includes(u)) return d.toLowerCase();
      return `${d.toLowerCase()} ${v.replace(',', '.')} ${u}`;
    });
}

// LA FRASE DEL ACTO, fuente única para la cronología y para la evolución (antes
// estaba escrita dos veces, con el riesgo de que una se corrigiera y la otra no).
//
// La costura se peinó leyendo TODAS las combinaciones (2026-08-23, dictado del
// fundador: "están bien, mientras tenga coherencia en la oración"):
//  · "y posterior intubación" en vez de "CON posterior": la frase traía tres
//    "con" seguidos (apoyo con X, con posterior Y, con drogas Z) y se leía como
//    un formulario, no como una enfermera.
//  · el RESULTADO ("al primer intento", "vía aérea difícil") va con el ACTO de
//    intubar, no colgando del tubo: describe cómo salió el procedimiento, no
//    cómo quedó el dispositivo.
//  · la INDICACIÓN se teje como la razón del acto ("por protección de vía
//    aérea"), que es donde va cuando no hubo deterioro.
export function actoIntubacionProsa(valores = {}, { incluirFarmacos = true } = {}) {
  const drogas = dosisIntubacionProsa(valores);
  const rescate = rescateIntubacionProsa(valores);
  const indicacion = indicacionIntubacionProsa(valores);
  const resultado = INTUBACION_RESULTADOS.includes(valores.resultado) ? valores.resultado : null;
  const tec = tecnicaIntubacionProsa(valores.tecnica);

  let acto = `intubación orotraqueal${indicacion ? ` por ${indicacion}` : ''} mediante ${tec}`;
  if (incluirFarmacos && drogas.length) acto += ` con ${listaProsaAcciones(drogas)}`;
  let frase = rescate
    ? `Requirió apoyo con ${rescate}, y posterior ${acto}`
    : `Requirió ${acto}`;
  if (resultado) frase += `, ${minusculaProsa(resultado)}`;
  return `${frase}.`;
}

// Cómo QUEDA la vía aérea, calcado del dictado del fundador (2026-08-23):
// "quedó con tot 8 a 24 cm de arcada dentaria, corroborado con rx a 5 cm d
// carina". Dos correcciones sobre la versión anterior, las dos suyas:
//  · la profundidad se mide desde la ARCADA DENTARIA, y decirlo importa (es la
//    referencia con la que el turno siguiente comprueba que no migró);
//  · la distancia a carina no se sabe mirando: sale de la RADIOGRAFÍA, así que
//    la prosa dice de dónde salió. Sin ese dato no se menciona la carina, que
//    es lo honesto: no se afirma una comprobación que no se hizo.
function medidasTuboIntubacion(valores = {}) {
  const n = String(valores.tuboNumero ?? '').trim();
  const com = String(valores.tuboComisura ?? '').trim();
  const car = String(valores.tuboCarina ?? '').trim();
  return {
    calibre: INTUB_TUBO_RE.test(n) ? n.replace(',', '.') : '',
    detalle: [INTUB_CM_RE.test(com) ? `a ${com} cm de arcada dentaria` : '',
      INTUB_CM_RE.test(car) ? `corroborado con Rx a ${car} cm de carina` : ''].filter(Boolean).join(', '),
  };
}

export function tuboIntubacionProsa(valores = {}) {
  const n = String(valores.tuboNumero ?? '').trim();
  let frase = INTUB_TUBO_RE.test(n) ? `TOT #${n.replace(',', '.')}` : 'TOT';
  const com = String(valores.tuboComisura ?? '').trim();
  if (INTUB_CM_RE.test(com)) frase += ` a ${com} cm de arcada dentaria`;
  const car = String(valores.tuboCarina ?? '').trim();
  if (INTUB_CM_RE.test(car)) frase += `, corroborado con Rx a ${car} cm de carina`;
  return frase;
}

// "a, b y c" — mismo patrón que listaProsa de trayectoria.js, que es privada de
// ese módulo; tres líneas duplicadas antes que un import que crearía un ciclo.
function listaProsaAcciones(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

export const EXTUBACION_TOLERANCIAS = ['Buena tolerancia', 'Tolerancia regular', 'Mala tolerancia'];
export const EXTUBACION_SOPORTES = [
  'Sin oxígeno suplementario', 'Naricera', 'Mascarilla Venturi', 'Mascarilla de recirculación',
  'CNAF', 'VNI',
];
// Cómo FUE el procedimiento, distinto de cómo lo toleró el paciente (caso G,
// turno real del 2026-09-07: "se realiza extubación sin incidentes" no traía
// tolerancia y la extubación caía como nota, así que el documento seguía
// afirmando el tubo). "Sin incidentes" no se traduce a "buena tolerancia": son
// datos distintos y la autora dijo el primero.
export const EXTUBACION_CURSOS = ['Sin incidentes', 'Con incidentes'];
// Por qué se extuba (opcional). Hasta el 2026-09-08 la plantilla imprimía "Se
// extuba según indicación" SIEMPRE, también cuando la enfermera escribió "se
// extuba sin incidentes" a secas: una afirmación de cumplimiento que ninguna
// fuente hizo (caso I de la evaluación, mismo criterio con que el caso C
// prohíbe "según indicación" inventado). Ahora se imprime SOLO si el dictado
// lo dijo o la autora lo marcó en el chip; "sin incidentes" se conserva tal
// cual, sin agregar tolerancia ni otra valoración.
export const EXTUBACION_INDICACIONES = ['Según indicación médica'];

// La ronda: el checklist de lo invisible — lo que se hace en cada pasada y
// nunca queda escrito. Todas marcables de un toque; compilan UNA línea.
export const RONDA_RAPIDA_ACCIONES = [
  { id: 'permeabiliza', label: 'Permeabilización de accesos', prosa: 'se permeabilizan los accesos vasculares' },
  { id: 'cuff',         label: 'Medición de cuff',            prosa: 'se mide presión de cuff', unidad: 'cmH₂O' },
  { id: 'rotacionTubo', label: 'Rotación del tubo',           prosa: 'se rota la comisura del tubo' },
  { id: 'fijacion',     label: 'Cambio de fijación',          prosa: 'se cambia la fijación' },
  { id: 'posicion',     label: 'Cambio de posición',          prosa: 'se realiza cambio de posición' },
  { id: 'aspiracion',   label: 'Aspiración de secreciones',   prosa: 'se aspiran secreciones' },
  { id: 'infusiones',   label: 'Revisión de infusiones',      prosa: 'se verifican las infusiones en curso' },
  { id: 'aseo',         label: 'Aseo y confort',              prosa: 'se realiza aseo y confort' },
];

// Minúscula inicial conservando siglas (CNAF, TAC, EESS…): si las dos
// primeras letras son mayúsculas, el término es sigla y no se toca.
// Exportada desde la película de la cronología (2026-08-22): la línea del
// retiro habla el MISMO caso de prosa que la frase de la acción rápida.
export function minusculaProsa(s) {
  const txt = String(s || '');
  return /^[A-ZÁÉÍÓÚÑ]{2}/.test(txt) ? txt : txt.charAt(0).toLowerCase() + txt.slice(1);
}

// El nombre de un dispositivo a media frase ("Curación de sitio de inserción
// de catéter venoso central", "Se retira catéter urinario permanente por…",
// "Se recibió con línea arterial, que se retira…"). minusculaProsa ya deja
// intactas las siglas que abren con dos mayúsculas (VVP, PICC, SNG, TOT, DVE),
// pero el catálogo trae además nombres PROPIOS que van tal cual: los de
// mayúscula interna (PiCCO, Swan-Ganz; minusculaProsa los dejaba "piCCO" y
// "swan-Ganz") y las marcas (Nexiva, Flexiseal: la marca ES la palabra, ver
// ALIAS_DISPOSITIVO). Solo cambia el caso: un nombre viejo guardado ("Sonda
// Foley (CUP)") se escribe con sus mismas palabras.
const MARCAS_DISPOSITIVO = new Set(['Nexiva', 'Flexiseal']);
export function dispositivoEnProsa(label) {
  const txt = String(label || '');
  const mayusculaInterna = /[A-ZÁÉÍÓÚÑ]/.test(txt.split(' ')[0].slice(1));
  return MARCAS_DISPOSITIVO.has(txt) || mayusculaInterna ? txt : minusculaProsa(txt);
}

const HORA_RE = /^\d{1,2}:\d{2}$/;

export function nuevaEntradaAccion(accionId, { hora, momento, ahora = new Date() } = {}) {
  return {
    id: nuevoId(),
    tipo: 'procedimiento',
    ...momentoDeEntradaNueva({ hora, momento, ahora }),
    ts: ahora instanceof Date ? ahora.getTime() : Date.now(), // mismo sello del sistema que nuevaEntrada
    v: 1,
    datos: { accion: accionId, valores: {} },
    nota: '',
  };
}

// ¿Tiene lo mínimo para registrarse? (la UI deshabilita "Registrar" sin esto).
export function accionRapidaCompleta(datos) {
  const { accion, valores = {} } = datos || {};
  switch (accion) {
    case 'curacion':    return CURACION_OBJETIVOS.includes(valores.objetivo);
    case 'instalacion': return !!dispositivoPorLabel(valores.dispositivo);
    case 'retiro':      return !!dispositivoPorLabel(valores.dispositivo) && RETIRO_MOTIVOS.includes(valores.motivo);
    case 'traslado':    return TRASLADO_DESTINOS.includes(valores.destino);
    // La TÉCNICA es lo mínimo; drogas, calibre y profundidad enriquecen pero no
    // bloquean ("captura ahora, estructura después").
    case 'intubacion':  return INTUBACION_TECNICAS.includes(tecnicaIntubacionCanonica(valores.tecnica));
    // Una extubación es un HECHO con el verbo; lo mínimo para darla por
    // registrada es un calificador de cómo fue (tolerancia o curso), que es lo
    // que distingue "se extuba sin incidentes" de una mención suelta.
    case 'extubacion':  return EXTUBACION_TOLERANCIAS.includes(valores.tolerancia) || EXTUBACION_CURSOS.includes(valores.curso);
    case 'ronda':       return (valores.acciones || []).some((a) => RONDA_RAPIDA_ACCIONES.some((r) => r.id === a));
    // Lo mínimo es QUÉ se conectó (y la modalidad si es continua: "TRRC" sin
    // modalidad no le sirve al nefrólogo que la indicó). Los flujos enriquecen
    // y no bloquean, mismo criterio que la técnica de la intubación.
    case 'conexion_trr':    return conexionTrrCompleta(valores);
    case 'desconexion_trr': return desconexionTrrCompleta(valores);
    default:            return false;
  }
}

// Compila la frase de bitácora de una acción rápida. Determinista y de
// catálogo cerrado: cualquier valor fuera de catálogo se ignora (anti-ruido /
// anti-PII, mismo criterio que resumenEntrada). '' si falta lo mínimo.
export function compilarAccionRapida(datos) {
  const { accion, valores = {} } = datos || {};
  if (!accionRapidaCompleta(datos)) return '';

  if (accion === 'curacion') {
    const objetivo = valores.objetivo;
    const esInvasivo = objetivo === 'Sitio de inserción de invasivo';
    const dispositivo = dispositivoPorLabel(valores.dispositivo)?.label || null;
    const zona = ZONAS_CURACION.includes(valores.zona) ? valores.zona : null;
    const aspectos = (valores.aspectos || []).filter((a) => CURACION_ASPECTOS.includes(a));
    const cobertura = COBERTURAS_LESION.includes(valores.cobertura) ? valores.cobertura : null;

    let sujeto = minusculaProsa(objetivo);
    if (esInvasivo && dispositivo) sujeto = `sitio de inserción de ${dispositivoEnProsa(dispositivo)}`;
    if (objetivo === 'Lesión por presión' && zona) sujeto += ` en ${minusculaProsa(zona)}`;

    let frase = `Curación de ${sujeto}`;
    if (aspectos.length) frase += `: ${aspectos.map(minusculaProsa).join(', ')}`;
    if (cobertura) {
      frase += cobertura === 'Sin cobertura'
        ? '; se deja sin cobertura'
        : `; se cubre con ${minusculaProsa(cobertura)}`;
    }
    return `${frase}.`;
  }

  if (accion === 'instalacion') {
    const sitio = SITIOS_INSTALACION.includes(valores.sitio) ? valores.sitio : null;
    // Sin afirmar comprobaciones no constatadas: se registra el hecho, el
    // estado se describe en la valoración o en la nota si corresponde.
    return `Se instala ${dispositivoEnProsa(valores.dispositivo)}${sitio ? ` en ${minusculaProsa(sitio)}` : ''}.`;
  }

  if (accion === 'retiro') {
    const sitioEstado = RETIRO_SITIO_ESTADOS.includes(valores.sitioEstado) ? valores.sitioEstado : null;
    return `Se retira ${dispositivoEnProsa(valores.dispositivo)} por ${minusculaProsa(valores.motivo)}${sitioEstado ? `; ${minusculaProsa(sitioEstado)}` : ''}.`;
  }

  if (accion === 'traslado') {
    const salida = HORA_RE.test(valores.horaSalida || '') ? valores.horaSalida : null;
    const regreso = TRASLADO_REGRESOS.includes(valores.regreso) ? valores.regreso : null;
    const horaRegreso = regreso && regreso !== 'Aún no regresa' && HORA_RE.test(valores.horaRegreso || '')
      ? valores.horaRegreso : null;
    let frase = `Se traslada a ${valores.destino}${salida ? ` a las ${salida}` : ''}`;
    if (regreso) frase += `; ${minusculaProsa(regreso)}${horaRegreso ? ` a las ${horaRegreso}` : ''}`;
    return `${frase}.`;
  }

  if (accion === 'intubacion') {
    // El arco completo del dictado: deterioro, lo que se intentó, el acto, y
    // cómo quedó. Las tres piezas salen de funciones compartidas con la
    // evolución: una sola redacción, imposible que se desalineen.
    return [
      deterioroIntubacionProsa(valores),
      actoIntubacionProsa(valores),
      `Quedó con ${tuboIntubacionProsa(valores)}.`,
    ].filter(Boolean).join(' ');
  }

  if (accion === 'extubacion') {
    const soporte = EXTUBACION_SOPORTES.includes(valores.soporte) ? valores.soporte : null;
    const como = [
      EXTUBACION_TOLERANCIAS.includes(valores.tolerancia) ? minusculaProsa(valores.tolerancia) : null,
      EXTUBACION_CURSOS.includes(valores.curso) ? minusculaProsa(valores.curso) : null,
    ].filter(Boolean);
    const indicada = EXTUBACION_INDICACIONES.includes(valores.indicacion);
    let frase = `Se extuba${indicada ? ' según indicación' : ''}${como.length ? `${indicada ? ':' : ','} ${como.join(', ')}` : ''}`;
    if (soporte) {
      frase += soporte === 'Sin oxígeno suplementario'
        ? ', queda sin oxígeno suplementario'
        : `, queda con ${minusculaProsa(soporte)}`;
    }
    return `${frase}.`;
  }

  // Las dos frases salen de `terapiaTrr.js`, que es fuente única con la
  // evolución: el hecho se redacta UNA vez.
  if (accion === 'conexion_trr')    return conexionTrrProsa(valores);
  if (accion === 'desconexion_trr') return desconexionTrrProsa(valores);

  if (accion === 'ronda') {
    const cuff = Number(valores.cuffValor);
    const conCuff = Number.isFinite(cuff) && cuff > 0 && cuff < 200;
    const prosas = RONDA_RAPIDA_ACCIONES
      .filter((r) => (valores.acciones || []).includes(r.id))
      .map((r) => (r.id === 'cuff' && conCuff ? `${r.prosa} (${cuff} ${r.unidad})` : r.prosa));
    return `Ronda: ${prosas.join(', ')}.`;
  }

  return '';
}

// Efecto colateral de una acción sobre el registro: instalación crea el
// dispositivo con la fecha de hoy (los días-catéter corren solos desde ya);
// retiro lo saca de la lista. Pura: devuelve el MISMO registro si no hay nada
// que aplicar. Un registro, dos efectos — la frase en la cronología y la
// ficha de dispositivos al día.
export function aplicarEfectoAccion(registro, entrada, hoy = new Date()) {
  const { accion, valores = {} } = entrada?.datos || {};
  // La fecha de instalación es la del CUIDADO, no la del registro: una vía
  // instalada a las 23:40 y anotada a las 00:20 empezaba a contar sus días un
  // día tarde. Con `momento` se usa su fecha; sin él (entrada vieja o hora
  // desconocida) se cae al día del registro, como antes.
  const mHecho = momentoDeEntrada(entrada, registro);
  const fechaCuidado = mHecho?.hora && mHecho?.fecha ? mHecho.fecha : fechaISO(hoy);
  if (accion === 'instalacion') {
    const cat = dispositivoPorLabel(valores.dispositivo);
    if (!cat) return registro;
    const disp = { ...nuevoDispositivo(cat.id), fechaInstalacion: fechaCuidado, origenEntradaId: entrada?.id || null };
    return { ...registro, dispositivos: [...(registro.dispositivos || []), disp] };
  }
  if (accion === 'intubacion' || accion === 'extubacion') {
    // La vía aérea se resuelve por REPLAY de sus hechos en orden cronológico
    // (`reconciliarViaAerea`), no aplicando este hecho sobre la lista tal
    // como está: una extubación de las 10:00 ingresada a las 16:00 borraba el
    // tubo de la intubación de las 14:00, y una intubación histórica volvía a
    // poner un tubo que un hecho posterior ya había sacado. El registro del
    // hecho (la entrada) queda igual aunque su efecto no se pueda aplicar.
    return reconciliarViaAerea(registro, { entrada, hoy }).registro;
  }
  if (accion === 'retiro') {
    const lista = registro.dispositivos || [];
    // El retiro de un TUBO es un hecho de la vía aérea y se resuelve con los
    // demás (ver reconciliarViaAerea): así queda representado en el replay y
    // una corrección posterior no lo resucita.
    if (esRetiroDeTubo(entrada, new Set(lista.filter((d) => d?.tipo === 'tet').map((d) => String(d.id))))) {
      return reconciliarViaAerea(registro, { entrada, hoy }).registro;
    }
    // Por id (elegido con el chip "de esta cama"): saca ese exacto.
    if (valores.dispositivoId) {
      if (!lista.some((d) => d.id === valores.dispositivoId)) return registro;
      return { ...registro, dispositivos: lista.filter((d) => d.id !== valores.dispositivoId) };
    }
    // Fallback por tipo/label cuando se eligió de la lista genérica (BUG FIX
    // 2026-07-18: antes la nota decía "se retira X" pero el dispositivo seguía
    // en la ficha, con su día-catéter y su alerta de recambio). Solo si el tipo
    // matchea EXACTAMENTE UN dispositivo de la cama (dos del mismo tipo = usa
    // el chip específico; cero = no hay nada que quitar, la nota queda igual).
    const cat = dispositivoPorLabel(valores.dispositivo);
    if (cat) {
      const delTipo = lista.filter((d) => d.tipo === cat.id);
      if (delTipo.length === 1) {
        return { ...registro, dispositivos: lista.filter((d) => d.id !== delTipo[0].id) };
      }
    }
  }
  if (accion === 'curacion') {
    // La curación aterriza EN su lesión del mapa (pieza 4 pre-Play): con
    // EXACTAMENTE una lesión en la zona curada se le ancla la hora de esta
    // entrada (y la cobertura, si la acción la trae). Cero o varias lesiones
    // en la zona = silencio (ante la ambigüedad no se toca nada; el lápiz de
    // la lesión sigue ahí). La hora del examen NO se re-sella: la lesión ya
    // existía con la suya. SOLO con objetivo LPP (auditoría 2026-08-01: una
    // curación de herida operatoria jamás debe escribir en una LPP del mapa).
    if (valores.objetivo !== 'Lesión por presión' || !valores.zona) return registro;
    const { base, lado } = zonaCuracionPartes(valores.zona);
    const { examenFisico, lesion } = anclarCuracion(registro.examenFisico, {
      zona: base, lado, cobertura: valores.cobertura || null, hora: entrada?.hora,
    });
    if (!lesion) return registro;
    return { ...registro, examenFisico };
  }
  return registro;
}

// ── Los CAMBIOS DE ESTADO que una acción rápida declara ──────────────────────
// Encargo del fundador (2026-08-23): "que la visita de enfermería sea el punto
// de referencia de la evolución por sistema y los hallazgos que se coloquen
// como nota rápida grafiquen lo que se hizo y como está actualmente".
//
// Hay dos formas de graficar, y son distintas a propósito (el fundador aprobó
// las dos en el mismo ejemplo):
//  · un DISPOSITIVO que se va o llega se cuenta compacto, en una frase que
//    relaciona la foto con el hecho ("Se recibió con catéter urinario
//    permanente, que se retira durante el turno por…"). Eso lo arma
//    `retirosNarrables` + peliculaDesdeAcciones.
//  · un CAMBIO DE ESTADO del sistema se cuenta en TRES TIEMPOS, porque el
//    estado en que queda es un dato clínico por derecho propio:
//        Se recibió con vía aérea nativa, ventilando en aire ambiente.
//        Durante el turno se extuba según indicación, con buena tolerancia.
//        Queda con CNAF.
//    Eso es lo que arma esta tabla.
//
// `hizo` y `queda` se construyen desde los VALORES de catálogo de la acción,
// nunca parseando la frase de `compilarAccionRapida`: parsear prosa para
// volver a componerla es el patrón que el repo ya pagó caro. La frase de la
// cronología y estas dos siguen saliendo del mismo catálogo, así que no pueden
// contradecirse.
//
// `queda: null` significa "el hecho ES el estado final" (un retiro no necesita
// decir cómo quedó: quedó sin el dispositivo).
// Cómo se dice EN QUÉ QUEDÓ. Dictado del fundador (2026-08-23), leído de su
// propia evolución: él escribe "Actualmente en RASS -2" para un ESTADO que
// sigue, y reserva la forma "queda con" para algo que se INSTALÓ o se dejó
// puesto. Dos palabras distintas para dos cosas distintas, y las dos son suyas.
//
// `frase` viene con su preposición incluida ("con TOT #8.0", "sin oxígeno
// suplementario") porque no todas las formas admiten la misma: "Queda con sin
// oxígeno" no existe.
export function fraseCierreAccion(cierre) {
  if (!cierre || !cierre.frase) return null;
  const frase = String(cierre.frase).trim();
  if (!frase) return null;
  // "Quedó" (pasado) para lo que se instaló y "Actualmente" (presente) para el
  // estado que sigue: los dos salen del dictado del 23-ago, donde el fundador
  // escribe "quedó con TOT…" y a renglón seguido "está conectado a VMI…".
  return cierre.tipo === 'estado' ? `Actualmente ${frase}.` : `Quedó ${frase}.`;
}

export function efectoEstadoDeAccion(entrada) {
  const { accion, valores = {} } = entrada?.datos || {};
  if (!accionRapidaCompleta(entrada?.datos)) return null;

  if (accion === 'intubacion') {
    return {
      sistema: 'ventilacion',
      // Frases COMPLETAS y en pasado, sin "durante el turno" delante: el
      // fundador no lo dice, y ese preámbulo repetido en cada sistema es justo
      // el delator de plantilla que el panel ciego marcó. El verbo ya ubica en
      // el tiempo. Son VARIAS porque el deterioro es oración propia, y salen de
      // las MISMAS funciones que la cronología: el episodio se redacta una vez.
      hizo: [deterioroIntubacionProsa(valores), actoIntubacionProsa(valores, { incluirFarmacos: false })].filter(Boolean),
      complementos: dosisIntubacionProsa(valores).length ? [{
        sistema: 'neuro',
        hizo: `Para la intubación${indicacionIntubacionProsa(valores) ? ` por ${indicacionIntubacionProsa(valores)}` : ''} se utilizó ${listaProsaAcciones(dosisIntubacionProsa(valores))}.`,
        queda: null,
      }] : [],
      // Un tubo es algo que se INSTALA: "queda con". (Ver fraseCierreAccion.)
      queda: fraseCierreAccion({ tipo: 'dispositivo', frase: `con ${tuboIntubacionProsa(valores)}` }),
      // Intubar cambia el neurológico, y la basal de la mañana ("Sin sedación",
      // "Vigil y conectado") no puede seguir afirmándose como presente sobre un
      // paciente que a esa hora ya está intubado: ese es exactamente el
      // documento contradictorio del turno real. `ancla` manda esos campos al
      // pasado ("Se recibió sin sedación") SIN inventar el presente: las drogas
      // de inducción son un bolo, no una sedación continua, y afirmar
      // "bajo sedoanalgesia" sería exactamente el plan-promovido-a-hecho que la
      // casa prohíbe. El presente lo escribe la autora al responder el campo, y
      // hasta que lo haga el documento calla en vez de mentir.
      ancla: ['neuro'],
    };
  }

  if (accion === 'extubacion') {
    const soporte = EXTUBACION_SOPORTES.includes(valores.soporte) ? valores.soporte : null;
    const como = [
      EXTUBACION_TOLERANCIAS.includes(valores.tolerancia) ? `con ${minusculaProsa(valores.tolerancia)}` : null,
      EXTUBACION_CURSOS.includes(valores.curso) ? minusculaProsa(valores.curso) : null,
    ].filter(Boolean);
    return {
      sistema: 'ventilacion',
      hizo: `Se extuba${EXTUBACION_INDICACIONES.includes(valores.indicacion) ? ' según indicación' : ''}${como.length ? `, ${como.join(', ')}` : ''}.`,
      // El soporte con que queda tras extubar también se DEJA PUESTO.
      queda: soporte
        ? fraseCierreAccion({
          tipo: 'dispositivo',
          frase: soporte === 'Sin oxígeno suplementario'
            ? 'sin oxígeno suplementario'
            : `con ${minusculaProsa(soporte)}`,
        })
        : null,
    };
  }

  if (accion === 'conexion_trr') {
    // `queda: null` porque el hecho ES el estado final: conectar ya dice en qué
    // quedó. Repetir los flujos en un "Actualmente…" sería el mismo dato dos
    // veces en el mismo párrafo.
    //
    // BRECHA CONOCIDA, para la rebanada que venga: esto NARRA la conexión pero
    // no siembra los campos de `renal` (trr, modalidad, flujos), así que si la
    // Mesa quedó respondida en la mañana con "Sin terapia de reemplazo renal",
    // el documento dice las dos cosas. Sembrar campos es trabajo del puente
    // (`turnoEvolucionBridge`), con su libro y su regla de solo-agregar; el
    // comentario de ese archivo ya anota `diálisis→renal` como pendiente.
    return { sistema: 'renal', hizo: conexionTrrProsa(valores), queda: null };
  }

  if (accion === 'desconexion_trr') {
    const estado = estadoTrasDesconexion(valores);
    return {
      sistema: 'renal',
      hizo: desconexionTrrProsa(valores),
      // Solo se afirma "sin terapia" cuando el motivo la TERMINA: un cambio de
      // set o un traslado son pausas, y declararlas fin sería el plan promovido
      // a hecho que la casa prohíbe (ver TRR_DESCONEXION_MOTIVOS).
      queda: estado ? fraseCierreAccion({ tipo: 'estado', frase: estado }) : null,
    };
  }

  if (accion === 'traslado') {
    const regreso = TRASLADO_REGRESOS.includes(valores.regreso) ? valores.regreso : null;
    return {
      sistema: 'procedimientos',
      hizo: `Se traslada a ${valores.destino}.`,
      // "Aún no regresa" es un HECHO del cierre, no una omisión: se dice.
      queda: regreso ? `${regreso}.` : null,
    };
  }

  return null;
}

// ── La película de la cronología: qué retiros cuentan su historia solos ───────
// Feedback del turno real del fundador (2026-08-22, Bupa): retiró la Foley a
// las 09:48 con su acción rápida y la evolución de la tarde seguía diciendo
// "Diuresis por catéter urinario permanente" — la foto de la mañana afirmada
// como presente, contradicha por un hecho que ÉL registró en catálogo cerrado.
// "Necesito que en la evolución por sistema se cuente la historia." La brecha
// estaba anotada en turnoEvolucionBridge.js (el documento decía que lo TUVO
// pero no que se RETIRÓ); el arreglo sigue el dictado posterior del fundador
// (2026-08-18, "que se relacionen, tomen contexto"): una línea de película
// "Se recibió con X, que se retira durante el turno por…" que la deriva
// `peliculaDesdeAcciones` (turnoEvolucionBridge.js) y se recompone al
// imprimir, jamás se guarda (doctrina de trayectoria).
//
// Esta función decide QUÉ retiros narra la película, y es fuente única para
// los dos lados: el que narra (el bridge) y el que deja de mandarlos a la IA
// (`lineasNotasTurno`, abajo) — narrado determinista + repartido por IA sería
// el mismo hecho dos veces en el documento. Criterios:
//  - acción rápida de retiro COMPLETA (catálogo cerrado, PII-safe);
//  - SIN nota libre de la autora: con nota, la entrada viaja entera a la IA
//    como siempre (partir la nota de su hecho degradaría el relato);
//  - sin instalación previa del MISMO tipo en el turno: instalado y retirado
//    en la misma jornada, "Se recibió con X" sería falso — ese caso queda en
//    el camino de la IA, que narra sin afirmar la recepción.
// Kill-switch ON por defecto (regla de flags del proyecto).
export const peliculaCronologiaEnabled = () =>
  process.env.REACT_APP_PELICULA_CRONOLOGIA_ENABLED !== 'false';

// ¿La película determinista ya cuenta esta acción como hecho del turno?
// (intubación y extubación completas: `efectoEstadoDeAccion` las narra con su
// frase y su estado final). El retiro tiene su propio criterio (retirosNarrables).
export function accionNarradaPorPelicula(e) {
  if (!peliculaCronologiaEnabled()) return false;
  const accion = e?.datos?.accion;
  if (!['intubacion', 'extubacion', 'conexion_trr', 'desconexion_trr'].includes(accion)) return false;
  return accionRapidaCompleta(e.datos);
}
// Hallazgos de catálogo que la película YA cuenta como hecho, por IDENTIDAD de
// la acción registrada: el reparto por IA no los marca además (una extubación
// completa en la cronología y "Extubación reciente" en ventilación era el
// mismo hecho dos veces, casos B e I de la corrida real 2026-09-08).
export function hallazgosNarradosPorPelicula(registro) {
  const entradas = Array.isArray(registro?.entradas) ? registro.entradas : [];
  const out = [];
  if (entradas.some((e) => e?.datos?.accion === 'extubacion' && accionNarradaPorPelicula(e))) {
    out.push({ sistema: 'ventilacion', hallazgo: 'Extubación reciente' });
  }
  return out;
}
// ¿La nota trae un número que la prosa de catálogo no imprime?
export function notaAportaNumero(nota, prosa) {
  const nums = String(nota || '').match(/\d+([.,]\d+)?/g) || [];
  const enProsa = new Set((String(prosa || '').match(/\d+([.,]\d+)?/g) || []));
  return nums.some((n) => !enProsa.has(n));
}

export function retirosNarrables(registro) {
  if (!peliculaCronologiaEnabled()) return [];
  const entradas = Array.isArray(registro?.entradas) ? registro.entradas : [];
  return entradas.filter((e) => {
    if (e?.tipo !== 'procedimiento' || e?.datos?.accion !== 'retiro' || !e?.id) return false;
    if (!accionRapidaCompleta(e.datos)) return false;
    // Con nota libre de la autora la entrada viaja entera a la IA. La nota que
    // es el TEXTO de origen de la acción (`fuente: 'texto'`) no cuenta como
    // nota aparte: es el mismo hecho, y la película lo narra igual.
    if ((e.nota || '').trim() && e.fuente !== 'texto') return false;
    const cat = dispositivoPorLabel(e.datos.valores?.dispositivo);
    if (!cat) return false;
    // "¿lo instalamos nosotros antes de retirarlo?" se decide por el momento
    // del HECHO, no comparando "HH:MM" como texto: en Noche y 24 horas una
    // instalación a las 23:10 y un retiro a las 00:30 se leían al revés.
    const mRetiro = momentoDeEntrada(e, registro);
    const instaladoEnTurno = entradas.some((i) => i?.tipo === 'procedimiento'
      && i?.datos?.accion === 'instalacion'
      && dispositivoPorLabel(i.datos?.valores?.dispositivo)?.id === cat.id
      && !esPosteriorA(momentoDeEntrada(i, registro), mRetiro));
    return !instaladoEnTurno;
  });
}

// ── Voz → acción rápida ───────────────────────────────────────────────────────
// Parser determinista (cero IA, espejo de parseExamenDictado): mapea lo que la
// enfermera dicta al salir de la pieza ("curación de CVC, limpia y seca";
// "instalo CVP en EESS derecha"; "lo trasladan a TAC") a la acción rápida
// estructurada + sus valores de catálogo cerrado, para pre-poblar el editor y
// que confirme con "Así queda" en un toque, en vez de re-elegir 4-5 chips.
// Devuelve { accion, valores } o null si no reconoce una acción con su dato
// primario (entonces cae como nota libre, como hasta ahora). PII-safe por
// construcción: SOLO aterrizan labels de catálogo; el ASR crudo nunca entra a
// un campo (misma garantía dura del examen).
const _normAccion = (t) => String(t || '')
  .toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

function _matchCue(norm, cues) {
  for (const [label, re] of cues) if (re.test(norm)) return label;
  return null;
}
function _matchMulti(norm, cues) {
  const out = [];
  for (const [label, re] of cues) if (re.test(norm) && !out.includes(label)) out.push(label);
  return out;
}

// ── La polaridad de una cue: negada, incierta o afirmada ─────────────────────
// Evaluación de extremo a extremo (2026-09-06, caso C): "sitio sin signos de
// infección" se registraba como "Con signos de infección local". La cue
// calzaba por substring y nadie miraba el "sin" de adelante: la app afirmaba
// en la cronología, y mandaba a la IA, lo contrario de lo que la autora
// escribió. Es la misma familia que `verificarPolaridad` del pulido.
//
// La regla es la doctrina de evolucionDictado.js (#723), no una nueva:
//  · negador PEGADO en la misma cláusula (sin coordinación en medio) ⇒ negada;
//  · negador con "y/e/o" en medio ⇒ AMBIGUA: no se afirma ni se niega;
//  · "no se descarta", "posible", "probable", "dudoso", "impresiona" ⇒ INCIERTA:
//    una duda no se convierte en afirmación ni en negación.
// La cláusula la cierran la coma, el punto y coma y el punto, y "pero" cierra la
// negación igual que una coma. Lo negado o incierto NO aterriza en el chip; el
// texto original se conserva en la nota de la entrada (ver `fuente: 'texto'`),
// así que la información no se pierde: deja de estar mal.
const _NEGADOR_ACCION = /\b(sin|no|ni|niega|nunca|tampoco|jamas|ningun\w*|nada de)\b/;
const _INCIERTO_ACCION = /no se descarta|posible|probable|dudos|impresiona|sospech|podria|pareciera/;
export function polaridadCue(norm, re) {
  const m = re.exec(norm);
  if (!m) return null;
  const antesTodo = norm.slice(0, m.index);
  const clausula = antesTodo.split(/[,;.]/).pop();
  if (_INCIERTO_ACCION.test(`${clausula} ${m[0]}`)) return 'incierta';
  const ultimo = [...clausula.matchAll(/\s(y|e|o|pero)\s/g)].pop();
  const ambito = ultimo ? clausula.slice(ultimo.index + ultimo[0].length) : clausula;
  if (_NEGADOR_ACCION.test(ambito)) return 'negada';
  // "sin exudado y CON signos de infección": el "con" explícito cierra la duda
  // que el "y" abría; sin él, la ambigüedad se queda ambigua.
  if (ultimo && ultimo[1] !== 'pero' && _NEGADOR_ACCION.test(clausula)) return /\bcon\b/.test(ambito) ? 'afirmada' : 'ambigua';
  return 'afirmada';
}
// Solo las cues AFIRMADAS aterrizan. Lo demás se devuelve aparte para quien
// quiera avisarlo; el parse conserva su forma de siempre.
function _matchMultiPolar(norm, cues) {
  const out = [];
  const noAfirmadas = [];
  for (const [label, re] of cues) {
    const pol = polaridadCue(norm, re);
    if (!pol) continue;
    if (pol === 'afirmada') { if (!out.includes(label)) out.push(label); } else noAfirmadas.push({ label, polaridad: pol });
  }
  return { out, noAfirmadas };
}
function _matchCuePolar(norm, cues) {
  for (const [label, re] of cues) {
    const pol = polaridadCue(norm, re);
    if (pol === 'afirmada') return label;
  }
  return null;
}

// El dispositivo se nombra igual en instalación, retiro y curación de invasivo:
// un solo diccionario (el label sale del catálogo, no se re-escribe). Orden: lo
// más específico primero (PICC antes que un CVC genérico).
// Los siete últimos entraron el 20-ago con el catálogo completo: sin cue, el
// dictado no podía nombrarlos aunque el chip ya existiera. Todas con `\b` (la
// doctrina de #723: el "-no" de "turno" armaba una negación por substring), y
// las específicas ANTES de la genérica que las contendría ('drenaje
// ventricular' antes que 'drenaje'; PiCCO antes que PICC, aunque \b ya los
// separa: "picc" no cierra frontera dentro de "picco").
const _DISPOSITIVO_CUES = [
  ['dve', /\bdve\b|drenaje ventricular/],
  ['picco', /\bpicco\b/],
  ['picc', /\bpicc\b/],
  ['swan', /\bswan\b|swan ?-? ?ganz|cateter de arteria pulmonar/],
  ['chd', /\bchd\b|cateter de hemodialisis|cateter de dialisis|cateter transitorio de hemodialisis/],
  ['cvc', /\bcvc\b|cateter venoso central|cateter central/],
  ['la', /linea arterial|cateter arterial|\blinea art/],
  ['nexiva', /\bnexiva\b/],
  ['cvp', /\bcvp\b|\bvvp\b|via venosa periferica|cateter venoso periferico|branula|abbocath/],
  ['foley', /\bfoley\b|sonda foley|\bcup\b|sonda vesical|sonda urinaria|cateter urinario/],
  ['sog', /\bsog\b|sonda orogastrica|sonda oro/],
  ['sng', /\bsng\b|\bsny\b|sonda naso|sonda nasogastrica|sonda nasoyeyunal|sonda gastrica/],
  ['tet', /\btet\b|\btot\b|tubo endotraqueal|tubo orotraqueal|intubad/],
  ['tqt', /\btqt\b|traqueostom/],
  ['marcapasos', /marcapaso/],
  ['drenaje', /\bdrenaj|\bdren\b|penrose|hemovac|pleurotom|tubo pleural/],
];
function _dispositivoDe(norm) {
  for (const [id, re] of _DISPOSITIVO_CUES) {
    if (re.test(norm)) return CATALOGO_DISPOSITIVOS.find((c) => c.id === id)?.label || null;
  }
  return null;
}

const _OBJETIVO_CUES = [
  ['Lesión por presión', /\blpp\b|\bupp\b|lesion por presion|ulcera por presion|\bescara/],
  ['Herida operatoria', /herida operatoria|herida qx|herida quirurgica|\bhop\b|herida op|sutura|incision/],
  ['Dermatitis (DAI)', /dermatitis|\bdai\b/],
  ['Drenaje', /drenaj/],
  ['Traqueostomía', /traqueostom|traqueo/],
  ['Sitio de inserción de invasivo', /sitio de insercion|sitio de puncion|sitio del? invasivo/],
];
const _ASPECTO_CUES = [
  ['Con exudado abundante', /exudado abundante|mucho exudado/],
  ['Con exudado moderado', /exudado moderad/],
  ['Con exudado escaso', /exudado escaso|poco exudado|escaso exudado/],
  ['Con signos de infección local', /signos de infeccion|infectad|purulent|mal olor/],
  ['Con tejido granulatorio', /granulator|granulacion|tejido de granulacion/],
  ['Piel perilesional enrojecida', /perilesional enrojecid|perilesional roj|perilesional irritad/],
  ['Piel perilesional indemne', /perilesional indemne|perilesional san/],
  // En masculino también: el sitio "está limpio y seco" (caso C de la
  // evaluación: la cue solo calzaba en femenino y la curación quedaba sin
  // aspecto).
  ['Limpia y seca', /limpi[ao] y sec[ao]|\blimpi[ao]\b|\bsec[ao]\b/],
];
const _COBERTURA_CUES = [
  ['Apósito de plata', /\bplata\b/],
  ['Espuma hidrocelular', /espuma|hidrocelular/],
  ['Hidrocoloide', /hidrocoloide/],
  ['Alginato', /alginato/],
  ['Gasa vaselinada', /vaselinada/],
  ['Sin cobertura', /sin cobertura|sin aposito|se deja descubiert|al aire/],
];
const _SITIO_CUES = [
  ['Yugular derecho', /yugular derech/],
  ['Yugular izquierdo', /yugular izquierd/],
  ['Subclavio derecho', /subclavi[oa] derech/],
  ['Subclavio izquierdo', /subclavi[oa] izquierd/],
  ['Femoral derecho', /femoral derech/],
  ['Femoral izquierdo', /femoral izquierd/],
  ['EESS derecha', /eess derech|brazo derech|antebrazo derech|extremidad superior derech|mano derech/],
  ['EESS izquierda', /eess izquierd|brazo izquierd|antebrazo izquierd|extremidad superior izquierd|mano izquierd/],
  ['EEII derecha', /eeii derech|pierna derech|extremidad inferior derech|pie derech/],
  ['EEII izquierda', /eeii izquierd|pierna izquierd|extremidad inferior izquierd|pie izquierd/],
  ['Nasal', /\bnasal\b|fosa nasal|\bnariz\b/],
  ['Oral', /\boral\b|por boca|\bboca\b/],
];
const _MOTIVO_CUES = [
  ['Salida accidental', /accidental|se salio|se sale|se arranco|traccion|se descoloc/],
  ['Signos de infección local', /infeccion|infectad|purulent|signos de infeccion/],
  ['Mal funcionamiento', /mal funciona|no funciona|obstru|tapad|disfuncion|no refluye|no permea/],
  ['Recambio programado', /recambio|cambio programad|programad|por rutina/],
  ['Fin de la indicación', /fin de (la )?indicacion|ya no (lo |la )?(necesita|requiere)|indicacion cumplida/],
];
const _SITIO_ESTADO_CUES = [
  ['Sitio con secreción', /secrecion|supura/],
  ['Sitio enrojecido', /enrojecid|eritema|\brojo\b|inflamad/],
  // Las dos primeras formas llevan el "sin" DENTRO de la cue: es el valor, no
  // un negador delante (polaridadCue mira solo lo que precede al calce).
  ['Sitio sin alteraciones', /sin alteracion|sitio indemne|sin signos|limpio/],
];
const _DESTINO_CUES = [
  ['TAC', /\btac\b|scanner|escaner|tomografia/],
  ['Resonancia', /resonancia|\brnm\b/],
  ['Rayos', /\brayos\b|radiografia/],
  ['Ecografía', /ecografia|\beco\b/],
  ['Endoscopía', /endoscopia/],
  ['Colonoscopía', /colonoscopia/],
  ['Pabellón', /pabellon|quirofano|\bcirugia\b/],
  ['Hemodinamia', /hemodinamia|cateterismo|angiografia/],
  ['Diálisis', /dialisis|hemodialisis/],
];
const _REGRESO_CUES = [
  ['Aún no regresa', /aun no regresa|no ha regresad|todavia (esta )?en|sigue en/],
  ['Regresa con nuevos requerimientos', /nuevos requerimient|regresa con|volvio con/],
  ['Regresa en condiciones estables', /regresa estable|volvio estable|condiciones estables|regresa bien/],
];
// "reintub" NO es una cue de mala tolerancia (U4): en "se extuba con buena
// tolerancia y a las 17 se reintuba", la reintubación es un hecho POSTERIOR y
// la app terminaba escribiendo "Mala tolerancia", o sea inventando una
// valoración contraria a la que la autora acababa de decir. Una tolerancia se
// documenta cuando se dice, no se deduce de lo que pasó después.
const _TOLERANCIA_CUES = [
  ['Mala tolerancia', /mala tolerancia|no tolera/],
  ['Tolerancia regular', /tolerancia regular|regular tolerancia|\bregular\b/],
  ['Buena tolerancia', /buena tolerancia|tolera bien|sin dificultad/],
];
// Curso del procedimiento (catálogo EXTUBACION_CURSOS, 1:1). "Con" antes que
// "sin" a propósito: la cue negativa lleva su negación adentro.
const _CURSO_CUES = [
  ['Con incidentes', /con incidentes|con complicacion/],
  ['Sin incidentes', /sin incidentes|sin complicacion|sin novedad/],
];
// Indicación (catálogo EXTUBACION_INDICACIONES, 1:1): solo si el dictado lo dice.
const _INDICACION_CUES = [
  ['Según indicación médica', /segun indicacion|por indicacion|indicad[oa] por (el |la )?(medic|doctor|residente|intensivista)/],
];
// ── Cues de INTUBACIÓN (2026-08-23) ─────────────────────────────────────────
// El chip existía desde el 23-ago pero su parser de voz nunca se escribió:
// medido, reconocía 0 de 3 frases de turno. Todas las cues mapean 1:1 a un
// valor del catálogo (contrato del parser, fijado por test): el parser SOLO
// emite labels que existen en INTUBACION_TECNICAS, _MOTIVOS y _RESCATE.
const _TECNICA_INTUB_CUES = [
  ['Intubación con videolaringoscopio', /videolaringoscop|\bvideo\b/],
  ['Intubación con fibrobroncoscopio', /fibrobroncoscop|fibroscop|\bfibra\b/],
  ['Secuencia rápida de intubación (SRI)', /\bsri\b|secuencia rapida|intubacion rapida|\bsir\b/],
  ['Intubación convencional', /convencional/],
];
// El motivo del deterioro. "Progresó con disnea y posterior falla respiratoria"
// del dictado real activa DOS, y el orden de la prosa lo pone el catálogo.
const _MOTIVO_INTUB_CUES = [
  ['Paro cardiorrespiratorio', /\bparo\b|\bpcr\b(?!\s*\d)|paro cardio/],
  ['Falla respiratoria', /falla respiratoria|insuficiencia respiratoria|fallo respiratorio/],
  ['Agotamiento respiratorio', /agotamiento|fatiga (muscular|respiratoria)|se agot/],
  ['Disnea', /\bdisnea|dificultad respiratoria|polipnea|taquipnea/],
  ['Compromiso de conciencia', /compromiso de conciencia|glasgow (bajo|menor)|sopor|coma\b/],
  ['Inestabilidad hemodinámica', /inestabilidad hemodinamica|shock\b|hipotension severa/],
  ['Protección de vía aérea', /proteccion de (la )?via aerea|protejer|proteger la via/],
  ['Procedimiento programado', /programad|electiv|para (el )?(pabellon|procedimiento)/],
];
// Lo que se intentó ANTES de intubar. "ambú" y "bolsa mascarilla" son la misma
// maniobra dicha de dos formas (el fundador las nombra juntas).
const _RESCATE_INTUB_CUES = [
  ['Ventilación con bolsa-mascarilla', /\bambu\b|bolsa mascarilla|bolsa-mascarilla|ambusear|ventilacion manual/],
  ['Oxígeno al 100%', /oxigeno al 100|o2 al 100|fio2 (de )?100|\b100 ?%/],
  ['Cánula orofaríngea', /canula orofaringea|\bmayo\b|guedel/],
  ['Aspiración de secreciones', /aspir\w* (de )?secrecion|se aspira/],
  ['CNAF', /\bcnaf\b|canula nasal de alto flujo|alto flujo/],
  ['VNI', /\bvni\b|ventilacion no invasiva|bipap|\bcpap\b/],
];
const _RESULTADO_INTUB_CUES = [
  ['Vía aérea difícil', /via aerea dificil|dificultosa|intubacion dificil/],
  ['Requirió más de un intento', /segundo intento|dos intentos|mas de un intento|varios intentos/],
  ['Al primer intento', /primer intento|primera vez|sin dificultad/],
];

const _SOPORTE_CUES = [
  ['CNAF', /\bcnaf\b|canula nasal de alto flujo|alto flujo/],
  ['VNI', /\bvni\b|ventilacion no invasiva|bipap|\bcpap\b/],
  ['Mascarilla de recirculación', /recirculacion|no rebreather/],
  ['Mascarilla Venturi', /venturi/],
  ['Naricera', /naricera|canula nasal(?! de alto)/],
  ['Sin oxígeno suplementario', /sin oxigeno|sin o2|aire ambiental|ambiental/],
];
// Zona de la LESIÓN en el dictado de curación (pieza 4 pre-Play): la base
// topográfica; el lado se detecta aparte y se compone con el género de
// ZONAS_CURACION. Nariz/boca acá son zonas de piel (no confundir con los
// sitios Nasal/Oral de instalación: otra acción, otro catálogo).
const _ZONA_LESION_CUES = [
  ['Sacro', /\bsacr[oa]\b/],
  ['Talón', /\btalon(es)?\b/],
  ['Isquion', /isquion|isquiatic/],
  ['Trocánter', /trocanter/],
  ['Occipucio', /occipucio|occipital/],
  ['Nariz', /\bnariz\b/],
  ['Boca y labios', /\bboca\b|labio|comisura/],
  ['Oreja', /\boreja\b|pabellon auricular/],
  ['Escápula', /escapula/],
  ['Codo', /\bcodo\b/],
  ['Rodilla', /rodilla/],
  ['Maléolo', /maleolo/],
];

// 'Talón' + 'der' → 'Talón derecho' (el label EXACTO de ZONAS_CURACION, con
// su género); una zona con lados dictada SIN lado no compone label (el editor
// pre-poblado deja elegirlo). Exportada para el efecto y los tests.
export function zonaCuracionLabel(base, lado) {
  const def = ZONAS_PRESION.find((z) => z.label === base);
  if (!def) return null;
  if (!def.lados) return base;
  if (!lado) return null;
  const fem = ZONA_FEMENINA.has(def.id);
  return `${base} ${lado === 'der' ? (fem ? 'derecha' : 'derecho') : (fem ? 'izquierda' : 'izquierdo')}`;
}

// 'Talón derecho' → { base: 'Talón', lado: 'der' } (para anclar en el mapa).
export function zonaCuracionPartes(label) {
  const t = String(label || '').trim();
  const m = t.match(/^(.*?)\s+(derech[oa]|izquierd[oa])$/i);
  if (!m) return { base: t, lado: null };
  return { base: m[1], lado: /^derech/i.test(m[2]) ? 'der' : 'izq' };
}

const _RONDA_CUES = [
  ['permeabiliza', /permeabili[cz]/],
  ['cuff', /\bcuff\b|presion de cuff/],
  ['rotacionTubo', /rot\w* (el |del )?tubo|comisura/],
  ['fijacion', /fijacion/],
  ['posicion', /cambio de posicion|reposicion/],
  ['aspiracion', /aspir/],
  ['infusiones', /infusion|revis\w* (las )?infusiones|\bbomba/],
  ['aseo', /\baseo\b|confort/],
];

// Qué acción es. Orden por distintividad del verbo (extubar/ronda no se
// confunden; curación va al final porque es la más genérica). Un dictado, una
// acción: la primera que matchea manda.
function _accionDe(norm) {
  if (/\bextub/.test(norm)) return 'extubacion';
  // Después de extubación a propósito: son verbos hermanos y el orden explícito
  // documenta cuál gana si un dictado nombrara los dos.
  if (/\bintub|\bse intuba|\biot\b|\btot\b/.test(norm)) return 'intubacion';
  if (/\bronda\b/.test(norm)) return 'ronda';
  if (/\btraslad|\blo (llev|baj)|\bva a (pabellon|tac|scanner|resonancia|dialisis|hemodinamia|endoscopia|colonoscopia|ecografia|rayos)/.test(norm)) return 'traslado';
  if (/\binstal/.test(norm)) return 'instalacion';
  if (/\bretir|\bsaqu[e]|\bse retir/.test(norm)) return 'retiro';
  if (/\bcura(cion|ndo|r|do)?\b|\bcure\b|\bcuraci/.test(norm)) return 'curacion';
  return null;
}

// Los números de la intubación, CADA UNO CON SU ANCLA. Un dictado real trae
// tres seguidos ("tot 8 a 24 cm de arcada dentaria... a 5 cm de carina") y
// asignarlos por posición sería adivinar: un calibre puesto como profundidad es
// un dato clínico falso en la ficha. Sin la palabra que lo ancla, el número NO
// se asigna y el editor se abre con lo que sí se entendió (patrón `seedValores`).
function _numerosIntubacion(norm) {
  const out = {};
  // Calibre: pegado a "tot"/"tubo"/"número". 6.0 a 9.5, con coma o punto.
  const cal = norm.match(/\b(?:tot|tet|tubo|numero|n)\s*#?\s*([6-9](?:[.,][05])?)\b/);
  if (cal) out.tuboNumero = cal[1].replace(',', '.');
  // Profundidad: el número que va con comisura / arcada dentaria / labio.
  const com = norm.match(/\b(\d{1,2})\s*(?:cm|centimetros)?\s*(?:a nivel )?(?:de |en )?(?:la )?(?:comisura|arcada|arcada dentaria|labio|\bad\b)/)
    || norm.match(/(?:comisura|arcada dentaria|arcada|labio)\D{0,12}?(\d{1,2})\s*(?:cm|centimetros)/);
  if (com) out.tuboComisura = com[1];
  // Distancia a carina: solo si la palabra "carina" está.
  const car = norm.match(/\b(\d{1,2})\s*(?:cm|centimetros)?\s*(?:de |sobre |por sobre )?(?:la )?carina/)
    || norm.match(/carina\D{0,12}?(\d{1,2})\s*(?:cm|centimetros)/);
  if (car) out.tuboCarina = car[1];
  // Saturación más baja: exige la palabra que la nombra.
  const sat = norm.match(/(?:desatur\w*|satur\w*|sao2|spo2)\D{0,16}?(\d{2,3})\s*%?/)
    || norm.match(/(\d{2,3})\s*%\s*(?:de )?(?:sao2|spo2|saturacion)/);
  if (sat) {
    const n = Number(sat[1]);
    if (n >= 0 && n <= 100) out.satMinima = String(n);
  }
  return out;
}

export function parseAccionDictada(texto) {
  const norm = _normAccion(texto);
  if (!norm) return null;
  const accion = _accionDe(norm);
  if (!accion) return null;
  const valores = {};

  if (accion === 'curacion') {
    const disp = _dispositivoDe(norm);
    let objetivo = _matchCue(norm, _OBJETIVO_CUES);
    if (disp && (!objetivo || objetivo === 'Sitio de inserción de invasivo')) {
      objetivo = 'Sitio de inserción de invasivo';
      valores.dispositivo = disp;
    }
    // Zona de la lesión dictada ("curación de sacro, limpia"): compone el
    // label exacto del catálogo y, sin otro objetivo, la zona de presión
    // implica LPP. SOLO para LPP (auditoría 2026-08-01: con "herida operatoria
    // de rodilla" la zona anclaba la LPP de rodilla del mapa, una lesión que
    // nadie curó). El LADO debe venir en la MISMA cláusula que la zona
    // ("decúbito lateral derecho" al final del dictado no lateraliza la
    // escápula). Y si la zona es bilateral y el lado no se dictó, NO se
    // infiere objetivo: el dictado cae a nota libre y "talón" no se pierde.
    const puedeZona = !objetivo || objetivo === 'Lesión por presión';
    const zonaBase = puedeZona ? _matchCue(norm, _ZONA_LESION_CUES) : null;
    if (zonaBase) {
      const cue = _ZONA_LESION_CUES.find(([label]) => label === zonaBase)[1];
      const clausula = norm.split(/[,;.]/).find((c) => cue.test(c)) || '';
      const lado = /derech/.test(clausula) ? 'der' : /izquierd/.test(clausula) ? 'izq' : null;
      const zonaLabel = zonaCuracionLabel(zonaBase, lado);
      if (zonaLabel) {
        valores.zona = zonaLabel;
        if (!objetivo) objetivo = 'Lesión por presión';
      } else if (!objetivo) {
        return null; // zona bilateral sin lado y sin otro objetivo → nota libre
      }
    }
    if (!objetivo) return null; // "curación" sin qué se curó → nota libre
    valores.objetivo = objetivo;
    // Con polaridad: "sin signos de infección" no es "con signos de infección".
    const { out: aspectos } = _matchMultiPolar(norm, _ASPECTO_CUES);
    if (aspectos.length) valores.aspectos = aspectos;
    const cob = _matchCue(norm, _COBERTURA_CUES);
    if (cob) valores.cobertura = cob;
    return { accion, valores };
  }

  if (accion === 'instalacion') {
    const disp = _dispositivoDe(norm);
    if (!disp) return null; // "instalo" sin qué → nota libre
    valores.dispositivo = disp;
    const sitio = _matchCue(norm, _SITIO_CUES);
    if (sitio) valores.sitio = sitio;
    return { accion, valores };
  }

  if (accion === 'retiro') {
    const disp = _dispositivoDe(norm);
    if (!disp) return null; // "retiro" sin qué → nota libre
    valores.dispositivo = disp;
    const motivo = _matchCuePolar(norm, _MOTIVO_CUES);
    if (motivo) valores.motivo = motivo;
    const se = _matchCuePolar(norm, _SITIO_ESTADO_CUES);
    if (se) valores.sitioEstado = se;
    return { accion, valores };
  }

  if (accion === 'traslado') {
    const destino = _matchCue(norm, _DESTINO_CUES);
    if (!destino) return null; // "traslado" sin destino → nota libre
    valores.destino = destino;
    const regreso = _matchCue(norm, _REGRESO_CUES);
    if (regreso) valores.regreso = regreso;
    return { accion, valores };
  }

  if (accion === 'intubacion') {
    // Verbo inequívoco: se abre aunque falte la técnica (la elige en un chip).
    const tec = _matchCue(norm, _TECNICA_INTUB_CUES);
    if (tec) valores.tecnica = tec;
    const motivos = _matchMulti(norm, _MOTIVO_INTUB_CUES);
    if (motivos.length) valores.motivos = motivos;
    const rescate = _matchMulti(norm, _RESCATE_INTUB_CUES);
    if (rescate.length) valores.rescate = rescate;
    const res = _matchCue(norm, _RESULTADO_INTUB_CUES);
    if (res) valores.resultado = res;
    Object.assign(valores, _numerosIntubacion(norm));
    return { accion, valores };
  }

  if (accion === 'extubacion') {
    // Verbo inequívoco: se abre aunque falte la tolerancia (la elige en 1 chip).
    const tol = _matchCue(norm, _TOLERANCIA_CUES);
    if (tol) valores.tolerancia = tol;
    const curso = _matchCue(norm, _CURSO_CUES);
    if (curso) valores.curso = curso;
    const ind = _matchCue(norm, _INDICACION_CUES);
    if (ind) valores.indicacion = ind;
    const sop = _matchCue(norm, _SOPORTE_CUES);
    if (sop) valores.soporte = sop;
    return { accion, valores };
  }

  if (accion === 'ronda') {
    const acciones = _matchMulti(norm, _RONDA_CUES);
    if (acciones.length) valores.acciones = acciones;
    return { accion, valores };
  }
  return null;
}

// ── Cierre del turno ──────────────────────────────────────────────────────────
// Resumen agregado de lo que el enfermero sostuvo en el turno, para el "cierre
// del día" (CierreTurno): le devuelve la magnitud de su jornada — sin esto, el
// turno simplemente se archiva sin cierre. Puro y des-identificado.
export function resumenTurno(registros = []) {
  const vivos = (registros || []).filter(Boolean);
  const porTipo = { valoracion: 0, farmaco: 0, procedimiento: 0, visita: 0, nota: 0 };
  let totalEntradas = 0;
  let farmacos10ok = 0;
  let dispositivos = 0;
  const tiposDisp = new Set();
  let aislamientos = 0;
  let gravedadMaxPeso = Infinity;
  let gravedadMax = null;

  // "Tu turno en números" (dictado del fundador 31-jul: objetivar la ganancia
  // con HECHOS registrados, jamás estimaciones — la cifra de minutos ahorrados
  // espera el dato real del piloto). Evolución COMPLETA = la misma definición
  // del check de la Mesa (completitudEvolucion, mínimo por sistema), no una
  // tercera vara. Examen: mediana de minutos entre recibir la cama y el sello
  // del sistema (mediana, no promedio: un atraso aislado no arruina el número).
  let evolucionesCompletas = 0;
  const minutosExamen = [];

  for (const r of vivos) {
    for (const e of (r.entradas || [])) {
      totalEntradas++;
      if (porTipo[e.tipo] != null) porTipo[e.tipo]++;
      if (e.tipo === 'farmaco') {
        const n = (e.datos?.correctos || []).filter((c) => IDS_10_CORRECTOS.has(c)).length;
        if (n >= LOS_10_CORRECTOS.length) farmacos10ok++;
      }
    }
    for (const d of (r.dispositivos || [])) {
      dispositivos++;
      if (d.tipo) tiposDisp.add(d.tipo);
    }
    if (aislamientosDe(r).length) aislamientos++;
    const peso = PESO_GRAVEDAD[r.gravedad];
    if (peso != null && peso < gravedadMaxPeso) { gravedadMaxPeso = peso; gravedadMax = r.gravedad; }

    if (r.evolucion) {
      const comp = completitudEvolucion(r.evolucion);
      if (comp.total > 0 && comp.conDatos === comp.total) evolucionesCompletas++;
    }
    const minEx = minutosHastaExamen(r.creado, r.examenFisico);
    if (Number.isFinite(minEx)) minutosExamen.push(minEx);
  }

  minutosExamen.sort((a, b) => a - b);
  const examenMinutos = minutosExamen.length
    ? minutosExamen[Math.floor((minutosExamen.length - 1) / 2)]
    : null;

  return {
    totalPacientes: vivos.length,
    totalEntradas,
    porTipo,
    farmacos10ok,
    dispositivos,
    tiposDispositivos: Array.from(tiposDisp),
    aislamientos,
    gravedadMax,
    evolucionesCompletas,
    examenMinutos,
  };
}

// Chips de una línea para el cierre ("5 pacientes", "8 registros", ...). Solo los
// hitos con valor; pluralización en español.
export function chipsResumenTurno(resumen) {
  if (!resumen || !resumen.totalPacientes) return [];
  const plural = (n, sing, plur) => `${n} ${n === 1 ? sing : plur}`;
  const chips = [plural(resumen.totalPacientes, 'paciente', 'pacientes')];
  if (resumen.totalEntradas) chips.push(plural(resumen.totalEntradas, 'registro', 'registros'));
  if (resumen.porTipo.farmaco) chips.push(plural(resumen.porTipo.farmaco, 'fármaco', 'fármacos'));
  if (resumen.porTipo.procedimiento) chips.push(plural(resumen.porTipo.procedimiento, 'procedimiento', 'procedimientos'));
  if (resumen.dispositivos) chips.push(plural(resumen.dispositivos, 'dispositivo', 'dispositivos'));
  return chips;
}

// ── "Mis turnos anteriores" (fix #3 auditoría MiPAE en turno) ───────────────────────
// El sello de cierre promete "guardado" pero hoy no hay dónde releerlo. Agrupa
// los registros guardados por SESIÓN de cierre: `cerrarTurno` (RegistroTurno)
// marca TODOS los registros del turno en curso con el MISMO `updated_at` (un
// solo `Date.now()` para todo el lote), así que ese timestamp es una clave de
// agrupación estable — dos cierres distintos nunca comparten milisegundo, y un
// registro cerrado+guardado ya no se vuelve a editar (queda fuera de
// `activos`), así que su `updated_at` no cambia después. Ordenado del más
// reciente al más antiguo.
export function turnosGuardados(registros = []) {
  const guardados = (registros || []).filter((r) => r?.cerrado && r?.guardado);
  const porTs = new Map();
  for (const r of guardados) {
    const ts = r.updated_at || r.creado || 0;
    const grupo = porTs.get(ts) || [];
    grupo.push(r);
    porTs.set(ts, grupo);
  }
  return Array.from(porTs.entries())
    .map(([ts, regs]) => ({
      ts,
      registros: ordenarPorPrioridad(regs),
      camas: regs.map((r) => sanitizarCama(r.cama)).filter(Boolean),
    }))
    .sort((a, b) => b.ts - a.ts);
}

// La "Valoración" del Registro del día usa el catálogo de entregaTurno.SISTEMAS
// (ids/vocabulario propio de la entrega SBAR); la evolución por sistema que
// arma compilarRegistro (buildEvolucion, de evolucionSistema.js) usa OTRO
// catálogo, con ids y opciones distintas. Antes se leía el mismo objeto
// `{ [sisId]: hallazgos }` con los dos catálogos sin traducir: como los ids Y
// las opciones no calzan, 3 de cada 5 hallazgos marcados desaparecían del
// bloque de evolución sin avisar (la cronología sí los mostraba — pérdida
// silenciosa). Este mapa traduce el id de sistema; ver turnoBridge.js para el
// patrón equivalente que traduce evolución → entrega.
const SISTEMA_VALORACION_A_EVOLUCION = {
  neuro: 'neuro',
  respiratorio: 'ventilacion',
  cardiovascular: 'hemodinamia',
  digestivo: 'gastro',
  tegumentario: 'tegumentario',
};

function estadoEvolucionDesdeEntradas(entradas) {
  const sistemas = {};
  const destino = (id) => sistemas[id] || (sistemas[id] = { hallazgos: [] });
  // Fragmentos que no calzan EXACTO con el vocabulario del sistema destino:
  // nada se pierde, van a la nota del sistema en vez de a los hallazgos.
  const notasPendientes = {};

  for (const e of entradas) {
    if (e.tipo !== 'valoracion') continue;
    for (const sis of SISTEMAS) { // catálogo de la Valoración (entregaTurno)
      const sel = (e.datos?.sistemas?.[sis.id] || []).filter((o) => sis.opciones.includes(o));
      if (!sel.length) continue;
      const destId = SISTEMA_VALORACION_A_EVOLUCION[sis.id] || sis.id;
      const opcionesDestino = SISTEMAS_EVOLUCION.find((d) => d.id === destId)?.opciones || [];
      for (const texto of sel) {
        if (opcionesDestino.includes(texto)) {
          const d = destino(destId);
          if (!d.hallazgos.includes(texto)) d.hallazgos.push(texto);
        } else {
          const pend = notasPendientes[destId] || (notasPendientes[destId] = []);
          if (!pend.includes(texto)) pend.push(texto);
        }
      }
    }
  }

  // Vuelca los fragmentos sin equivalencia en la nota del sistema destino:
  // primera letra en mayúscula, unidos por ', ', terminando en '.'.
  for (const [destId, frags] of Object.entries(notasPendientes)) {
    const frase = upperFirst(frags.join(', '));
    const d = destino(destId);
    d.nota = frase.endsWith('.') ? frase : `${frase}.`;
  }

  return sistemas;
}

function upperFirst(t) {
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}

// Compila el registro completo en texto: cabecera + timeline cronológica +
// evolución por sistema acumulada. Apto para copiar/exportar.
export function compilarRegistro(registro, { fecha } = {}) {
  const r = registro || nuevoRegistro();
  const cama = sanitizarCama(r.cama);
  const fechaTxt = fecha || new Date().toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });

  const cab = [
    'REGISTRO DEL DÍA',
    cama ? `Cama ${cama}` : null,
    r.turno ? `Turno ${r.turno.toLowerCase()}` : null,
    fechaTxt,
  ].filter(Boolean).join(' · ');

  const ordenadas = ordenarPorHecho(r.entradas, r);
  const timeline = ordenadas.length
    ? ordenadas.map((e) => `${e.hora}  ${resumenEntrada(e)}`).join('\n')
    : 'Sin registros aún.';

  // La evolución que escribió la persona en la Mesa (registro.evolucion) es la
  // del paciente; el reparto por entradas queda solo como respaldo para un
  // registro que nunca abrió la Mesa. Auditoría del preview 2026-09-28 (A09):
  // este resumen decía "Sin hallazgos registrados" con una traqueostomía en
  // VMI escrita en Evolucionar, porque leía únicamente las entradas.
  const evolucionPropia = tieneContenido(r.evolucion) ? r.evolucion : null;
  const evol = buildEvolucion(
    evolucionPropia || { turno: r.turno, sistemas: estadoEvolucionDesdeEntradas(r.entradas || []) },
    { fecha: fechaTxt, turno: r.turno, incluirEncabezado: false }, // la cabecera ya trae turno + fecha
  );
  const evolucionLectura = formatearEvolucionParaLectura(evol.texto);

  // Bloque de dispositivos declarados AL RECIBIR (F1), con lúmenes, día-catéter
  // y cuidados marcados. Es esa sección y no un inventario clínico: si en F1
  // no hay ninguno pero la evolución sí los tiene, se remite a Invasivos en vez
  // de afirmar una ausencia (A09: "no decir ausencia de dispositivos cuando
  // existe información en otra etapa").
  const dispLineas = (r.dispositivos || []).map((d) => {
    const cat = CATALOGO_DISPOSITIVOS.find((c) => c.id === d.tipo);
    if (!cat) return null;
    const lumenes = cat.multiLumen && d.lumenes ? ` (${d.lumenes} lúmenes)` : '';
    const dia = diaDispositivo(d, new Date(), { desdeUno: cuentaDesdeUno(r) });
    const diaTxt = dia != null ? `, día ${dia}` : '';
    const cuidados = (d.cuidados || []).filter((c) => cuidadosDe(d.tipo).includes(c));
    return `• ${cat.label}${lumenes}${diaTxt}${cuidados.length ? `: ${cuidados.join(', ')}.` : '.'}`;
  }).filter(Boolean);
  const invasivosEnEvolucion = (evolucionPropia?.sistemas?.invasivos?.dispositivos || []).length > 0;
  const dispBloque = dispLineas.length
    ? dispLineas.join('\n')
    : (invasivosEnEvolucion ? 'Ninguno declarado al recibir; los del turno van en Invasivos, arriba.' : 'Sin dispositivos declarados al recibir.');

  // Examen físico de ingreso: la foto al recibir, con su hora sellada. Va
  // antes de la evolución (cronológicamente es lo primero del turno); si no
  // se registró, se omite — el silencio dice "no registrado", nunca se finge.
  const examen = buildExamenFisico(r.examenFisico);

  // Orden pedido: cabecera → examen de ingreso → evolución por sistema →
  // dispositivos → cronología.
  const texto = [
    cab, '',
    ...(examen.texto ? [examen.texto, ''] : []),
    evolucionLectura, '',
    'DISPOSITIVOS AL RECIBIR', dispBloque, '',
    'CRONOLOGÍA', timeline,
  ].join('\n');
  return { texto, timeline, evolucion: evolucionLectura, dispositivos: dispBloque, examen: examen.texto };
}
